// Joana chat shell — view builders. PURE functions: state → catalog VNode tree.
// No DOM, no side effects, no network — just the declarative description the
// ViewHost diffs into patch frames. Interactions are catalog actions with the
// `shell` intent; the surface decodes them and calls the store/network. Author
// ids are stable so the diff keeps node identity across renders (message bubbles
// update in place; the list reorders by id, never re-creates).

import type { VAction, VNode } from "../render/catalog.ts";
import type { ActivityEvent, Channel, ChannelListing, ChannelPost, Chat, CirclePost, Message, MiniAppListItem, OrgApiKey, Organisation, OrganisationMember, Publisher, StatusTrayEntry, WireAvatar, WireContactCard } from "./types.ts";
import { chatById, type OrgEditor, type ShellState } from "./store.ts";
import { LANGUAGES, SHIPPING_LANGUAGES, browserTags, resolveLanguage, translator } from "../i18n/index.ts";
import { areaOf, frame, layoutFor } from "./frame.ts";
import { memberName } from "./author-names.ts";
import { WALLPAPERS } from "./wallpaper.ts";
import { parseNoticeMeta, systemNoticeText, systemRowText } from "./system-notice.ts";
// `color()` resolves a role to its `var(…)`. Needed here for exactly one
// value: the conversation canvas nests the role INSIDE a `var()` fallback, and
// the renderer only substitutes a role that is the whole value or a
// space-separated word of it — a `chat.canvas` sitting inside `var(a, b)` is
// neither, so it would have reached CSS as the literal text `chat.canvas` and
// painted nothing.
import { color } from "../render/tokens.ts";

/** A small labelled button — the profile screen's row affordance. */
function button(id: string, label: string, action: VAction): VNode {
  return {
    type: "button", id, props: { label }, action,
    style: { background: "surface.variant", color: C.textPrimary, border: "none", font_size: 13, font_weight: 600, padding: "6 10", radius: 8 },
  };
}

/** A shell interaction. The surface routes these by `cmd`. */
export function act(cmd: string, extra: Record<string, unknown> = {}): VAction {
  return { intent: "shell", payload: { cmd, ...extra } };
}

/**
 * The shell's colour vocabulary — ROLE NAMES, never hexes.
 *
 * Every entry resolves through `tokens.ts`, which is generated from
 * `branding/wavetalk-colors.json`, so a role carries both appearances and the
 * renderer hands the DOM a `var(--wt-…)` rather than a colour. That is what
 * makes dark mode free here: the tree does not change when the lights do.
 *
 * `brandEnd` is the ACCENT and no longer the gradient's end stop. It was the
 * latter, drawn on some thirty-five active states, ticks and dots — and
 * `#FF3D77` is chosen to read on white and is a muddy shape on near-black,
 * which is precisely the conversion BRAND.md says a hand-made dark palette
 * skips. The gradient keeps its own two roles, used only where a gradient is
 * actually drawn.
 */
const C = {
  gradStart: "brand.gradient.start",
  gradEnd: "brand.gradient.end",
  brandEnd: "accent",
  onAccent: "on.accent",
  onTint: "on.tint",
  onMedia: "on.media",
  onBar: "brand.on.bar",
  barControl: "brand.bar.control",
  barSubtitle: "brand.bar.subtitle",
  accentWash: "accent.wash",
  textPrimary: "text.primary",
  textSecondary: "text.secondary",
  surface: "surface",
  bgApp: "surface.variant",
  outline: "outline.variant",
  danger: "danger",
  dangerBg: "danger.container",
  info: "info",
  infoBg: "info.container",
  success: "success",
  warning: "warning",
  bubbleThem: "chat.bubble.theirs",
  bubbleMine: "chat.bubble.mine",
  bubbleMineText: "chat.on.bubble.mine",
  bubbleThemText: "chat.on.bubble.theirs",
  chatCanvas: "chat.canvas",
  composerSurface: "composer.surface",
  composerField: "composer.field",
  tickRead: "chat.tick.read",
  backdrop: "media.backdrop",
};

/** Top-level: pick the screen. One root node so the diff has a stable root. */
/**
 * The translator every view below draws through, set once per render pass.
 *
 * Module-level and mutable, which needs saying out loud in a file whose first
 * line promises pure functions. It is DERIVED FROM THE STATE and set at the
 * single entry point — `buildTree` is the only way into this file's tree — so
 * the tree is still a function of the state alone. The alternative was
 * threading a `t` through some ninety builder signatures, which is a lot of
 * noise for a value that never differs within one pass.
 *
 * It defaults to English so a helper called outside a render (`chatPreview`
 * from the surface, say) draws words rather than throwing.
 */
let t = translator("en");
/** The language being DRAWN, for the platform's own date formatting — a day
 *  line's date follows the app's language (s174), not the browser's locale,
 *  the way the phones format "24 Septemba 2026" in the app's locale. */
let uiLang = "en";

/**
 * The server will not serve this bundle — ADR-0033 D9.
 *
 * ## Why the web's version of this is a RELOAD
 *
 * On a phone a forced update is an install. Here it is not: the browser
 * fetches the newest bundle on the next load, so the failure this closes is a
 * tab left open across a deploy, or a cache still serving yesterday's files.
 * One button, and it does the only thing that can help.
 *
 * ## Why it is a wall and not a bar
 *
 * At this point every request is answered `426` and the socket will not open.
 * A page that let somebody keep typing would be collecting messages that can
 * never be sent — the state WhatsApp calls "this version has expired", and it
 * is blunt about it for the same reason.
 */
function updateRequiredView(): VNode {
  return {
    type: "stack",
    id: "update-required",
    style: {
      direction: "column", align: "center", justify: "center", gap: 14,
      padding: 32, height: "100%", background: C.surface, text_align: "center",
    },
    children: [
      { type: "text", id: "update-required-icon", props: { value: "⬇️" }, style: { font_size: 44 } },
      {
        type: "text",
        id: "update-required-title",
        props: { value: t("Update WaveTalk to keep using it") },
        style: { font_size: 20, font_weight: 700, color: C.textPrimary },
      },
      {
        type: "text",
        id: "update-required-body",
        props: {
          value: t(
            "This version is no longer supported. Install the latest one to send and receive messages again.",
          ),
        },
        style: { font_size: 14, color: C.textSecondary, max_width: 380 },
      },
      {
        type: "button",
        id: "update-required-reload",
        props: { label: t("Reload WaveTalk") },
        style: {
          background: C.brandEnd, color: C.onAccent, border: "none",
          font_size: 15, font_weight: 600, padding: "11 22", radius: 999,
        },
        action: act("reload"),
      },
    ],
  };
}

/**
 * ADR-0037 B — ONE Office zone refuses this bundle. The same remedy as the
 * whole-app wall above (a reload fetches the newest bundle), drawn inside the
 * conversation's pane because the rest of WaveTalk still works.
 */
function zoneUpdateRequiredView(): VNode {
  return {
    type: "stack",
    id: "zone-update-required",
    style: {
      direction: "column", align: "center", justify: "center", gap: 14,
      padding: 32, flex: 1, text_align: "center",
    },
    children: [
      { type: "text", id: "zone-update-required-icon", props: { value: "⬇️" }, style: { font_size: 44 } },
      {
        type: "text",
        id: "zone-update-required-title",
        props: { value: t("Update WaveTalk to open this zone") },
        style: { font_size: 20, font_weight: 700, color: C.textPrimary },
      },
      {
        type: "text",
        id: "zone-update-required-body",
        props: { value: t("This zone needs a newer version of WaveTalk. Your other chats work as usual.") },
        style: { font_size: 14, color: C.textSecondary, max_width: 380 },
      },
      {
        type: "button",
        id: "zone-update-required-reload",
        props: { label: t("Reload WaveTalk") },
        style: {
          background: C.brandEnd, color: C.onAccent, border: "none",
          font_size: 15, font_weight: 600, padding: "11 22", radius: 999,
        },
        action: act("reload"),
      },
    ],
  };
}

export function buildTree(state: ShellState): VNode {
  // ADR-0028: the whole shell in the account's language, not just the picker.
  uiLang = resolveLanguage(state.uiLanguage, browserTags());
  t = translator(uiLang);

  // ADR-0033 D9 — the server refuses this BUNDLE, and it outranks every other
  // screen including login. Signed in or not, every request the page can make
  // is answered 426 and the socket will not open; a login form behind this
  // would refuse the person for the same reason one screen further from the
  // answer. There is nothing under it to reach.
  if (state.updateRequired) return updateRequiredView();

  // Login is the one screen outside the frame: there is no rail to show
  // somebody who is not signed in, and no list to put beside it.
  if (state.screen === "login") return loginView(state);

  // THE RULE for the detail pane, and it is the one thing s159 left open.
  //
  // The AREA you are looking at owns the pane. A channel opened from Discover,
  // a publisher opened from Office, a Mazungumzo thread — each takes the pane
  // while you are in that area, and the conversation comes back the moment you
  // return to Chats. Switching areas on a desktop still does not CLOSE the
  // thread you were reading (`activeChatId` is untouched); it just stops being
  // what the right-hand pane is for.
  //
  // The alternative — the conversation always winning — is what s159 shipped,
  // and it is why every area's drill-down stayed inside a 400px column.
  const areaPane = areaDetail(state);
  return frame({
    state,
    t,
    layout: layoutFor(state.viewportWidth),
    list: listColumn(state),
    detail: areaPane ?? (state.activeChatId !== null ? conversationView(state) : null),
    // Narrow has ONE pane. It may only show the detail when the detail belongs
    // to the area the rail is highlighting — otherwise a conversation open in
    // the background would fill the screen while the rail said Updates.
    detailIsCurrentArea: areaPane !== null,
    avatar: railAvatar(state),
  });
}

/**
 * The current AREA's detail, or null when it has none open.
 *
 * Scoped to the area on purpose. `channelViewing` survives a move to Office,
 * and a channel drawn beside the publisher list would be the pane claiming to
 * belong to a screen it does not — so each case asks `areaOf(state)`
 * first and answers only for its own.
 *
 * Nothing here CLOSES anything. Leaving Discover with a channel open and
 * coming back to it should find the channel where you left it, which is the
 * same promise the conversation already makes.
 */
function areaDetail(state: ShellState): VNode | null {
  const area = areaOf(state);

  // A channel is reachable from BOTH Updates (a followed channel's row) and
  // Discover (the directory), and it is the same screen either way.
  if ((area === "updates" || area === "discover") && state.channelViewing) {
    return channelDetail(state);
  }
  // ADR-0029 D17 — a numbered series, read beside the feed it is numbered
  // against rather than instead of it.
  if (area === "updates" && state.mazungumzoThread.length > 0) {
    return mazungumzoThreadView(state);
  }
  if (area === "office") {
    if (state.screen === "office_publisher") return publisherView(state);
    if (state.screen === "office_new") return createPublisherView(state);
    // ADR-0018. The zone itself is deliberately absent: a zone IS a chat, so
    // it opens the CONVERSATION screen and the pane falls through to the
    // conversation below. That is the whole point of reusing the type — the
    // area is Office (`areaOf`), the pane is the one every chat uses.
    if (state.screen === "office_org") return organisationView(state);
    if (state.screen === "office_org_new") return createOrganisationView(state);
    if (state.screen === "office_zone_new") return createZoneView(state);
  }
  return null;
}

/**
 * What the list column shows — the AREA's screen, at its full existing shape.
 *
 * A conversation maps to Chats rather than to itself: the thread moved to the
 * detail pane, and the list must keep showing the list beside it. That is the
 * single biggest thing the one-column web client got wrong on a desktop.
 */
function listColumn(state: ShellState): VNode {
  switch (state.screen) {
    case "login":
    case "chats":
      return chatsView(state);
    // A zone is never in Chats, so beside an open zone the list is the one it
    // was opened from — the organisations — and the rail says Office.
    case "conversation":
      return areaOf(state) === "office" ? organisationsView(state) : chatsView(state);
    case "apps":
      return appsView(state);
    case "profile":
      return profileView(state);
    case "updates":
      return updatesView(state);
    case "explore":
      return exploreView(state);
    // All three Office screens draw the same list column. The publisher and
    // the composer moved to the detail pane (`areaDetail`), for the reason the
    // conversation did: looking at one publisher should not cost you the list
    // of them.
    case "office":
    case "office_new":
    case "office_publisher":
      return officeView(state);
    // ADR-0018 — organisations keep the same list column and put the detail in
    // the detail pane, exactly as publishers do: opening one organisation
    // should not cost you the list of them.
    case "office_orgs":
    case "office_org":
    case "office_org_new":
    case "office_zone_new":
      return organisationsView(state);
  }
}

/** The rail's own face. Built here because `views.ts` owns the avatar loader. */
function railAvatar(state: ShellState): VNode {
  const me = state.me;
  return avatar("rail-me-face", me?.display_name ?? me?.username ?? "?", {
    ownerId: me?.id, av: me?.avatar ?? null, frame: me?.frame ?? null, size: 30,
    ring: areaOf(state) === "me",
  });
}

// --- login -----------------------------------------------------------------

/** Shown while a stored refresh token is being exchanged for a live session.
 *  Deliberately has no controls: there is nothing useful to do but wait, and
 *  offering a sign-in here would say the session is gone when it is not. */
function restoringView(): VNode {
  return {
    type: "stack",
    id: "login-root",
    style: {
      direction: "column",
      justify: "center",
      align: "stretch",
      gap: 10,
      padding: 24,
      width: "100%",
      max_width: 380,
      margin: "0 auto",
      min_height: "100vh",
    },
    children: [
      {
        type: "text",
        id: "brand",
        props: { value: t("WaveTalk") },
        style: { font_size: 34, font_weight: 700, color: C.brandEnd, text_align: "center" },
      },
      {
        type: "text",
        id: "restoring",
        props: { value: t("Signing you in…") },
        style: { font_size: 15, color: C.textSecondary, text_align: "center", margin: "4 0 16 0" },
      },
    ],
  };
}

function loginView(state: ShellState): VNode {
  // A stored session is being exchanged right now. Draw the wordmark and a line
  // saying so — NOT the sign-in form, and not a QR code. Somebody who is
  // already signed in must never be shown a sign-in screen on the way back into
  // their own account; that flash is indistinguishable from having been logged
  // out, which is exactly the feeling this work exists to remove.
  if (state.restoringSession) return restoringView();

  const twoFactor = state.twoFactorToken !== null;
  const qrMode = state.loginMode === "qr" && !twoFactor;
  const children: VNode[] = [
    {
      type: "text",
      id: "brand",
      props: { value: t("WaveTalk") },
      style: { font_size: 34, font_weight: 700, color: C.brandEnd, text_align: "center" },
    },
    {
      type: "text",
      id: "tagline",
      props: {
        value: twoFactor
          ? t("Enter your verification code")
          : qrMode
            ? t("Scan with WaveTalk on your phone")
            // Was a bare literal, so it drew English on a Kiswahili screen while
            // both i18n gates read zero — the ratchet cannot see a string that
            // never reaches `t()`.
            : t("Sign in to keep talking"),
      },
      style: { font_size: 15, color: C.textSecondary, text_align: "center", margin: "4 0 16 0" },
    },
  ];

  if (state.error) {
    children.push({
      type: "text",
      id: "error",
      props: { value: state.error },
      style: { font_size: 14, color: C.danger, text_align: "center", margin: "0 0 8 0" },
    });
  }

  if (qrMode) {
    children.push(...qrPanel(state));
    children.push(
      linkButton("login-mode-password", t("Sign in with password instead"), act("login_mode", { mode: "password" })),
    );
  } else if (!twoFactor) {
    children.push(
      field("username", t("Username or phone"), state.loginUsername, "loginUsername"),
      field("password", t("Password"), state.loginPassword, "loginPassword", { secure: true }),
      primaryButton("signin", state.loading ? t("Signing in…") : t("Sign in"), act("login"), state.loading),
      linkButton("login-mode-qr", t("Scan with phone instead"), act("login_mode", { mode: "qr" })),
    );
  } else {
    children.push(
      field("code", t("6-digit code"), state.twoFactorCode, "twoFactorCode", { input_type: "number" }),
      primaryButton("verify", state.loading ? t("Verifying…") : t("Verify"), act("verify_2fa"), state.loading),
    );
  }

  return {
    type: "stack",
    id: "login-root",
    style: {
      direction: "column",
      justify: "center",
      align: "stretch",
      gap: 10,
      padding: 24,
      width: "100%",
      max_width: 380,
      margin: "0 auto",
      min_height: "100vh",
    },
    children,
  };
}

/** The scan-to-login panel: the QR card plus its status line. Node ids are
 *  stable per state so the diff swaps content cleanly. */
function qrPanel(state: ShellState): VNode[] {
  const { status, svg, scannedBy } = state.qr;
  const card: VNode[] = [];

  if (status === "waiting" && svg) {
    card.push({
      type: "image",
      id: "qr-code",
      props: {
        url: `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
        alt: "Scan this code with WaveTalk on your phone",
      },
      style: { width: 216, height: 216, margin: "0 auto" },
    });
  } else if (status === "scanned") {
    card.push(
      qrStatusText("qr-scanned", t("✓ Scanned"), { font_size: 28, color: C.success, font_weight: 700 }),
      qrStatusText(
        "qr-confirm-hint",
        scannedBy ? `Confirm on ${scannedBy}’s phone` : t("Confirm on your phone"),
        { font_size: 15, color: C.textPrimary, margin: "8 0 0 0" },
      ),
    );
  } else if (status === "loading") {
    card.push(qrStatusText("qr-loading", t("Generating code…"), { font_size: 15, color: C.textSecondary }));
  } else {
    const message =
      status === "rejected"
        ? t("Sign-in was declined on your phone")
        : status === "expired"
          ? t("This code expired")
          : "Couldn’t reach the server";
    card.push(
      qrStatusText("qr-problem", message, { font_size: 15, color: C.textPrimary }),
      primaryButton("qr-refresh", t("Get a new code"), act("qr_refresh")),
    );
  }

  return [
    {
      type: "stack",
      id: "qr-card",
      style: {
        direction: "column",
        justify: "center",
        align: "center",
        gap: 8,
        padding: 20,
        radius: 14,
        background: C.surface,
        border: "1px solid outline.variant",
        min_height: 260,
      },
      children: card,
    },
    {
      type: "text",
      id: "qr-help",
      props: { value: t("Open WaveTalk on your phone → Settings → Scan to log in") },
      style: { font_size: 13, color: C.textSecondary, text_align: "center", margin: "8 0 0 0" },
    },
  ];
}

function qrStatusText(id: string, value: string, style: Record<string, unknown>): VNode {
  return { type: "text", id, props: { value }, style: { text_align: "center", ...style } };
}

function linkButton(id: string, label: string, action: VAction): VNode {
  return {
    type: "button",
    id: `b-${id}`,
    props: { label },
    style: {
      padding: 8,
      background: "transparent",
      color: C.brandEnd,
      font_size: 14,
      border: "none",
      margin: "6 0 0 0",
    },
    action,
  };
}

function field(
  id: string,
  placeholder: string,
  value: string,
  storeField: string,
  extraProps: Record<string, unknown> = {},
): VNode {
  return {
    type: "input",
    id: `f-${id}`,
    props: { placeholder, value, ...extraProps },
    style: {
      padding: 12,
      radius: 10,
      border: "1px solid outline.variant",
      font_size: 16,
      background: C.surface,
    },
    events: { input: act("set_field", { field: storeField }) },
  };
}

function primaryButton(id: string, label: string, action: VAction, disabled = false): VNode {
  return {
    type: "button",
    id: `b-${id}`,
    props: { label, disabled },
    style: {
      padding: 13,
      radius: 10,
      background: C.brandEnd,
      color: C.onAccent,
      font_size: 16,
      font_weight: 600,
      border: "none",
      opacity: disabled ? 0.6 : 1,
    },
    action,
  };
}

// --- chat list -------------------------------------------------------------

function chatsView(state: ShellState): VNode {
  return {
    type: "stack",
    id: "chats-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("chats-header", t("Chats"), state, { pill: true }),
      ...historySyncBar(state),
      {
        type: "scroll-view",
        id: "chat-list",
        style: { direction: "column", flex: 1, padding: "8 0", overflow: "auto" },
        children:
          state.chats.length === 0
            ? [emptyState("no-chats", t("No conversations yet"))]
            : state.chats.map((c) => chatRow(c, state.me?.id ?? null, moodBadgeFor(state, c))),
      },
      // The viewer is an overlay on the chat list rather than a screen of its
      // own: closing it must land you exactly where you were.
      ...(state.statusViewing ? [statusViewerOverlay(state)] : []),
    ],
  };
}

// --- Status (D8, ADR-0023) --------------------------------------------------

const CARD_W = 96;
const CARD_H = 140;
const FACE = 44;

/** The strip's height, so a caller can reserve the space before it loads. */
const STATUS_TRAY_H = CARD_H + 16;

/**
 * The status strip, pinned at the top of Updates (ADR-0024 A9).
 *
 * ## Cards, not rings
 *
 * The first build drew a row of 60px circles, which is WhatsApp's shape and
 * the wrong one here: a circle can show a face and nothing else, so a picture
 * somebody posted arrives too small to read and the strip is a list of avatars
 * rather than a list of what was posted. The card is 96x140 with the picture AS
 * the background — the same card the phones draw, at the same size, because a
 * surface that redraws a shared component at its own dimensions is one that
 * will drift.
 *
 * The ring stays, on the card: it is the unseen affordance, the brand gradient
 * while anything is unseen and a flat outline once everything is — and no
 * number anywhere, because a tally is exactly what this design refuses.
 */
function statusTray(state: ShellState): VNode {
  const meId = state.me?.id ?? null;
  const mine = state.statusTray.find((e) => e.author_id === meId) ?? null;
  const others = state.statusTray.filter((e) => e.author_id !== meId);

  const card = (id: string, label: string, opts: {
    entry: typeof mine;
    fallbackId: string;
    action: VNode["action"];
    plus?: boolean;
  }): VNode => {
    const entry = opts.entry;
    const newest = entry?.items[entry.items.length - 1] ?? null;
    const unseen = entry ? !entry.all_viewed : false;
    const hasMedia = !!newest && newest.kind !== "text" && newest.kind !== "mood";
    const blob = hasMedia ? statusBlob(newest!.id, "thumb") : null;
    const tint = !hasMedia && newest?.text_bg ? newest.text_bg : null;
    // True when the backdrop painted something to read over. A mood has
    // neither a picture nor a colour by design, and lands on the surface's own
    // colour — which is why the label asks rather than assuming white.
    const onMedia = !!blob || !!tint;

    const layers: VNode[] = [];
    if (blob) {
      layers.push({
        type: "image", id: `${id}-backdrop`, props: { url: blob, alt: "" },
        style: { position: "absolute", inset: 0, width: CARD_W, height: CARD_H, object_fit: "cover" },
      });
    } else if (tint) {
      layers.push({
        type: "stack", id: `${id}-tint`,
        style: { position: "absolute", inset: 0, background: tint },
        children: [],
      });
    }
    if (onMedia) {
      layers.push({
        type: "stack", id: `${id}-scrim`,
        style: {
          position: "absolute", inset: 0,
          background: "linear-gradient(to bottom, rgba(0,0,0,0) 60%, rgba(0,0,0,0.6) 100%)",
        },
        children: [],
      });
    }

    const ring: VNode = {
      type: "stack", id: `${id}-ring`,
      style: {
        direction: "column", align: "center", justify: "center",
        width: FACE, height: FACE, radius: 999, padding: 2,
        background: entry
          ? (unseen ? `linear-gradient(135deg, ${C.gradStart}, ${C.gradEnd})` : C.textSecondary)
          : "transparent",
      },
      children: [
        // The newest item's picture is the CARD now, so drawing it again
        // inside the ring would be the same image twice on one card at two
        // sizes. The ring's job on a card is to say WHO.
        avatar(`${id}-face`, label, {
          ownerId: entry?.author?.user_id ?? opts.fallbackId,
          av: entry?.author?.avatar ?? null,
          frame: entry?.author?.frame ?? null,
          size: FACE - 5,
        }),
      ],
    };

    return {
      type: "stack", id, action: opts.action,
      style: {
        position: "relative", direction: "column",
        width: CARD_W, height: CARD_H, radius: 15, overflow: "hidden",
        background: "surface.variant", border: "1px solid outline.variant",
      },
      children: [
        ...layers,
        {
          type: "stack", id: `${id}-body`,
          style: {
            position: "absolute", inset: 0,
            // `stretch`, not `center`, and the difference is a real defect:
            // a child sized `width: 100%` under `align: center` resolves that
            // 100% against the card's OUTER width and is then centred inside
            // the padded box, so it hangs 9px off each edge and the card's
            // `overflow: hidden` eats the first letter of every name. The ring
            // is centred by its own row instead.
            direction: "column", align: "stretch", justify: "space-between",
            padding: "12 9 10 9",
          },
          children: [
            {
              type: "stack", id: `${id}-ring-row`,
              style: { direction: "row", justify: "center", align: "center", gap: 6 },
              children: [
                ring,
                ...(newest && newest.kind === "mood" && newest.mood_glyph
                  ? [{ type: "text" as const, id: `${id}-mood`, props: { value: newest.mood_glyph }, style: { font_size: 15 } }]
                  : []),
              ],
            },
            {
              type: "text", id: `${id}-label`, props: { value: label },
              style: {
                font_size: 11.5, font_weight: 600,
                color: onMedia ? C.onMedia : C.textPrimary,
                opacity: entry && entry.all_viewed ? 0.6 : 1,
              },
            },
          ],
        },
        // "My status" carries the compose badge whether or not anything is up,
        // so "add another" and "add your first" are the same gesture in the
        // same place.
        ...(opts.plus
          ? [{
              type: "stack" as const, id: `${id}-plus`,
              style: {
                position: "absolute", left: 60, top: 40,
                width: 21, height: 21, radius: 999,
                direction: "row", align: "center", justify: "center",
                background: `linear-gradient(135deg, ${C.gradStart}, ${C.gradEnd})`,
              },
              action: act("status_open"),
              children: [{ type: "text" as const, id: `${id}-plus-t`, props: { value: "+" }, style: { font_size: 13, font_weight: 700, color: C.onMedia } }],
            }]
          : []),
      ],
    };
  };

  return {
    type: "scroll-view", id: "status-tray",
    style: {
      direction: "row", gap: 9, padding: "2 16 14 16",
      background: C.surface, overflow: "auto", height: STATUS_TRAY_H,
    },
    children: [
      card("status-mine", mine ? t("My status") : t("Add status"), {
        entry: mine,
        fallbackId: state.me?.id ?? "",
        // Tapping opens what you have; with nothing up it opens the composer,
        // which is the only route to it from this screen.
        action: mine ? act("status_view", { author_id: state.me?.id ?? "" }) : act("status_open"),
        plus: true,
      }),
      ...others.map((entry) =>
        card(`status-${entry.author_id}`, entry.author?.display_name ?? entry.author?.username ?? t("Someone"), {
          entry,
          fallbackId: entry.author_id,
          action: act("status_view", { author_id: entry.author_id }),
        })
      ),
    ],
  };
}

/** The full-screen viewer. No forward, no share, no reshare, no counter. */
function statusViewerOverlay(state: ShellState): VNode {
  const entry = state.statusTray.find((e) => e.author_id === state.statusViewing);
  if (!entry) return { type: "stack", id: "status-viewer-empty", style: {}, children: [] };

  const item = entry.items[Math.min(state.statusIndex, entry.items.length - 1)];
  if (!item) return { type: "stack", id: "status-viewer-empty", style: {}, children: [] };

  const mine = entry.author_id === state.me?.id;
  const blob = item.kind === "photo"
    ? statusBlob(item.id, "full")
    : item.kind === "video"
      ? statusBlob(item.id, "poster")
      : null;

  const body: VNode = blob
    ? {
        type: "image", id: "status-body", props: { url: blob, alt: item.caption ?? "" },
        style: { flex: 1, width: "100%", object_fit: "contain" },
      }
    : {
        type: "stack", id: "status-body",
        style: {
          direction: "column", flex: 1, align: "center", justify: "center", gap: 14,
          padding: 32, background: item.text_bg ?? C.gradStart,
        },
        children: [
          ...(item.kind === "mood" && item.mood_glyph
            ? [{ type: "text" as const, id: "status-mood", props: { value: item.mood_glyph }, style: { font_size: 64 } }]
            : []),
          ...(item.caption
            ? [{ type: "text" as const, id: "status-caption", props: { value: item.caption }, style: { font_size: 24, color: C.onMedia, font_weight: 600 } }]
            : []),
        ],
      };

  return {
    type: "stack", id: "status-viewer",
    style: {
      direction: "column", position: "fixed", inset: 0, background: C.backdrop, z_index: 40,
    },
    children: [
      // One segment per item, filled up to where you are. No numbers.
      {
        type: "stack", id: "status-segments",
        style: { direction: "row", gap: 3, padding: "10 12 6 12" },
        children: entry.items.map((it, i) => ({
          type: "stack" as const, id: `seg-${it.id}`,
          style: {
            flex: 1, height: 3, radius: 2,
            background: i <= state.statusIndex ? C.onMedia : withAlpha("#FFFFFF", 0.3),
          },
          children: [],
        })),
      },
      {
        type: "stack", id: "status-head",
        style: { direction: "row", align: "center", gap: 10, padding: "0 12 8 12" },
        children: [
          avatar("status-author-face", entry.author?.display_name ?? "?", {
            ownerId: entry.author_id,
            av: entry.author?.avatar ?? null,
            frame: entry.author?.frame ?? null,
            size: 34,
          }),
          {
            type: "text", id: "status-author",
            props: { value: entry.author?.display_name ?? entry.author?.username ?? t("Someone") },
            style: { flex: 1, font_size: 14, font_weight: 600, color: C.onTint },
          },
          button("status-close", t("Close"), act("status_close")),
        ],
      },
      body,
      {
        type: "stack", id: "status-foot",
        style: { direction: "row", align: "center", gap: 10, padding: 12 },
        children: mine
          ? [
              {
                type: "text", id: "status-seen",
                props: { value: `Seen by ${item.views ?? 0}` },
                style: { flex: 1, font_size: 13, color: C.onMedia },
              },
              button("status-delete", t("Delete"), act("status_delete", { status_id: item.id })),
            ]
          : [
              { type: "stack", id: "status-prev", style: { padding: "8 12" }, action: act("status_step", { delta: -1 }),
                children: [{ type: "text", id: "status-prev-t", props: { value: "‹" }, style: { color: C.onMedia, font_size: 20 } }] },
              // A like nobody counts: only the author ever sees it.
              { type: "stack", id: "status-like", style: { flex: 1, padding: "8 12" }, action: act("status_like", { status_id: item.id, liked: !item.liked }),
                children: [{ type: "text", id: "status-like-t", props: { value: item.liked ? t("♥ Liked") : t("♡ Like") }, style: { color: item.liked ? C.gradEnd : C.onMedia, font_size: 15 } }] },
              { type: "stack", id: "status-next", style: { padding: "8 12" }, action: act("status_step", { delta: 1 }),
                children: [{ type: "text", id: "status-next-t", props: { value: "›" }, style: { color: C.onMedia, font_size: 20 } }] },
              // Mute and Report, which Android has had since s130 and the web
              // did not. Reporting is the obligation half: EPOCA gives a
              // moderator two hours, and the status is gone in twenty-four.
              { type: "stack", id: "status-mute", style: { padding: "8 10" }, action: act("status_mute", { author_id: entry.author_id }),
                children: [{ type: "text", id: "status-mute-t", props: { value: t("Mute") }, style: { color: withAlpha("#FFFFFF", 0.8), font_size: 13 } }] },
              { type: "stack", id: "status-report", style: { padding: "8 10" }, action: act("status_report_open", { status_id: item.id }),
                children: [{ type: "text", id: "status-report-t", props: { value: t("Report") }, style: { color: withAlpha("#FFFFFF", 0.8), font_size: 13 } }] },
            ],
      },
      ...(state.statusReporting ? [statusReportSheet(state.statusReporting)] : []),
      ...(state.statusNotice ? [statusNoticeBar(state.statusNotice)] : []),
    ],
  };
}

/** The confirmation, over the viewer where the reader actually is. */
function statusNoticeBar(text: string): VNode {
  return {
    type: "stack", id: "status-notice",
    style: {
      position: "fixed", inset: 0, z_index: 60,
      direction: "column", justify: "center", align: "center",
      background: withAlpha("#000000", 0.55),
    },
    action: act("status_notice_clear"),
    children: [
      {
        type: "stack", id: "status-notice-card",
        style: { direction: "column", background: C.surface, radius: 12, padding: 20, gap: 12 },
        children: [
          {
            type: "text", id: "status-notice-t",
            props: { value: text },
            style: { font_size: 15, color: C.textPrimary },
          },
          {
            type: "text", id: "status-notice-ok",
            props: { value: t("OK") },
            style: { font_size: 15, font_weight: 600, color: C.brandEnd, text_align: "right" },
          },
        ],
      },
    ],
  };
}

/** The six reasons, matching `ReportReason`'s wire values in the shared module
 *  — the server rejects anything else rather than inventing a default. One
 *  taxonomy across all three clients, or the Ops queue reads three. */
const REPORT_REASONS: ReadonlyArray<readonly [string, string]> = [
  ["spam", t("Spam")],
  ["harassment", t("Harassment or bullying")],
  ["scam", t("Scam or fraud")],
  ["impersonation", t("Pretending to be someone else")],
  ["explicit_content", t("Explicit content")],
  ["other", t("Something else")],
];

function statusReportSheet(statusId: string): VNode {
  return {
    type: "stack", id: "status-report-sheet",
    style: {
      direction: "column", position: "fixed", inset: 0, z_index: 50,
      background: withAlpha("#000000", 0.6), justify: "flex-end",
    },
    children: [
      {
        type: "stack", id: "status-report-card",
        style: {
          direction: "column", background: C.surface, padding: 16, gap: 4,
          radius: "16 16 0 0",
        },
        children: [
          {
            type: "text", id: "status-report-title",
            props: { value: t("Why are you reporting this?") },
            style: { font_size: 16, font_weight: 600, color: C.textPrimary, padding: "0 0 8 0" },
          },
          ...REPORT_REASONS.map(([wire, label]) => ({
            type: "stack" as const, id: `status-report-${wire}`,
            style: { direction: "row", align: "center", padding: "12 8" },
            action: act("status_report_send", { status_id: statusId, reason: wire }),
            children: [
              {
                type: "text" as const, id: `status-report-${wire}-l`,
                props: { value: label },
                style: { font_size: 15, color: C.textPrimary },
              },
            ],
          })),
          {
            type: "stack", id: "status-report-cancel",
            style: { direction: "row", justify: "center", padding: "12 8" },
            action: act("status_report_cancel"),
            children: [
              {
                type: "text", id: "status-report-cancel-l",
                props: { value: t("Cancel") },
                style: { font_size: 15, color: C.textSecondary },
              },
            ],
          },
        ],
      },
    ],
  };
}

/**
 * **Updates** — the non-chat surface (ADR-0024 A9).
 *
 * Two axes govern everything here: ephemeral vs permanent, and circle vs
 * world. The screen is shaped to match them, exactly as the phones are.
 *
 *   * The **status strip** is ephemeral and circle-only.
 *   * A pinned **Circle | World** switcher splits the PERMANENT content by
 *     who it is from.
 *   * **Circle** holds Moments (D9), and — because the web has no separate
 *     settings screen — the status composer and who may see it.
 *   * **World** holds channels (D6) you follow, and channels to find.
 *
 * ## The duplicate list is gone
 *
 * This screen used to draw every status TWICE: once as a ring in the strip,
 * again as a row under "Recent updates" / "Viewed". The ring already carries
 * seen-vs-unseen, which is how every product in this category expresses it, so
 * the second rendering was the same data wearing a heading that read like a
 * filter.
 */
function updatesView(state: ShellState): VNode {
  const world = state.updatesSegment === "world";

  return {
    type: "stack", id: "status-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("status-header", t("Updates"), state, { search: true, pill: true }),
      // A9: the strip is PINNED ABOVE the switcher, and it is the same strip
      // in both segments. It used to be the first row of Circle's own scroll,
      // which put it BELOW the control and deleted it from World entirely — so
      // seeing who posted this morning cost a segment change, which is the
      // exact thing A9 names.
      statusTray(state),
      updatesSwitcher(state),
      ...(world ? [duniamiSegment(state)] : [circleSegment(state)]),
      // The channel used to be drawn HERE, as a `position: fixed` overlay over
      // everything — the only option before there was a second pane. It is the
      // detail pane's now (`areaDetail`), which is also what fixed Discover:
      // that screen's rows dispatch the same `channel_open` and this tree was
      // the only one that drew the result, so a channel opened from the
      // directory set state and rendered nothing at all.
      //
      // Closing a channel still lands you exactly where you were, on the
      // segment you were reading — the list column never moved.
    ],
  };
}

/** The pinned **Circle | World** control. */
function updatesSwitcher(state: ShellState): VNode {
  const world = state.updatesSegment === "world";
  // A dot, never a number: ADR-0023's rule is that this surface tells nobody
  // how many of anything, and a tab is entitled to say only "there is
  // something".
  const worldUnread = state.channels.some((c) => (c.unread ?? 0) > 0);

  const seg = (id: string, label: string, on: boolean, segment: "circle" | "world", dot: boolean): VNode => ({
    type: "stack", id,
    style: {
      direction: "row", align: "center", justify: "center", gap: 6, flex: 1,
      padding: "8 12", radius: 999,
      background: on ? C.accentWash : "transparent",
    },
    action: act("updates_segment", { segment }),
    children: [
      {
        type: "text" as const, id: `${id}-l`, props: { value: label },
        style: { font_size: 14, font_weight: on ? 700 : 500, color: on ? C.brandEnd : C.textSecondary },
      },
      ...(dot
        ? [{ type: "text" as const, id: `${id}-dot`, props: { value: "●" }, style: { font_size: 9, color: C.brandEnd } }]
        : []),
    ],
  });

  return {
    type: "stack", id: "updates-switcher",
    style: { direction: "row", align: "center", gap: 8, padding: "8 14", background: C.surface },
    children: [
      seg("segment-circle", t("Circle"), !world, "circle", false),
      // "World", not "Duniani". ADR-0024's names table gives every surface an
      // English/Swahili pair and left this one's English column as a dash,
      // which is how Android's first build shipped reading "Circle | Duniani"
      // — two languages in one switcher. The pair is Circle/Wangu and
      // World/Duniani; K1 swaps the whole locale at once.
      seg("segment-world", t("World"), world, "world", worldUnread),
    ],
  };
}

/** Circle — the strip, Moments, and (web only) the status controls the phones
 *  keep on a settings screen. */
function circleSegment(state: ShellState): VNode {
  const audience = state.statusAudience;
  const draft = state.statusDraft;

  const policies = (audience?.policies ?? []).map((policy) => {
    const on = audience?.policy === policy;
    return {
      type: "stack" as const, id: `status-policy-${policy}`,
      style: { direction: "row", align: "center", gap: 10, padding: "10 16" },
      action: act("status_policy", { policy }),
      children: [
        { type: "text" as const, id: `status-policy-${policy}-dot`, props: { value: on ? "●" : "○" }, style: { color: on ? C.brandEnd : C.textSecondary, font_size: 16 } },
        { type: "text" as const, id: `status-policy-${policy}-l`, props: { value: policyLabel(policy) }, style: { font_size: 15, color: C.textPrimary } },
      ],
    };
  });

  return {
    type: "scroll-view", id: "status-body-scroll",
    style: { direction: "column", flex: 1, overflow: "auto" },
    children: [
      sectionLabel("status-compose-label", t("New status")),
      {
        type: "stack", id: "status-compose",
        style: { direction: "column", gap: 8, padding: 16, background: C.surface },
        children: [
          {
            type: "input", id: "status-draft",
            props: { value: draft.body, placeholder: t("Type a status") },
            action: act("status_draft"),
            style: { padding: 12, radius: 10, background: C.bgApp },
          },
          {
            type: "stack", id: "status-compose-actions",
            style: { direction: "row", gap: 8, align: "center" },
            children: [
              button("status-colour", t("Colour"), act("status_colour")),
              button("status-post", state.statusBusy ? t("Posting…") : t("Post"), act("status_post")),
            ],
          },
          // Who sees this, and what it is not. Both, always — the rule is
          // that no surface may claim an encryption it does not have.
          {
            type: "text", id: "status-encryption-note",
            props: { value: t("Only the people you choose can see this. Status is not end-to-end encrypted.") },
            style: { font_size: 12, color: C.textSecondary },
          },
        ],
      },
      sectionLabel("status-own-label", t("My status")),
      {
        type: "stack", id: "status-own",
        style: { direction: "column", background: C.surface },
        children: state.statusOwn.length === 0
          ? [emptyState("status-own-empty", t("Nothing up right now. A status disappears after 24 hours."))]
          : state.statusOwn.map((item) => ({
              type: "stack" as const, id: `status-own-${item.id}`,
              style: { direction: "row", align: "center", gap: 12, padding: "10 16" },
              children: [
                { type: "text" as const, id: `status-own-${item.id}-t`, props: { value: item.caption || item.kind }, style: { flex: 1, font_size: 15, color: C.textPrimary } },
                // The count the AUTHOR sees; nobody else is given one.
                { type: "text" as const, id: `status-own-${item.id}-v`, props: { value: item.views != null ? `Seen by ${item.views}` : t("No views yet") }, style: { font_size: 12, color: C.textSecondary } },
              ],
            })),
      },
      // --- D9, the circle feed (ADR-0024) ---------------------------
      //
      // Its own composer, because a post and a status are different promises
      // — one stays and one is gone tomorrow, and a shared button would mean
      // people publish permanently by accident.
      sectionLabel("post-compose-label", t("New post")),
      postComposer(state),
      sectionLabel("feed-label", t("Posts")),
      ...feedSection(state),
      sectionLabel("status-priv-label", t("Who can see it")),
      {
        type: "stack", id: "status-priv",
        style: { direction: "column", background: C.surface },
        children: policies.length > 0
          ? policies
          : [emptyState("status-priv-empty", t("Loading…"))],
      },
      ...(state.statusError
        ? [{ type: "text" as const, id: "status-error", props: { value: state.statusError }, style: { font_size: 13, color: C.danger, padding: 16 } }]
        : []),
    ],
  };
}

/**
 * **Explore** — the channel directory (ADR-0024 D6, A16).
 *
 * The other half of discovery. Until this existed the only way to a channel
 * was the handful of suggestion rows on the World segment: a channel that
 * cannot be FOUND is a channel nobody follows.
 *
 * Alphabetical rather than ranked, which is a stronger version of ADR-0023's
 * rule and not a weaker one: a directory is scanned, an order that shifts as
 * follower counts change makes the same browse return different pages, and the
 * count does not enter the server's query at all here.
 *
 * A channel you already follow still appears, marked. A directory that hides
 * what you have returns nothing when you search for it, which reads as broken
 * rather than as tidy.
 */
function exploreView(state: ShellState): VNode {
  const children: VNode[] = [];

  // The facets, from the SERVER's list — the taxonomy went from nine values to
  // seven in one migration. "All" is a MEMBER of the row rather than the
  // absence of a choice: a filter you cannot see how to leave is one people
  // leave the screen to escape.
  const chip = (label: string, value: string | null): VNode => {
    const on = state.exploreKind === value;
    return {
      type: "stack", id: `explore-kind-${value ?? "all"}`,
      style: {
        direction: "row", align: "center", padding: "6 12", radius: 999,
        background: on ? C.accentWash : "surface.variant",
        // A chip in a horizontal scroller must not shrink OR wrap. Without
        // both, "News & Information" folded onto two lines inside a pill sized
        // for one and lost its second word — a facet you cannot read is a
        // facet nobody taps.
        flex: "0 0 auto", white_space: "nowrap",
      },
      action: act("explore_kind", { kind: value }),
      children: [{
        type: "text", id: `explore-kind-${value ?? "all"}-t`, props: { value: label },
        style: {
          font_size: 14, font_weight: on ? 700 : 500,
          color: on ? C.brandEnd : C.textPrimary, white_space: "nowrap",
        },
      }],
    };
  };

  children.push({
    type: "stack", id: "explore-search-row",
    style: { direction: "row", align: "center", gap: 8, padding: "10 16", background: C.surface },
    children: [{
      type: "input", id: "explore-query",
      props: { value: state.exploreQuery, placeholder: t("Search channels") },
      action: act("explore_query"),
      style: { flex: 1, padding: 10, radius: 10, background: C.bgApp },
    }],
  });

  if (state.publisherKinds.length > 0) {
    children.push({
      type: "scroll-view", id: "explore-kinds",
      style: { direction: "row", gap: 8, padding: "4 16 10 16", overflow: "auto", background: C.surface },
      children: [chip(t("All"), null), ...state.publisherKinds.map((k) => chip(publisherKindLabel(k, state.publisherKinds), k))],
    });
  }

  if (state.exploreError) {
    children.push(emptyState("explore-error", state.exploreError));
  } else if (state.exploreResults.length === 0 && !state.exploreBusy) {
    children.push(
      emptyState(
        "explore-empty",
        state.exploreQuery === "" && state.exploreKind === null
          ? t("No channels yet. When publishers arrive, they show up here.")
          : "Nothing matches that."
      )
    );
  } else {
    children.push(...state.exploreResults.map((c) => exploreRow(c, state.publisherKinds)));
  }

  return {
    type: "stack", id: "explore-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      // No Back. Discover was a sub-screen of Updates before the rail existed,
      // and its back still went there — so somebody who arrived from the rail
      // (the ordinary way now) was offered a way back to a screen they had not
      // come from. Every rail area is a destination; the rail is how you leave
      // one, and Updates is one click away in it.
      header("explore-header", t("Discover"), state, {}),
      { type: "scroll-view", id: "explore-scroll", style: { direction: "column", flex: 1, overflow: "auto" }, children },
    ],
  };
}

function exploreRow(listing: ChannelListing, kinds: string[]): VNode {
  const name = listing.name ?? t("Channel");
  // The description if there is one, the kind if there is not. A row that says
  // only "Organization" cannot tell somebody whether this is the TCRA they were
  // looking for — but a blank second line is worse than a category, so the kind
  // is the FALLBACK rather than the label.
  const subtitle = (listing.description ?? "").trim() !== ""
    ? (listing.description as string)
    : publisherKindLabel(listing.kind ?? null, kinds);

  return {
    type: "card", id: `explore-${listing.id}`,
    style: { direction: "row", align: "center", gap: 12, padding: "12 16", background: C.surface, margin: "0 0 1 0" },
    children: [
      channelInitialsCircle(`explore-${listing.id}-face`, name, listing.id, 44),
      {
        type: "stack", id: `explore-${listing.id}-meta`,
        style: { direction: "column", flex: 1, gap: 2, min_width: 0 },
        action: act("channel_open", { channel_id: listing.id }),
        children: [
          { type: "text", id: `explore-${listing.id}-n`, props: { value: name }, style: { font_size: 16, font_weight: 600, color: C.textPrimary, white_space: "nowrap", overflow: "hidden", text_overflow: "ellipsis" } },
          ...(subtitle
            ? [{ type: "text" as const, id: `explore-${listing.id}-s`, props: { value: subtitle }, style: { font_size: 13, color: C.textSecondary } }]
            : []),
        ],
      },
      listing.following
        // A word, not a button. Unfollowing belongs on the channel itself,
        // where somebody can see what they are leaving; a one-tap unfollow
        // beside a search result is a mis-tap away from losing a channel they
        // went looking for.
        ? {
            type: "text" as const, id: `explore-${listing.id}-following`,
            props: { value: t("Following") },
            style: { font_size: 14, color: C.textSecondary, padding: "0 8" },
          }
        : button(`explore-${listing.id}-follow`, t("Follow"), act("channel_follow", { channel_id: listing.id })),
    ],
  };
}

/**
 * World — channels you follow, then channels to find, in ONE scroll.
 *
 * Discovery is inline rather than behind a button: the tester screenshots show
 * WhatsApp doing exactly this, and a directory you have to go looking for is a
 * directory nobody opens.
 *
 * What will NEVER appear between these rows is an advert. That, not the shared
 * surface, is what makes the competitor's version of this screen its
 * most-complained-about one.
 */
/**
 * Duniani holds TWO sections, and A10 forbids fusing them into one scroll:
 * *"the axis that separates the three is who can REPLY — mutual contacts,
 * nobody, anyone — and mixing them means the reply model changes silently
 * mid-scroll and people stop knowing whether commenting is safe."*
 */
function duniamiSegment(state: ShellState): VNode {
  const chip = (label: string, on: boolean, id: string, action: VNode["action"]): VNode => ({
    type: "stack", id,
    style: {
      direction: "row", align: "center", padding: "6 12", radius: 999,
      background: on ? C.accentWash : "surface.variant",
      flex: "0 0 auto", white_space: "nowrap",
    },
    action,
    children: [{
      type: "text", id: `${id}-t`, props: { value: label },
      style: {
        font_size: 14, font_weight: on ? 700 : 500,
        color: on ? C.brandEnd : C.textPrimary, white_space: "nowrap",
      },
    }],
  });

  const onMazungumzo = state.duniamiTab === "mazungumzo";

  return {
    type: "stack", id: "duniami-root",
    style: { direction: "column", flex: 1, min_height: 0 },
    children: [
      {
        type: "stack", id: "duniami-tabs",
        style: { direction: "row", gap: 8, padding: "8 16" },
        children: [
          chip(t("Channels"), !onMazungumzo, "tab-channels", act("duniami_tab", { tab: "channels" })),
          chip(t("Mazungumzo"), onMazungumzo, "tab-mazungumzo", act("duniami_tab", { tab: "mazungumzo" })),
        ],
      },
      onMazungumzo ? mazungumzoSection(state) : worldSegment(state),
    ],
  };
}

/**
 * **Mazungumzo** — the public conversation (D12, ADR-0025).
 *
 * Ranked by the SERVER on recency, city, language and social proximity, and on
 * nothing else — no engagement is measured anywhere in this product, so it
 * cannot rank by popularity even if somebody wanted it to. No score arrives, so
 * this shell has nothing to re-sort by even if it tried.
 *
 * "Following" is chronological, and its control does not exist until this
 * person follows somebody (A11/D9) — most viewers see one feed and no switcher.
 */
/**
 * The number beside a control (ADR-0029 D13).
 *
 * Rendered only when there IS one and it is above zero. A circle post carries
 * no `counts` key at all, so an absent count must draw nothing rather than
 * "0" — zero is a true thing to say about a public post and a false thing to
 * say about a private one, and the renderer is where that distinction is
 * either kept or quietly lost.
 */
function countText(id: string, n: number | undefined): VNode[] {
  if (n == null || n <= 0) return [];
  const short =
    n >= 1_000_000
      ? `${Math.round(n / 100_000) / 10}M`
      : n >= 1_000
        ? `${Math.round(n / 100) / 10}K`
        : String(n);
  return [{
    type: "text", id, props: { value: short },
    style: { font_size: 12, color: C.textSecondary },
  }];
}

/** The post a quote points at, or a tombstone when it is gone (D14). */
function quotedBlock(post: CirclePost): VNode {
  const q = post.quoted!;
  const name = q.author?.display_name ?? q.author?.username ?? t("Someone");
  return {
    type: "stack", id: `mz-${post.id}-q`,
    style: {
      direction: "column", gap: 2, padding: "8 10", radius: 10,
      background: "surface.variant", margin: "4 0 0 0",
    },
    children: q.gone
      ? [{
          type: "text", id: `mz-${post.id}-q-gone`,
          props: { value: t("This post is no longer available.") },
          style: { font_size: 13, color: C.textSecondary },
        }]
      : [
          {
            type: "text", id: `mz-${post.id}-q-n`, props: { value: name },
            style: { font_size: 13, font_weight: 600, color: C.textPrimary },
          },
          ...(q.caption
            ? [{
                type: "text" as const, id: `mz-${post.id}-q-b`,
                props: { value: q.caption },
                style: { font_size: 13, color: C.textPrimary },
              }]
            : []),
        ],
  };
}

/**
 * Like, reply, repost, quote, save — with each number beside its OWN control.
 *
 * Repost and quote appear only on a `public` post: amplification is confined to
 * the surface that has an audience of strangers (D12), and the check is on the
 * POST so that rendering a private one here could never grow the buttons.
 */
function mazungumzoActions(post: CirclePost): VNode {
  const publicPost = post.audience === "public";
  const item = (id: string, label: string, on: boolean, action: VNode["action"]): VNode => ({
    type: "text", id, props: { value: label },
    action,
    style: {
      font_size: 13,
      font_weight: on ? 700 : 500,
      color: on ? C.brandEnd : C.textSecondary,
    },
  });

  const children: VNode[] = [
    item(`mz-${post.id}-like`, post.liked ? "♥" : "♡", !!post.liked,
      act("post_like", { post_id: post.id, liked: !post.liked })),
    ...countText(`mz-${post.id}-likec`, post.counts?.likes),
    item(`mz-${post.id}-reply`, t("Reply"), false, act("post_open", { post_id: post.id })),
    ...countText(`mz-${post.id}-replyc`, post.counts?.replies),
  ];

  if (publicPost) {
    children.push(
      item(`mz-${post.id}-repost`, t("Repost"), !!post.reposted,
        act(post.reposted ? "mazungumzo_unrepost" : "mazungumzo_repost", { post_id: post.id })),
      ...countText(`mz-${post.id}-repostc`, post.counts?.reposts),
      item(`mz-${post.id}-quote`, t("Quote"), false,
        act("mazungumzo_quote", { post_id: post.id })),
      ...countText(`mz-${post.id}-quotec`, post.counts?.quotes),
    );
  }

  children.push(
    // No count beside Save, ever: a save is private (D17) and a number would
    // turn a bookmark into a signal.
    item(`mz-${post.id}-save`, post.saved ? t("Saved") : t("Save"), !!post.saved,
      act(post.saved ? "mazungumzo_unsave" : "mazungumzo_save", { post_id: post.id })),
  );

  return {
    type: "stack", id: `mz-${post.id}-actions`,
    style: { direction: "row", align: "center", gap: 10, margin: "6 0 0 0" },
    children,
  };
}

/** Search box and the Feeds drawer, at the top of the SECTION — Updates
 *  already owns the screen's header, and a second bar under the first is how a
 *  surface starts to feel like two apps. */
function mazungumzoBar(state: ShellState): VNode {
  const listChip = (label: string, on: boolean, id: string, list: string | null): VNode => ({
    type: "stack", id,
    style: {
      direction: "row", align: "center", padding: "4 9", radius: 999,
      background: on ? C.accentWash : "surface.variant",
      flex: "0 0 auto", white_space: "nowrap",
    },
    action: act("mazungumzo_list", { list }),
    children: [{
      type: "text", id: `${id}-t`, props: { value: label },
      style: {
        font_size: 12, font_weight: on ? 700 : 500,
        color: on ? C.brandEnd : C.textSecondary, white_space: "nowrap",
      },
    }],
  });

  return {
    type: "stack", id: "mz-bar",
    style: { direction: "column", gap: 6, padding: "0 16 8 16" },
    children: [
      {
        type: "input", id: "mz-search",
        props: { value: state.mazungumzoQuery, placeholder: t("Search Mazungumzo") },
        action: act("mazungumzo_search", {}),
        style: { font_size: 14, padding: "8 10", radius: 10, background: C.surface },
      },
      {
        type: "stack", id: "mz-lists",
        style: { direction: "row", gap: 8 },
        children: [
          // "For you" first, because it is the way BACK — a drawer you can
          // enter and not leave is the shape of a trapped user.
          listChip(t("For you"), state.mazungumzoFeedList === null, "mz-list-foryou", null),
          listChip(t("Liked"), state.mazungumzoFeedList === "liked", "mz-list-liked", "liked"),
          listChip(t("Saved"), state.mazungumzoFeedList === "saved", "mz-list-saved", "saved"),
          listChip(t("Activity"), state.mazungumzoFeedList === "activity", "mz-list-activity", "activity"),
        ],
      },
    ],
  };
}

/** Search results — posts and people kept apart, because merging them would
 *  need an ordering between a person and a sentence that nothing can justify. */
function mazungumzoSearch(state: ShellState): VNode[] {
  const out: VNode[] = [];
  if (state.mazungumzoSearchPosts.length === 0 && state.mazungumzoSearchPeople.length === 0) {
    out.push(emptyState("mz-search-empty", t("Nothing found.")));
    return out;
  }

  if (state.mazungumzoSearchPeople.length > 0) {
    out.push(sectionLabel("mz-search-people", t("People")));
    for (const p of state.mazungumzoSearchPeople) {
      out.push({
        type: "card", id: `mz-sp-${p.user_id}`,
        style: { direction: "row", align: "center", gap: 10, padding: "10 16", background: C.surface },
        children: [{
          type: "text", id: `mz-sp-${p.user_id}-n`,
          props: { value: p.display_name ?? p.username ?? t("Someone") },
          style: { font_size: 14, color: C.textPrimary },
        }],
      });
    }
  }

  if (state.mazungumzoSearchPosts.length > 0) {
    out.push(sectionLabel("mz-search-posts", t("Posts")));
    for (const post of state.mazungumzoSearchPosts) {
      out.push(mazungumzoCard(post, state));
    }
  }
  return out;
}

/** The Activity tab (D17), with Threads' two filters.
 *
 *  Deliberately NO unread badge: a count here would be a counter on the one
 *  surface ADR-0023's refusal still fully governs — your own inbox. */
function mazungumzoActivityList(state: ShellState): VNode[] {
  const verb = (k: ActivityEvent["kind"]): string =>
    k === "reply" ? t("replied to you")
      : k === "quote" ? t("quoted you")
        : k === "repost" ? t("reposted you")
          : "followed you";

  const filterChip = (label: string, value: string | null, id: string): VNode => ({
    type: "stack", id,
    style: {
      direction: "row", align: "center", padding: "4 9", radius: 999,
      background: state.mazungumzoActivityFilter === value
        ? C.accentWash : "surface.variant",
      flex: "0 0 auto", white_space: "nowrap",
    },
    action: act("mazungumzo_activity_filter", value ? { filter: value } : {}),
    children: [{
      type: "text", id: `${id}-t`, props: { value: label },
      style: {
        font_size: 12,
        font_weight: state.mazungumzoActivityFilter === value ? 700 : 500,
        color: state.mazungumzoActivityFilter === value ? C.brandEnd : C.textSecondary,
        white_space: "nowrap",
      },
    }],
  });

  const out: VNode[] = [{
    type: "stack", id: "mz-act-filters",
    style: { direction: "row", gap: 8, padding: "0 16 8 16" },
    children: [
      filterChip(t("All"), null, "mz-act-all"),
      filterChip(t("Mentions"), "mentions", "mz-act-mentions"),
      filterChip(t("People you follow"), "following", "mz-act-following"),
    ],
  }];

  if (state.mazungumzoActivity.length === 0) {
    out.push(emptyState("mz-activity-empty", t("Nothing yet.")));
    return out;
  }

  for (const e of state.mazungumzoActivity) {
    const who = e.actor?.display_name ?? e.actor?.username ?? t("Someone");
    out.push({
      type: "card", id: `mz-act-${e.kind}-${e.actor_id}-${e.post_id ?? "x"}-${e.at ?? ""}`,
      style: { direction: "column", gap: 2, padding: "10 16", background: C.surface, margin: "0 0 1 0" },
      children: [
        {
          type: "text", id: `mz-act-${e.actor_id}-${e.at ?? ""}-n`,
          props: { value: `${who} ${verb(e.kind)}` },
          style: { font_size: 14, color: C.textPrimary },
        },
        ...(e.body
          ? [{
              type: "text" as const, id: `mz-act-${e.actor_id}-${e.at ?? ""}-b`,
              props: { value: e.body },
              style: { font_size: 13, color: C.textSecondary },
            }]
          : []),
      ],
    });
  }
  return out;
}

/** One numbered series, read end to end (D17). */
function mazungumzoThreadView(state: ShellState): VNode {
  return {
    type: "stack", id: "mz-thread",
    style: { direction: "column" },
    children: [
      {
        type: "stack", id: "mz-thread-bar",
        style: { direction: "row", align: "center", gap: 8, padding: "8 16" },
        action: act("mazungumzo_close_thread", {}),
        children: [{
          type: "text", id: "mz-thread-back", props: { value: t("← Thread") },
          style: { font_size: 14, font_weight: 600, color: C.textPrimary },
        }],
      },
      ...state.mazungumzoThread.map((p) => mazungumzoCard(p, state)),
    ],
  };
}

/**
 * ONE Mazungumzo card, used by the feed, by search results and by a thread.
 *
 * Its own card and not the circle feed's: A4's reply rule differs here — a
 * Moment takes comments from mutual contacts only, a Mazungumzo post from
 * anyone — and the circle card renders that mutual-only thread inline.
 * Extracted from the feed loop when search and threads needed it, because
 * three copies of a card is three places for a count to be drawn differently.
 */
function mazungumzoCard(post: CirclePost, state: ShellState): VNode {
  void state;
    // Its OWN card, not the circle feed's.
    //
    // A4's comment rule differs here — a Moment takes comments from mutual
    // contacts only, a Mazungumzo post from anyone — and the circle card
    // renders the mutual-only thread inline. Reusing it would put one reply
    // model's UI under the other's post, which is the exact confusion A10
    // separates these sections to prevent.
    const name = post.author?.display_name ?? post.author?.username ?? t("Someone");
return {
      type: "card", id: `mz-${post.id}`,
      style: {
        direction: "column", gap: 4, padding: "12 16",
        background: C.surface, margin: "0 0 1 0",
      },
      children: [
        {
          type: "stack", id: `mz-${post.id}-h`,
          style: { direction: "row", align: "center", gap: 6 },
          children: [
            {
              type: "text", id: `mz-${post.id}-n`, props: { value: name },
              style: { font_size: 14, font_weight: 600, color: C.textPrimary },
            },
            // "1/5" — tappable, because a number saying there are four more
            // posts and offering no way to reach them is worse than none.
            ...(post.thread
              ? [{
                  type: "text" as const, id: `mz-${post.id}-thread`,
                  props: { value: `${post.thread.index}/${post.thread.of}` },
                  action: act("mazungumzo_thread", { post_id: post.id }),
                  style: {
                    font_size: 12, font_weight: 600, color: C.brandEnd,
                    background: C.accentWash, radius: 6, padding: "1 6",
                  },
                }]
              : []),
            // A ghost post SAYS it disappears. One that quietly vanishes is
            // indistinguishable from one that was deleted or moderated.
            ...(post.comment_policy === "author" && post.audience === "public"
              ? [{
                  type: "text" as const, id: `mz-${post.id}-ghost`,
                  props: { value: t("Ghost · 24h") },
                  style: {
                    font_size: 12, color: C.textSecondary,
                    background: "surface.variant", radius: 6, padding: "1 6",
                  },
                }]
              : []),
          ],
        },
        ...(post.caption
          ? [{
              type: "text" as const, id: `mz-${post.id}-b`,
              props: { value: post.caption },
              style: { font_size: 15, color: C.textPrimary },
            }]
          : []),
        {
          type: "text", id: `mz-${post.id}-t`,
          props: { value: postedAgo(post.posted_at ?? null) },
          style: { font_size: 12, color: C.textSecondary },
        },
        // ADR-0029 D14 — what this post quotes, inside the quoter's card.
        ...(post.quoted ? [quotedBlock(post)] : []),
        // ADR-0029 D13/D14/D17 — the action row and its numbers.
        mazungumzoActions(post),
      ],
    };
}

function mazungumzoSection(state: ShellState): VNode {
  // A thread used to REPLACE this section — a numbered series read inside the
  // feed it came from would number itself against the wrong list, and the only
  // way to keep the two apart was to show one at a time. There is a second
  // pane now, so the thread goes there (`areaDetail`) and the feed stays where
  // it was, which is the whole point of the frame.
  const posts =
    state.mazungumzoFeedList === "saved"
      ? state.mazungumzoSaved
      : state.mazungumzoFeedList === "liked"
        ? state.mazungumzoLiked
        : state.mazungumzoShowFollowing
          ? state.mazungumzoFollowing
          : state.mazungumzoRanked;
  const children: VNode[] = [mazungumzoBar(state)];

  const chip = (label: string, on: boolean, id: string, action: VNode["action"]): VNode => ({
    type: "stack", id,
    style: {
      direction: "row", align: "center", padding: "5 10", radius: 999,
      background: on ? C.accentWash : "surface.variant",
      flex: "0 0 auto", white_space: "nowrap",
    },
    action,
    children: [{
      type: "text", id: `${id}-t`, props: { value: label },
      style: {
        font_size: 13, font_weight: on ? 700 : 500,
        color: on ? C.brandEnd : C.textSecondary, white_space: "nowrap",
      },
    }],
  });

  // Only once there is somewhere to switch TO. A control offering a choice
  // between a feed and an empty one teaches people the surface is broken (A16).
  if (state.followed.length > 0 && state.mazungumzoFeedList === null) {
    children.push({
      type: "stack", id: "mz-segments",
      style: { direction: "row", gap: 8, padding: "0 16 8 16" },
      children: [
        chip(t("What's being said"), !state.mazungumzoShowFollowing, "mz-ranked",
          act("mazungumzo_segment", { following: false })),
        chip(t("Following"), state.mazungumzoShowFollowing, "mz-following",
          act("mazungumzo_segment", { following: true })),
      ],
    });
  }

  // ADR-0029 D17 — search REPLACES the feed while a query stands: a result
  // list under a feed is two answers to one question.
  if (state.mazungumzoQuery.trim().length >= 2) {
    return { type: "stack", id: "mz", style: { direction: "column" },
      children: [mazungumzoBar(state), ...mazungumzoSearch(state)] };
  }

  if (state.mazungumzoFeedList === "activity") {
    return { type: "stack", id: "mz", style: { direction: "column" },
      children: [mazungumzoBar(state), ...mazungumzoActivityList(state)] };
  }

  if (!state.mazungumzoLoaded) {
    children.push(emptyState("mazungumzo-loading", t("Loading…")));
  } else if (posts.length === 0 && state.mazungumzoFeedList === "saved") {
    children.push(emptyState("mazungumzo-saved-empty", t("Nothing saved yet.")));
  } else if (posts.length === 0 && state.mazungumzoFeedList === "liked") {
    children.push(emptyState("mazungumzo-liked-empty", t("Nothing liked yet.")));
  } else if (posts.length === 0 && state.mazungumzoShowFollowing) {
    // Empty is a REAL answer and says so rather than showing the ranked feed.
    children.push(emptyState("mazungumzo-following-empty", t("Nothing yet from the people you follow.")));
  } else if (posts.length === 0) {
    children.push(emptyState(
      "mazungumzo-empty",
      t("Nobody has said anything in public yet. When they do, it shows up here.")
    ));
  } else {
    for (const post of posts) {
      children.push(mazungumzoCard(post, state));
      // A10: Follow sits ON the post being read — a person is found through
      // their words, so it cannot live only in a list.
      if (post.author_id !== state.me?.id) {
        const isFollowing = state.followed.some((u) => u.user_id === post.author_id);
        children.push({
          type: "stack", id: `mz-follow-${post.id}`,
          style: { direction: "row", justify: "flex-end", padding: "0 16 12 16" },
          action: act(
            isFollowing ? "mazungumzo_unfollow" : "mazungumzo_follow",
            { user_id: post.author_id }
          ),
          children: [{
            type: "text", id: `mz-follow-${post.id}-t`,
            props: { value: isFollowing ? t("Following") : t("Follow") },
            style: {
              font_size: 13,
              font_weight: isFollowing ? 500 : 700,
              color: isFollowing ? C.textSecondary : C.brandEnd,
            },
          }],
        });
      }
    }
  }

  return {
    type: "scroll-view", id: "mazungumzo-scroll",
    style: { direction: "column", flex: 1, overflow: "auto" },
    children,
  };
}

function worldSegment(state: ShellState): VNode {
  const children: VNode[] = [];

  // "Channels you follow" does not EXIST until you follow one. An empty
  // section on day one teaches people the surface is broken, and the state it
  // would describe is the state every new account is in — so discovery is
  // simply the first thing there, which is also the only thing worth doing
  // from that screen.
  if (state.channels.length > 0) {
    children.push(sectionLabel("channels-label", t("Channels you follow")));
    children.push(...state.channels.map(channelRow));
    if (state.channelSuggestions.length > 0) {
      children.push({
        type: "stack", id: "world-rule",
        style: { height: 1, margin: "12 16", background: "outline.variant" },
        children: [],
      });
    }
  }

  if (state.channelSuggestions.length > 0) {
    children.push(sectionLabel("suggestions-label", t("Find channels to follow")));
    children.push(...state.channelSuggestions.map((c) => suggestionRow(c, state.publisherKinds)));
  }

  // Only when there is genuinely nothing — no follows AND nothing to suggest.
  // A blank pane with no words in it reads as a failed load.
  if (state.channels.length === 0 && state.channelSuggestions.length === 0) {
    children.push(emptyState("channels-empty", t("No channels yet. When publishers arrive, they show up here.")));
  }

  // The way out of a list of three, and it sits OUTSIDE the suggestions block
  // on purpose: follow everything suggested and that section disappears, which
  // would take the only door to the directory with it — at the exact moment
  // somebody has proved they want more.
  children.push({
    type: "stack", id: "explore-all",
    style: { direction: "row", justify: "center", padding: "14 16" },
    action: act("explore_open", { focus: false }),
    children: [{
      type: "text", id: "explore-all-t", props: { value: t("Explore all channels") },
      style: { font_size: 15, font_weight: 600, color: C.brandEnd },
    }],
  });

  // The other direction: not a channel to READ, one to RUN. A16's third
  // held-back control and the last one — it opens Office, which is what did not
  // exist when the design drew it. Outside the suggestions block for the reason
  // "Explore all channels" is.
  children.push({
    type: "stack", id: "create-channel",
    style: { direction: "row", justify: "center", padding: "0 16 16 16" },
    action: act("office_open"),
    children: [{
      type: "text", id: "create-channel-t", props: { value: t("Create a channel") },
      style: { font_size: 15, font_weight: 600, color: C.brandEnd },
    }],
  });

  return {
    type: "scroll-view", id: "world-scroll",
    style: { direction: "column", flex: 1, overflow: "auto" },
    children,
  };
}

// --- Office (ADR-0024 D6/D7, Phase 3) ---------------------------------------
//
// s136 shipped Explore, so the product has a channel directory. Until Office
// there was no way to put anything in it from inside the app: the first channel
// on production was created with two curl calls, which is fine for one and
// impossible for a market.
//
// Three verbs and no more — create a publisher, turn publishing on, write a
// post. ADR-0018 is what Office eventually contains and none of it is built.
//
// Joana has no `switch` primitive, so the publishing control is a tappable row
// that STATES its position, the same shape `policyRow` uses. That is not a
// compromise here: what the row has to communicate is a capability that is on
// or off, and a word says it more plainly at 320px than a 40px track.

function officeView(state: ShellState): VNode {
  const children: VNode[] = [];

  if (!state.publishersLoaded) {
    // "Not fetched yet" and "you own nothing" want opposite screens, which is
    // why the store tracks them separately: an explanation shown while a
    // request is in flight is one that may be about to be wrong.
    children.push(emptyState("office-loading", t("Loading…")));
  } else if (state.publishers.length === 0) {
    children.push({
      type: "stack", id: "office-empty",
      style: { direction: "column", align: "center", gap: 10, padding: "40 28" },
      children: [
        { type: "text", id: "office-empty-h", props: { value: t("Publish to people who follow you") }, style: { font_size: 17, font_weight: 700, color: C.textPrimary, text_align: "center" } },
        // What a publisher IS, in the words somebody who has never had one
        // would use. A shop, a station, a ministry and a church committee are
        // all the same object here — the whole point of A15's facet.
        { type: "text", id: "office-empty-b", props: { value: t("A channel is an account that broadcasts — a shop, a station, a church, an office. People follow it and see what you post on their Updates. Nobody can reply.") }, style: { font_size: 14, color: C.textSecondary, text_align: "center" } },
        button("office-create-first", t("Create a channel"), act("office_create_open")),
      ],
    });
  } else {
    children.push(...state.publishers.map((p) => publisherRow(p, state.publisherKinds)));
    children.push({
      type: "stack", id: "office-create-row",
      style: { direction: "row", justify: "center", padding: "16" },
      action: act("office_create_open"),
      children: [{
        type: "text", id: "office-create-t", props: { value: t("New channel") },
        style: { font_size: 15, font_weight: 600, color: C.brandEnd },
      }],
    });
  }

  if (state.officeError) children.push(emptyState("office-error", state.officeError));

  return {
    type: "stack", id: "office-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("office-header", t("Office"), state, {}),
      // ADR-0018 step 1 — Office's SECOND section, above the channels. A row
      // and not a tab: nothing enters the header, and a second tab would make
      // this screen about choosing between two things rather than about what
      // this person runs.
      //
      // It is drawn whether or not the reader is in any organisation, because
      // a section that only appears once you have one is a section nobody
      // finds.
      {
        type: "card", id: "office-orgs-row",
        style: { direction: "row", align: "center", gap: 12, padding: "14 16", background: C.surface, margin: "0 0 1 0" },
        action: act("orgs_open"),
        children: [
          {
            type: "stack", id: "office-orgs-meta",
            style: { direction: "column", flex: 1, gap: 2, min_width: 0 },
            children: [
              { type: "text", id: "office-orgs-t", props: { value: t("Organisations") }, style: { font_size: 16, font_weight: 600, color: C.textPrimary } },
              {
                type: "text", id: "office-orgs-s",
                props: {
                  value: state.organisations.length === 0
                    ? t("Work together in one place")
                    : t("In %d organisations").replace("%d", String(state.organisations.length)),
                },
                style: { font_size: 13, color: C.textSecondary },
              },
            ],
          },
          { type: "text", id: "office-orgs-chev", props: { value: "›" }, style: { font_size: 18, color: C.textSecondary } },
        ],
      },
      { type: "text", id: "office-channels-h", props: { value: t("Your channels") }, style: { font_size: 12, font_weight: 700, color: C.brandEnd, padding: "14 16 6 16" } },
      { type: "scroll-view", id: "office-scroll", style: { direction: "column", flex: 1, overflow: "auto" }, children },
    ],
  };
}

// --- ADR-0018 step 1: organisations and zones -------------------------------

/**
 * The trust-domain notice a zone carries (ADR-0018 D3).
 *
 * A SEPARATE node from the encryption banner, so the two states can never share
 * a drawing. The sentence says what is true and does not soften it: "encrypted
 * in transit and at rest" belongs in a settings paragraph, and in a notice it
 * reads as reassurance — which is the failure this whole arc is about.
 */
export function zoneNotice(id: string): VNode {
  return {
    type: "text", id,
    props: {
      value: t("Office zone. WaveTalk's servers can read this zone so an assistant can take part. It is not end-to-end encrypted."),
    },
    style: {
      font_size: 12, color: C.textSecondary, background: "surface.variant",
      padding: "8 16", text_align: "center",
    },
  };
}

/** A label, not a permission — every kind behaves identically. An unfamiliar
 *  value draws the generic word rather than a raw wire token, because the
 *  server owns this taxonomy and it has grown before. */
function zoneKindLabel(kind: string | null | undefined): string {
  switch (kind) {
    case "build": return t("Build");
    case "support": return t("Support");
    case "sales": return t("Sales");
    case "ops": return t("Ops");
    default: return t("Zone");
  }
}

/** A zone's header subtitle: "Zone · Build". A general zone's kind label IS
 *  "Zone", so it says the word once — "Zone · Zone" repeated itself. */
function zoneSubtitle(kind: string | null | undefined): string {
  return ZONE_KINDS_NAMED.has(kind ?? "") ? `${t("Zone")} · ${zoneKindLabel(kind)}` : t("Zone");
}
/** The kinds zoneKindLabel names; every other value draws the word "Zone". */
const ZONE_KINDS_NAMED = new Set(["build", "support", "sales", "ops"]);

function orgRoleLabel(role: string): string {
  switch (role) {
    case "owner": return t("Owner");
    case "admin": return t("Admin");
    default: return t("Member");
  }
}

function organisationsView(state: ShellState): VNode {
  const children: VNode[] = [];

  if (!state.organisationsLoaded) {
    children.push(emptyState("orgs-loading", t("Loading…")));
  } else if (state.organisations.length === 0) {
    children.push({
      type: "stack", id: "orgs-empty",
      style: { direction: "column", align: "center", gap: 10, padding: "40 28" },
      children: [
        { type: "text", id: "orgs-empty-h", props: { value: t("Work together in one place") }, style: { font_size: 17, font_weight: 700, color: C.textPrimary, text_align: "center" } },
        { type: "text", id: "orgs-empty-b", props: { value: t("An organisation is a shop, a church committee, a school or an NGO — the people you work with, and the conversations that belong to the group rather than to whoever started them.") }, style: { font_size: 14, color: C.textSecondary, text_align: "center" } },
        button("orgs-create-first", t("Create an organisation"), act("org_create_open")),
      ],
    });
  } else {
    children.push(...state.organisations.map((org) => organisationRow(org, state.activeOrgId)));
    children.push({
      type: "stack", id: "orgs-create-row",
      style: { direction: "row", justify: "center", padding: "16" },
      action: act("org_create_open"),
      children: [{
        type: "text", id: "orgs-create-t", props: { value: t("New organisation") },
        style: { font_size: 15, font_weight: 600, color: C.brandEnd },
      }],
    });
  }

  if (state.orgError) children.push(emptyState("orgs-error", state.orgError));

  return {
    type: "stack", id: "orgs-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("orgs-header", t("Organisations"), state, { back: true }),
      { type: "scroll-view", id: "orgs-scroll", style: { direction: "column", flex: 1, overflow: "auto" }, children },
    ],
  };
}

function organisationRow(org: Organisation, activeOrgId: string | null): VNode {
  const zones = org.zone_count;
  const people = org.member_count;
  const subtitle = zones == null || people == null
    ? orgRoleLabel(org.role)
    // Counted in the catalogue's own words. Kiswahili's plural is not
    // English's, so "%d zones" with a number substituted would be right in one
    // language and wrong in the other at exactly one value.
    : `${zones === 1 ? t("1 zone") : t("%d zones").replace("%d", String(zones))} · ${people === 1 ? t("1 person") : t("%d people").replace("%d", String(people))}`;

  return {
    type: "card", id: `org-${org.id}`,
    style: {
      direction: "row", align: "center", gap: 12, padding: "12 16",
      background: org.id === activeOrgId ? C.accentWash : C.surface, margin: "0 0 1 0",
    },
    action: act("org_open", { org_id: org.id }),
    children: [
      channelInitialsCircle(`org-${org.id}-face`, org.name, org.id, 44),
      {
        type: "stack", id: `org-${org.id}-meta`,
        style: { direction: "column", flex: 1, gap: 2, min_width: 0 },
        children: [
          { type: "text", id: `org-${org.id}-n`, props: { value: org.name }, style: { font_size: 16, font_weight: 600, color: C.textPrimary, white_space: "nowrap", overflow: "hidden", text_overflow: "ellipsis" } },
          { type: "text", id: `org-${org.id}-s`, props: { value: subtitle }, style: { font_size: 13, color: C.textSecondary } },
        ],
      },
    ],
  };
}

function organisationView(state: ShellState): VNode {
  const org = state.organisations.find((o) => o.id === state.activeOrgId);
  if (!org) return emptyState("org-loading", t("Loading…"));
  if (state.orgEditor) return orgEditorView(state, state.orgEditor);

  const children: VNode[] = [
    {
      type: "text", id: "org-zones-h", props: { value: t("Zones") },
      style: { font_size: 12, font_weight: 700, color: C.brandEnd, padding: "14 16 6 16" },
    },
  ];

  if (state.orgZones.length === 0) {
    children.push(emptyState("org-zones-empty", t("No zones yet.")));
  } else {
    children.push(...state.orgZones.map((zone) => zoneRow(zone)));
  }

  // Only somebody who manages the organisation is offered the control. It is
  // not the gate — the server refuses a member's mutation whatever this draws
  // — it is the honest shape of the screen.
  if (org.role === "owner" || org.role === "admin") {
    children.push({
      type: "stack", id: "org-zone-create-row",
      style: { direction: "row", justify: "center", padding: "12" },
      action: act("zone_create_open"),
      children: [{
        type: "text", id: "org-zone-create-t", props: { value: t("New zone") },
        style: { font_size: 15, font_weight: 600, color: C.brandEnd },
      }],
    });
  }

  children.push({
    type: "text", id: "org-people-h", props: { value: t("People") },
    style: { font_size: 12, font_weight: 700, color: C.brandEnd, padding: "14 16 6 16" },
  });

  children.push(...state.orgMembers.map((m) => organisationMemberRow(m, org.role)));

  // ADR-0037 E — the assistant and API keys. Managers only: every route
  // behind them answers `forbidden` to anybody else.
  if (org.role === "owner" || org.role === "admin") children.push(...assistantSections(state));

  if (org.role !== "owner") {
    children.push({
      type: "stack", id: "org-leave-row",
      style: { direction: "row", justify: "center", padding: "16" },
      action: act("org_leave"),
      children: [{
        type: "text", id: "org-leave-t", props: { value: t("Leave organisation") },
        style: { font_size: 15, font_weight: 600, color: C.danger },
      }],
    });
  }

  if (state.orgError) children.push(emptyState("org-error", state.orgError));

  return {
    type: "stack", id: "org-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("org-header", org.name, state, { back: true, backWhenNarrowOnly: true, subtitle: `@${org.slug} · ${orgRoleLabel(org.role)}` }),
      { type: "scroll-view", id: "org-scroll", style: { direction: "column", flex: 1, overflow: "auto" }, children },
    ],
  };
}

// =============================================================================
// ADR-0037 E — an organisation's assistant and API keys (Android twin:
// OfficeAssistantScreens.kt; the four surfaces offer the same doors in the
// same order).
// =============================================================================

function sectionHeading(id: string, label: string): VNode {
  return {
    type: "text", id, props: { value: label },
    style: { font_size: 12, font_weight: 700, color: C.brandEnd, padding: "14 16 6 16" },
  };
}

function infoRow(id: string, title: string, subtitle: string, action: VAction | undefined): VNode {
  return {
    type: "stack", id,
    style: { direction: "column", gap: 2, padding: "10 16" },
    ...(action ? { action } : {}),
    children: [
      { type: "text", id: `${id}-t`, props: { value: title }, style: { font_size: 15, color: C.textPrimary } },
      { type: "text", id: `${id}-s`, props: { value: subtitle }, style: { font_size: 12, color: C.textSecondary } },
    ],
  };
}

export function zoneAssistantState(enabled: boolean, shape: string | null | undefined): string {
  if (!enabled) return t("Switched off");
  const trimmed = (shape ?? "").trim();
  return trimmed ? t("On, as %s").replace("%s", trimmed) : t("Switched on");
}

function assistantSections(state: ShellState): VNode[] {
  const out: VNode[] = [sectionHeading("org-asst-h", t("Assistant"))];
  const setup = state.assistantSetup;
  if (setup) {
    if (setup.assistant) {
      out.push(infoRow("org-asst-row", setup.assistant.display_name ?? "", t("Tap to rename"), act("asst_name_open")));
      for (const zone of setup.zones) {
        out.push(infoRow(
          `org-asst-zone-${zone.chat_id}`,
          (zone.name ?? "").trim() || zoneKindLabel("general"),
          zoneAssistantState(zone.assistant_enabled, zone.assistant_shape),
          act("asst_zone_open", { chat_id: zone.chat_id }),
        ));
      }
    } else {
      out.push(infoRow(
        "org-asst-add",
        t("Add assistant"),
        t("An assistant can read the zones you switch it on in, and post there through a tool you give an API key. It is not a person and never signs in."),
        act("asst_name_open"),
      ));
    }
  }

  out.push(sectionHeading("org-keys-h", t("API keys")));
  out.push(infoRow(
    "org-key-issue",
    t("Issue an API key"),
    t("A key lets a tool act as this organisation's assistant, only in zones where the assistant is on. Every use is recorded."),
    act("key_issue_open"),
  ));
  for (const key of state.orgApiKeys ?? []) out.push(apiKeyRow(key));
  return out;
}

function scopeLabel(scope: string): string {
  if (scope === "zones:read") return t("Read zones");
  if (scope === "zones:post") return t("Post in zones");
  // A scope this build has never heard of is shown as the server wrote it: it
  // is a permission, and hiding one would understate what a key can do.
  return scope;
}

/** The server writes UTC ISO-8601; a day is all a key's row needs. */
function keyDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(uiLang, { year: "numeric", month: "short", day: "numeric" });
}

function apiKeyRow(key: OrgApiKey): VNode {
  const id = `org-key-${key.id}`;
  const line = (suffix: string, value: string, color: string = C.textSecondary): VNode => ({
    type: "text", id: `${id}-${suffix}`, props: { value }, style: { font_size: 12, color },
  });
  const children: VNode[] = [
    { type: "text", id: `${id}-n`, props: { value: key.name }, style: { font_size: 15, color: C.textPrimary } },
    // The key's public half as the server shows it — a token, not words.
    {
      type: "text", id: `${id}-p`,
      props: { value: [`wtk_${key.key_prefix}_…`, ...key.scopes.map(scopeLabel)].join(" · ") },
      style: { font_size: 12, color: C.textSecondary, font_family: "monospace" },
    },
    line("st", key.revoked_at
      ? t("Revoked %s").replace("%s", keyDay(key.revoked_at))
      : t("Expires %s").replace("%s", keyDay(key.expires_at))),
    line("u", key.last_used_at ? t("Last used %s").replace("%s", keyDay(key.last_used_at)) : t("Never used")),
  ];
  // The leaked-key signal (ADR-0037 A): a live key used from outside its
  // allow-list, or aimed at another organisation's zone.
  const refused = key.refused_count ?? 0;
  if (refused > 0) {
    children.push(line("rf", refused === 1
      ? t("Refused once — check where it is used")
      : t("Refused %d times — check where it is used").replace("%d", String(refused)), C.danger));
  }
  if (!key.revoked_at) {
    children.push({
      type: "stack", id: `${id}-act`, style: { direction: "row", padding: "4 0 0 0" },
      children: [button(`${id}-revoke`, t("Revoke"), act("key_revoke_open", { key_id: key.id, name: key.name }))],
    });
  }
  return { type: "stack", id, style: { direction: "column", gap: 2, padding: "10 16" }, children };
}

function editorField(id: string, label: string, value: string, field: string, opts: { placeholder?: string; multiline?: boolean; max: number }): VNode {
  return {
    type: "stack", id: `${id}-wrap`,
    style: { direction: "column", gap: 4, padding: "8 16" },
    children: [
      { type: "text", id: `${id}-l`, props: { value: label }, style: { font_size: 13, color: C.textSecondary } },
      {
        type: opts.multiline ? "textarea" : "input", id,
        props: { value, placeholder: opts.placeholder ?? "", max_length: opts.max },
        events: { input: act("org_editor_field", { field }) },
        style: { padding: 10, radius: 10, background: C.surface },
      },
    ],
  };
}

function choiceChip(id: string, label: string, on: boolean, action: VAction): VNode {
  return {
    type: "stack", id,
    style: {
      direction: "row", align: "center", padding: "6 12", radius: 999,
      background: on ? C.accentWash : "surface.variant", flex: "0 0 auto", white_space: "nowrap",
    },
    action,
    children: [{ type: "text", id: `${id}-t`, props: { value: label }, style: { font_size: 13, color: on ? C.brandEnd : C.textPrimary } }],
  };
}

const KEY_DAYS = [30, 90, 365];

/** The one open editor, in place of the organisation's screen. */
function orgEditorView(state: ShellState, editor: OrgEditor): VNode {
  let title: string;
  let save: { label: string; disabled: boolean } | null = { label: t("Save"), disabled: state.orgBusy };
  let cancel = t("Cancel");
  const children: VNode[] = [];

  switch (editor.kind) {
    case "assistant_name":
      title = editor.creating ? t("Add assistant") : t("Assistant name");
      if (editor.creating) {
        children.push(emptyState("asst-name-note", t("The assistant belongs to this organisation. It has no phone number, never signs in, and reads only the zones you switch it on in.")));
      }
      children.push(editorField("asst-name", t("Assistant name"), editor.value, "value", { max: 60 }));
      save = { label: editor.creating ? t("Add") : t("Save"), disabled: state.orgBusy || !editor.value.trim() };
      break;
    case "zone_assistant":
      title = t("Assistant in %s").replace("%s", editor.zone_name);
      children.push({
        type: "stack", id: "zone-asst-switch-row",
        style: { direction: "row", align: "center", gap: 10, padding: "12 16" },
        children: [
          { type: "text", id: "zone-asst-switch-l", props: { value: t("Assistant in this zone") }, style: { font_size: 15, color: C.textPrimary, flex: 1 } },
          choiceChip("zone-asst-switch", editor.enabled ? t("On") : t("Off"), editor.enabled,
            act("org_editor_field", { field: "enabled", value: !editor.enabled })),
        ],
      });
      children.push(emptyState("zone-asst-note", t("When it is on, the assistant is a member of this zone and can read it. Everyone in the zone sees it join or leave.")));
      children.push(editorField("zone-asst-shape", t("Shape"), editor.shape, "shape", { placeholder: t("For example: Ideas, Support"), max: 40 }));
      children.push(editorField("zone-asst-instr", t("Instructions"), editor.instructions, "instructions", { multiline: true, max: 4000 }));
      break;
    case "key_issue": {
      title = t("Issue an API key");
      children.push(editorField("key-name", t("Key name"), editor.name, "name", { placeholder: t("What will use it"), max: 80 }));
      children.push({
        type: "stack", id: "key-scopes", style: { direction: "row", gap: 8, padding: "8 16", flex_wrap: "wrap" },
        children: [
          choiceChip("key-scope-read", t("Read zones"), editor.read, act("org_editor_field", { field: "read", value: !editor.read })),
          choiceChip("key-scope-post", t("Post in zones"), editor.post, act("org_editor_field", { field: "post", value: !editor.post })),
        ],
      });
      children.push({ type: "text", id: "key-days-l", props: { value: t("Lasts") }, style: { font_size: 13, color: C.textSecondary, padding: "8 16 4 16" } });
      children.push({
        type: "stack", id: "key-days", style: { direction: "row", gap: 8, padding: "0 16", flex_wrap: "wrap" },
        children: KEY_DAYS.map((d) => choiceChip(`key-days-${d}`, t("%d days").replace("%d", String(d)), editor.days === d,
          act("org_editor_field", { field: "days", value: d }))),
      });
      const ready = editor.name.trim().length > 0 && (editor.read || editor.post);
      save = { label: t("Issue"), disabled: state.orgBusy || !ready };
      break;
    }
    case "key_issued":
      // Shown ONCE. The secret lives only in this editor slot; Done drops it.
      title = t("Your new API key");
      children.push({ type: "text", id: "key-once", props: { value: t("Copy it now. You will not see this key again.") }, style: { font_size: 14, color: C.danger, padding: "12 16" } });
      children.push({ type: "text", id: "key-secret", props: { value: editor.secret }, style: { font_size: 13, font_family: "monospace", padding: "8 16", user_select: "text", word_break: "break-all", color: C.textPrimary } });
      children.push({
        type: "stack", id: "key-copy-row", style: { direction: "row", align: "center", gap: 10, padding: "8 16" },
        children: [
          button("key-copy", t("Copy"), act("key_copy")),
          ...(editor.copied ? [{ type: "text" as const, id: "key-copied", props: { value: t("Copied.") }, style: { font_size: 12, color: C.textSecondary } }] : []),
        ],
      });
      save = null;
      cancel = t("Done");
      break;
    case "key_revoke":
      title = t("Revoke");
      children.push(emptyState("key-revoke-q", t("Revoke “%s”? Anything using it stops working at once.").replace("%s", editor.name)));
      save = { label: t("Revoke"), disabled: state.orgBusy };
      break;
  }

  if (state.orgError) children.push(emptyState("org-editor-error", state.orgError));

  children.push({
    type: "stack", id: "org-editor-actions",
    style: { direction: "row", justify: "center", gap: 12, padding: 16 },
    children: [
      button("org-editor-cancel", cancel, act("org_editor_close")),
      ...(save ? [primaryButton("org-editor-save", save.label, act("org_editor_save"), save.disabled)] : []),
    ],
  });

  return {
    type: "stack", id: "org-editor-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("org-editor-header", title, state),
      { type: "scroll-view", id: "org-editor-scroll", style: { direction: "column", flex: 1, overflow: "auto" }, children },
    ],
  };
}

function zoneRow(zone: Chat): VNode {
  return {
    type: "card", id: `zone-${zone.id}`,
    style: { direction: "row", align: "center", gap: 12, padding: "10 16", background: C.surface, margin: "0 0 1 0" },
    // A zone opens the CONVERSATION, which is the whole point of it being a
    // chat: the same thread, the same composer, the same everything.
    action: act("zone_open", { chat_id: zone.id }),
    children: [
      channelInitialsCircle(`zone-${zone.id}-face`, zone.name ?? "", zone.id, 36),
      {
        type: "stack", id: `zone-${zone.id}-meta`,
        style: { direction: "column", flex: 1, gap: 2, min_width: 0 },
        children: [
          { type: "text", id: `zone-${zone.id}-n`, props: { value: zone.name ?? "" }, style: { font_size: 15, font_weight: 600, color: C.textPrimary, white_space: "nowrap", overflow: "hidden", text_overflow: "ellipsis" } },
          { type: "text", id: `zone-${zone.id}-s`, props: { value: zoneKindLabel(zone.zone_kind) }, style: { font_size: 12, color: C.textSecondary } },
        ],
      },
    ],
  };
}

function organisationMemberRow(member: OrganisationMember, viewerRole: string): VNode {
  const name = (member.display_name ?? "").trim() || (member.username ? `@${member.username}` : t("Someone"));
  const children: VNode[] = [
    avatar(`orgm-${member.user_id}-face`, name, {
      ownerId: member.user_id, av: member.avatar ?? null, frame: member.frame ?? null, size: 36,
    }),
    {
      type: "stack", id: `orgm-${member.user_id}-meta`,
      style: { direction: "column", flex: 1, gap: 2, min_width: 0 },
      children: [
        { type: "text", id: `orgm-${member.user_id}-n`, props: { value: name }, style: { font_size: 15, color: C.textPrimary, white_space: "nowrap", overflow: "hidden", text_overflow: "ellipsis" } },
        { type: "text", id: `orgm-${member.user_id}-r`, props: { value: orgRoleLabel(member.role) }, style: { font_size: 12, color: C.textSecondary } },
      ],
    },
  ];

  // The owner is never offered for removal — that is a transfer — and a
  // manager is not offered controls over themselves through this door.
  const manageable = (viewerRole === "owner" || viewerRole === "admin") && member.role !== "owner";
  if (manageable) {
    children.push(
      button(
        `orgm-${member.user_id}-role`,
        member.role === "member" ? t("Make admin") : t("Remove as admin"),
        act("org_set_role", {
          user_id: member.user_id,
          role: member.role === "member" ? "admin" : "member",
        }),
      ),
      button(`orgm-${member.user_id}-rm`, t("Remove"), act("org_remove_member", { user_id: member.user_id })),
    );
  }

  return {
    type: "stack", id: `orgm-${member.user_id}`,
    style: { direction: "row", align: "center", gap: 10, padding: "8 16", flex_wrap: "wrap" },
    children,
  };
}

function createOrganisationView(state: ShellState): VNode {
  const name = state.orgForm.name;
  // The handle is shown BEFORE it is committed to, because it cannot be
  // changed afterwards — the server refuses it in the update changeset.
  // Discovering an unchangeable value after the fact is what this prevents.
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

  const children: VNode[] = [
    {
      type: "stack", id: "orgnew-name-wrap",
      style: { direction: "column", gap: 4, padding: "8 16" },
      children: [
        { type: "text", id: "orgnew-name-l", props: { value: t("Organisation name") }, style: { font_size: 13, color: C.textSecondary } },
        {
          // `input`, not `text-input`: the latter is not a node type and the
          // renderer's `TAG[type] ?? "div"` obliged silently, which made three
          // composers on this shell un-typable for as long as it took somebody
          // to try (s136).
          type: "input", id: "orgnew-name",
          props: { value: name, placeholder: "" },
          events: { input: act("org_form", { field: "name" }) },
          style: { padding: 10, radius: 10, background: C.surface },
        },
      ],
    },
  ];

  if (slug.length >= 3) {
    children.push({
      type: "text", id: "orgnew-slug",
      props: { value: t("Handle: @%s. This cannot be changed later.").replace("%s", slug) },
      style: { font_size: 12, color: C.textSecondary, padding: "0 16 8 16" },
    });
  }

  if (state.orgError) children.push(emptyState("orgnew-error", state.orgError));

  children.push({
    type: "stack", id: "orgnew-submit-wrap",
    style: { direction: "row", justify: "center", padding: 16 },
    children: [primaryButton("orgnew-submit", t("Create organisation"), act("org_create"), slug.length < 3 || state.orgBusy)],
  });

  return {
    type: "stack", id: "orgnew-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("orgnew-header", t("New organisation"), state, { back: true }),
      { type: "scroll-view", id: "orgnew-scroll", style: { direction: "column", flex: 1, overflow: "auto" }, children },
    ],
  };
}

const ZONE_KINDS = ["general", "build", "support", "sales", "ops"] as const;

function createZoneView(state: ShellState): VNode {
  const form = state.zoneForm;

  const children: VNode[] = [
    {
      type: "stack", id: "zonenew-name-wrap",
      style: { direction: "column", gap: 4, padding: "8 16" },
      children: [
        { type: "text", id: "zonenew-name-l", props: { value: t("Zone name") }, style: { font_size: 13, color: C.textSecondary } },
        {
          type: "input", id: "zonenew-name",
          props: { value: form.name, placeholder: "" },
          events: { input: act("zone_form", { field: "name" }) },
          style: { padding: 10, radius: 10, background: C.surface },
        },
      ],
    },
    {
      type: "text", id: "zonenew-kind-l", props: { value: t("What is this zone for?") },
      style: { font_size: 13, color: C.textSecondary, padding: "8 16 4 16" },
    },
    {
      type: "stack", id: "zonenew-kinds",
      style: { direction: "row", gap: 8, padding: "0 16", flex_wrap: "wrap" },
      children: ZONE_KINDS.map((kind) => ({
        type: "stack" as const, id: `zonenew-kind-${kind}`,
        style: {
          direction: "row", align: "center", padding: "6 12", radius: 999,
          background: form.kind === kind ? C.accentWash : "surface.variant",
          flex: "0 0 auto", white_space: "nowrap",
        },
        action: act("zone_form", { field: "kind", value: kind }),
        children: [{
          type: "text" as const, id: `zonenew-kind-${kind}-t`,
          props: { value: zoneKindLabel(kind) },
          style: { font_size: 13, color: form.kind === kind ? C.brandEnd : C.textPrimary },
        }],
      })),
    },
    // Said BEFORE the zone exists, not discovered inside it: a person choosing
    // where a conversation lives should know what the choice means for who can
    // read it.
    zoneNotice("zonenew-notice"),
  ];

  if (state.orgError) children.push(emptyState("zonenew-error", state.orgError));

  children.push({
    type: "stack", id: "zonenew-submit-wrap",
    style: { direction: "row", justify: "center", padding: 16 },
    children: [primaryButton("zonenew-submit", t("Create zone"), act("zone_create"), form.name.trim().length === 0 || state.orgBusy)],
  });

  return {
    type: "stack", id: "zonenew-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("zonenew-header", t("New zone"), state, { back: true }),
      { type: "scroll-view", id: "zonenew-scroll", style: { direction: "column", flex: 1, overflow: "auto" }, children },
    ],
  };
}

function publisherRow(publisher: Publisher, kinds: string[]): VNode {
  const title = (publisher.display_name ?? "").trim() || publisher.name;
  const label = publisherKindLabel(publisher.kind ?? null, kinds);
  const subtitle = label ? `@${publisher.name} · ${label}` : `@${publisher.name}`;
  const on = publisher.publishes === true;

  return {
    type: "card", id: `publisher-${publisher.id}`,
    style: { direction: "row", align: "center", gap: 12, padding: "12 16", background: C.surface, margin: "0 0 1 0" },
    action: act("publisher_open", { publisher_id: publisher.id }),
    children: [
      channelInitialsCircle(`publisher-${publisher.id}-face`, title, publisher.id, 44),
      {
        type: "stack", id: `publisher-${publisher.id}-meta`,
        style: { direction: "column", flex: 1, gap: 2, min_width: 0 },
        children: [
          {
            type: "text", id: `publisher-${publisher.id}-n`,
            // `approved` is the server's own word. The business list tested for
            // "verified", which the schema's whitelist does not contain, so the
            // tick it drew could never appear on any account, ever.
            props: { value: publisher.verification_status === "approved" ? `${title} ✓` : title },
            style: { font_size: 16, font_weight: 600, color: C.textPrimary, white_space: "nowrap", overflow: "hidden", text_overflow: "ellipsis" },
          },
          { type: "text", id: `publisher-${publisher.id}-s`, props: { value: subtitle }, style: { font_size: 13, color: C.textSecondary } },
        ],
      },
      {
        // The one fact this list exists to show. "Not publishing" rather than
        // nothing, because turning it on is why its owner came here.
        type: "text", id: `publisher-${publisher.id}-state`,
        props: { value: on ? t("Publishing") : t("Not publishing") },
        style: { font_size: 12, color: on ? C.brandEnd : C.textSecondary },
      },
    ],
  };
}

/**
 * Create a publisher.
 *
 * The form asks for exactly what the server requires and one optional line. It
 * does NOT ask for standing: `account_type` was castable from the request body
 * until s138, and a form offering "Official" would be the same hole with a
 * label on it. Standing is an operator's to grant, on a different axis (A15).
 */
function createPublisherView(state: ShellState): VNode {
  const form = state.officeForm;
  const handleValid = form.name.trim().length >= 3;

  const field = (id: string, label: string, value: string, placeholder: string): VNode => ({
    type: "stack", id: `pub-${id}-wrap`,
    style: { direction: "column", gap: 4, padding: "8 16" },
    children: [
      { type: "text", id: `pub-${id}-l`, props: { value: label }, style: { font_size: 13, color: C.textSecondary } },
      {
        // `input`, not `text-input`. The latter is not a node type and never
        // has been, and the renderer's `TAG[type] ?? "div"` obliged silently —
        // three composers on this shell were un-typable for as long as it took
        // somebody to try (s136).
        type: "input", id: `pub-${id}`,
        props: { value, placeholder },
        events: { input: act("office_form", { field: id }) },
        style: { padding: 10, radius: 10, background: C.surface },
      },
    ],
  });

  const chip = (id: string, label: string, on: boolean, action: VNode["action"]): VNode => ({
    type: "stack", id,
    style: {
      direction: "row", align: "center", padding: "6 12", radius: 999,
      background: on ? C.accentWash : "surface.variant",
      flex: "0 0 auto", white_space: "nowrap",
    },
    action,
    children: [{
      type: "text", id: `${id}-t`, props: { value: label },
      style: { font_size: 14, font_weight: on ? 700 : 500, color: on ? C.brandEnd : C.textPrimary, white_space: "nowrap" },
    }],
  });

  const children: VNode[] = [
    field("display_name", t("Name"), form.displayName, t("What people see")),
    field("name", t("Handle"), form.name, t("unique, permanent")),
    {
      type: "text", id: "pub-handle-hint",
      props: {
        // The handle is the one field creation fixes for good, and the server's
        // rule is 3-100 and unique. Saying so here is cheaper than a 409 after
        // the round trip.
        value: form.name === "" || handleValid
          ? t("The handle is unique and permanent. Lower case, no spaces.")
          : "The handle needs at least 3 characters.",
      },
      style: { font_size: 12, color: C.textSecondary, padding: "0 16 8 16" },
    },
    sectionLabel("pub-kind-label", t("What kind of publisher is this?")),
    {
      type: "scroll-view", id: "pub-kinds",
      style: { direction: "row", gap: 8, padding: "0 16 10 16", overflow: "auto" },
      // From the SERVER's list, with no fallback: the taxonomy went from nine
      // values to seven in one migration.
      children: state.publisherKinds.map((k) =>
        chip(`pub-kind-${k}`, publisherKindLabel(k, state.publisherKinds), form.kind === k, act("office_form", { field: "kind", value: k }))
      ),
    },
  ];

  // The industry only means something for a BUSINESS (A15, and a CHECK).
  if (form.kind === "business") {
    children.push(sectionLabel("pub-cat-label", t("What does it trade in?")));
    children.push({
      type: "scroll-view", id: "pub-cats",
      style: { direction: "row", gap: 8, padding: "0 16 10 16", overflow: "auto" },
      children: state.businessCategories.map((c) =>
        chip(`pub-cat-${c}`, c.replace(/_/g, " "), form.category === c, act("office_form", { field: "category", value: c }))
      ),
    });
  }

  children.push(field("description", t("What is it? (optional)"), form.description, t("One line, shown in Explore")));
  children.push(field("email", t("Contact email"), form.email, "you@example.com"));
  children.push(field("phone", t("Contact phone"), form.phone, "+255…"));

  if (state.officeError) {
    children.push({
      type: "text", id: "pub-error", props: { value: state.officeError },
      style: { font_size: 14, color: C.danger, padding: "8 16" },
    });
  }

  children.push({
    type: "stack", id: "pub-submit-wrap", style: { direction: "column", padding: "12 16 32 16" },
    children: [button("pub-submit", state.officeBusy ? t("Creating…") : t("Create"), act("office_create"))],
  });

  return {
    type: "stack", id: "office-new-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("office-new-header", t("New channel"), state, { back: true }),
      { type: "scroll-view", id: "office-new-scroll", style: { direction: "column", flex: 1, overflow: "auto" }, children },
    ],
  };
}

/**
 * One publisher: the switch that makes it a channel, and the composer.
 *
 * The composer is hidden until publishing is on rather than shown-and-refused.
 * The server answers 403 for a post to an account with the capability off, and
 * a composer that always takes text and sometimes throws it away is worse than
 * one that says what has to happen first.
 */
function publisherView(state: ShellState): VNode {
  const publisher = state.publishers.find((p) => p.id === state.officePublisherId) ?? null;

  if (!publisher) {
    return {
      type: "stack", id: "office-pub-root",
      style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
      children: [
        header("office-pub-header", t("Channel"), state, { back: true }),
        emptyState("publisher-loading", t("Loading…")),
      ],
    };
  }

  const title = (publisher.display_name ?? "").trim() || publisher.name;
  const on = publisher.publishes === true;
  const label = publisherKindLabel(publisher.kind ?? null, state.publisherKinds);
  const children: VNode[] = [
    {
      type: "stack", id: "publishing-row",
      style: { direction: "row", align: "center", gap: 10, padding: "14 16", background: C.surface },
      action: act("publisher_publishing", { publisher_id: publisher.id, enabled: !on }),
      children: [
        { type: "text", id: "publishing-dot", props: { value: on ? "●" : "○" }, style: { color: on ? C.brandEnd : C.textSecondary, font_size: 16 } },
        {
          type: "stack", id: "publishing-texts", style: { direction: "column", flex: 1, gap: 1 },
          children: [
            { type: "text", id: "publishing-l", props: { value: t("Publishing") }, style: { font_size: 15, color: C.textPrimary } },
            {
              type: "text", id: "publishing-h",
              props: {
                value: on
                  ? t("People can follow this and see what you post.")
                  : "Turn this on to post to followers.",
              },
              style: { font_size: 12, color: C.textSecondary },
            },
          ],
        },
      ],
    },
    {
      type: "text", id: "publisher-sub",
      props: { value: label ? `@${publisher.name} · ${label}` : `@${publisher.name}` },
      style: { font_size: 13, color: C.textSecondary, padding: "8 16" },
    },
  ];

  if (on) {
    children.push({
      type: "stack", id: "publish-composer-row",
      style: { direction: "row", align: "center", gap: 8, padding: "8 16", background: C.surface },
      children: [
        {
          type: "input", id: "publish-composer",
          props: { value: state.officeDraft, placeholder: t("Write to your followers") },
          events: { input: act("publisher_draft") },
          style: { flex: 1, padding: 10, radius: 10, background: C.bgApp },
        },
        button("publish-send", state.officeBusy ? "…" : t("Post"), act("publisher_publish", { publisher_id: publisher.id })),
      ],
    });
  }

  if (state.officeError) {
    children.push({
      type: "text", id: "publisher-error", props: { value: state.officeError },
      style: { font_size: 14, color: C.danger, padding: "8 16" },
    });
  }

  if (state.officeTimeline.length === 0) {
    children.push(emptyState(
      "publisher-timeline-empty",
      on ? t("Nothing posted yet.") : t("Publishing is off, so nobody can follow this yet.")
    ));
  } else {
    children.push(...state.officeTimeline.map((post) => ({
      type: "card" as const, id: `office-post-${post.id}`,
      style: { direction: "column" as const, gap: 4, padding: "12 16", background: C.surface, margin: "0 0 1 0" },
      children: [
        { type: "text" as const, id: `office-post-${post.id}-b`, props: { value: channelPreview(post) }, style: { font_size: 15, color: C.textPrimary } },
        { type: "text" as const, id: `office-post-${post.id}-t`, props: { value: postedAgo(post.posted_at ?? null) }, style: { font_size: 12, color: C.textSecondary } },
      ],
    })));
  }

  return {
    type: "stack", id: "office-pub-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      // Narrow-only, exactly like the conversation's: in a two-pane window the
      // list of channels is right there and "back" points at what you can
      // already see. The COMPOSER keeps its back at every width, because a
      // half-typed form needs a way out that is not "click something else".
      header("office-pub-header", title, state, { back: true, backWhenNarrowOnly: true }),
      { type: "scroll-view", id: "office-pub-scroll", style: { direction: "column", flex: 1, overflow: "auto" }, children },
    ],
  };
}

function channelRow(channel: Channel): VNode {
  const name = channel.name ?? t("Channel");
  const preview = channelPreview(channel.latest ?? null);
  const unread = channel.unread ?? 0;

  return {
    type: "card", id: `channel-${channel.id}`,
    style: { direction: "row", align: "center", gap: 12, padding: "12 16", background: C.surface, margin: "0 0 1 0" },
    action: act("channel_open", { channel_id: channel.id }),
    children: [
      // Initials, not a photo: a channel has no avatar on the wire yet, and a
      // blank circle in every row reads as a list that failed to load.
      channelInitialsCircle(`channel-${channel.id}-face`, name, channel.id, 44),
      {
        type: "stack", id: `channel-${channel.id}-meta`,
        // `min_width: 0` all the way down, and it is not decoration: a flex
        // child will not shrink below its content, so a long channel name
        // pushed the unread badge off the right edge of the row instead of
        // ellipsing. The name is the thing that gives way.
        style: { direction: "column", flex: 1, gap: 3, min_width: 0 },
        children: [
          {
            type: "stack", id: `channel-${channel.id}-top`,
            style: { direction: "row", align: "baseline", gap: 8, min_width: 0 },
            children: [
              { type: "text", id: `channel-${channel.id}-n`, props: { value: name }, style: { flex: 1, min_width: 0, font_size: 16, font_weight: 600, color: C.textPrimary, white_space: "nowrap", overflow: "hidden", text_overflow: "ellipsis" } },
              // The stamp takes the accent while something is unread, so the
              // row says "new" twice — once where the eye lands on a name and
              // once at the badge. The same fact, not a second one.
              { type: "text", id: `channel-${channel.id}-t`, props: { value: channelStamp(channel.latest ?? null) }, style: { font_size: 12, color: unread > 0 ? C.brandEnd : C.textSecondary } },
            ],
          },
          ...(preview
            ? [{ type: "text" as const, id: `channel-${channel.id}-p`, props: { value: preview }, style: { font_size: 13, color: C.textSecondary, white_space: "nowrap", overflow: "hidden", text_overflow: "ellipsis", min_width: 0 } }]
            : []),
        ],
      },
      // Posts THIS READER has not read — the same fact a chat badge carries,
      // about the reader and never about the author. A muted channel reports
      // 0, so the row stays and the badge goes.
      ...(unread > 0
        ? [{
            type: "text" as const, id: `channel-${channel.id}-u`,
            props: { value: unread > 99 ? "99+" : String(unread) },
            style: { font_size: 12, font_weight: 700, color: C.onTint, background: C.gradEnd, radius: 999, padding: "2 7" },
          }]
        : []),
    ],
  };
}

function suggestionRow(channel: Channel, kinds: string[]): VNode {
  const name = channel.name ?? t("Channel");
  // The KIND, and deliberately no follower count: the count orders this list
  // inside the server's query and never leaves it. A kind the server has not
  // listed still renders — an unknown value means this client is older than
  // the server, and its own word is more use than a blank.
  const kind = publisherKindLabel(channel.kind ?? null, kinds);

  return {
    type: "card", id: `suggest-${channel.id}`,
    style: { direction: "row", align: "center", gap: 12, padding: "12 16", background: C.surface, margin: "0 0 1 0" },
    children: [
      channelInitialsCircle(`suggest-${channel.id}-face`, name, channel.id, 40),
      {
        type: "stack", id: `suggest-${channel.id}-meta`,
        style: { direction: "column", flex: 1, gap: 2, min_width: 0 },
        action: act("channel_open", { channel_id: channel.id }),
        children: [
          { type: "text", id: `suggest-${channel.id}-n`, props: { value: name }, style: { font_size: 16, font_weight: 600, color: C.textPrimary } },
          ...(kind
            ? [{ type: "text" as const, id: `suggest-${channel.id}-k`, props: { value: kind }, style: { font_size: 13, color: C.textSecondary } }]
            : []),
        ],
      },
      button(`suggest-${channel.id}-follow`, t("Follow"), act("channel_follow", { channel_id: channel.id })),
    ],
  };
}

/**
 * A channel's reading screen (ADR-0036), in the detail pane.
 *
 * A conversation without a composer, the way WhatsApp draws a channel: the
 * header with the avatar, the tick and the FOLLOWER COUNT (the World half's
 * number, ADR-0029 D13 — drawn on a channel, never on a person); a thread with
 * the newest post at the bottom and a day line above the first post of each
 * day; incoming-style bubbles (nobody here is "me") carrying the media or the
 * caption and the time; under each, the reaction pill and the forward pill.
 *
 * Media comes through the STATUS byte route — a channel post IS a `statuses`
 * row (057) — and the size pill is the promise ADR-0023 D11 makes on a
 * metered network: nothing heavier than a poster is fetched until the reader
 * taps it. A reaction pill is drawn from the SERVER's aggregate and never
 * from a local +1, because the count is over readers this browser cannot see.
 *
 * It was `channelOverlay` — `position: fixed; inset: 0` over the whole shell,
 * the honest answer when the web had one column. Beside a list it is a pane.
 * "Done" still closes it, because on a NARROW window this is the one pane.
 */
function channelDetail(state: ShellState): VNode {
  const listed =
    state.channels.find((c) => c.id === state.channelViewing) ??
    state.channelSuggestions.find((c) => c.id === state.channelViewing) ??
    null;
  const detail = state.channelDetail;
  const name = detail?.name ?? listed?.name ?? t("Channel");
  const muted = detail?.muted ?? listed?.muted ?? false;

  const body: VNode[] = state.channelBusy
    ? [emptyState("channel-loading", t("Loading…"))]
    : state.channelError
      ? [emptyState("channel-error", state.channelError)]
      : state.channelPosts.length === 0
        ? [emptyState("channel-empty", t("This channel has not posted yet."))]
        : channelThread(state);

  return {
    type: "stack", id: "channel-detail",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.chatCanvas },
    children: [
      channelBar(state, name, detail, muted),
      // A REVERSED column: the browser anchors a reversed flex container at
      // its end, so the pane opens on the newest post the way a conversation
      // does, and older pages append at the far end without a scroll jump —
      // the same trick Android's `reverseLayout` plays. The children are in
      // wire order (newest first) for that reason.
      {
        type: "scroll-view", id: "channel-detail-scroll",
        style: { direction: "column-reverse", flex: 1, overflow: "auto", padding: "8 12" },
        children: body,
      },
      ...(state.channelNotice
        ? [{
            type: "text" as const, id: "channel-notice", props: { value: state.channelNotice },
            style: { padding: "8 16", font_size: 13, color: C.textSecondary, background: C.surface, flex_shrink: 0 },
          }]
        : []),
      ...(state.channelLightbox ? [channelLightbox(state)] : []),
      ...(state.channelForwarding ? [channelForwardSheet(state)] : []),
    ],
  };
}

/** Avatar · name + tick · "N followers" · Mute · Done. */
function channelBar(state: ShellState, name: string, detail: Channel | null, muted: boolean): VNode {
  const followers = detail?.followers ?? null;
  return {
    type: "stack", id: "channel-detail-bar",
    style: {
      direction: "row", align: "center", gap: 10, padding: "10 16",
      background: C.surface, border_bottom: "1px solid outline.variant", flex_shrink: 0,
    },
    children: [
      channelInitialsCircle("channel-detail-face", name, state.channelViewing ?? name, 36),
      {
        type: "stack", id: "channel-detail-titles",
        style: { direction: "column", flex: 1, gap: 1, min_width: 0 },
        children: [
          {
            type: "text", id: "channel-detail-title",
            props: { value: detail?.verified ? `${name} ✓` : name },
            style: { font_size: 16, font_weight: 700, color: C.textPrimary, white_space: "nowrap", overflow: "hidden", text_overflow: "ellipsis" },
          },
          ...(followers !== null
            ? [{
                type: "text" as const, id: "channel-detail-followers",
                props: { value: followers === 1 ? t("1 follower") : t("%d followers").replace("%d", compactCount(followers)) },
                style: { font_size: 12, color: C.textSecondary },
              }]
            : []),
        ],
      },
      // Mute is "stop waking me", never "hide it from me": the row stays.
      ...(detail?.following
        ? [{
            type: "button" as const, id: "channel-mute",
            props: { label: muted ? t("Unmute") : t("Mute") },
            style: { background: C.accentWash, color: C.brandEnd, border: "none", font_size: 13, font_weight: 600, padding: "6 12", radius: 999 },
            action: act("channel_mute", { muted: !muted }),
          }]
        : []),
      button("channel-close", t("Done"), act("channel_close")),
    ],
  };
}

/**
 * The thread, oldest at the top and newest at the bottom — the wire serves
 * newest first, so it is reversed here. A day line goes ABOVE the first post
 * of each day. "Load older" sits at the very top, and only while the server
 * has more: a control for nothing teaches people the surface is broken.
 */
function channelThread(state: ShellState): VNode[] {
  // Newest first, drawn in a reversed column: the FIRST child sits at the
  // bottom. A day line goes above the first post of its day, which in this
  // order means AFTER the last post of that day.
  const posts = state.channelPosts;
  const out: VNode[] = [];
  posts.forEach((post, i) => {
    out.push(channelPostBubble(post, state));
    const older = posts[i + 1] ?? null;
    if (!older || dayKey(older.posted_at ?? null) !== dayKey(post.posted_at ?? null)) {
      out.push(channelDayLine(post.id, dayLabel(post.posted_at ?? null)));
    }
  });
  if (!state.channelAtEnd) {
    out.push({
      type: "stack", id: "channel-more-row",
      style: { direction: "row", justify: "center", padding: "4 0 8 0" },
      children: [button("channel-more", state.channelLoadingMore ? t("Loading…") : t("Load older posts"), act("channel_more"))],
    });
  }
  return out;
}

/**
 * The pill that starts a day — "Today", "Yesterday", "5 April 2026". Built for
 * the channel screen (ADR-0036) and, from s174, drawn in every conversation
 * too (Android s173, the phones and the desktop alongside): a thread over
 * three days read as one undated column until then. ONE function, so the
 * channel and a chat cannot disagree about what a day line looks like.
 */
function dayLine(id: string, label: string): VNode {
  return {
    type: "stack", id,
    style: { direction: "row", justify: "center", padding: "8 0" },
    children: [{
      type: "text", id: `${id}-t`, props: { value: label },
      style: { font_size: 12, color: C.textSecondary, background: C.bubbleThem, padding: "4 10", radius: 8 },
    }],
  };
}

function channelDayLine(postId: string, label: string): VNode {
  return dayLine(`channel-day-${postId}`, label);
}

/** The six a reader can choose from, the same six Android offers. */
const CHANNEL_REACTIONS = ["❤️", "👍", "😂", "😮", "😢", "🙏"];

/**
 * One post: an incoming-style bubble with the media or the caption and the
 * time bottom-right; under it the reaction pill, the emoji row when it is
 * open, and — for a text post — the forward pill.
 */
function channelPostBubble(post: ChannelPost, state: ShellState): VNode {
  const caption = (post.caption ?? "").trim();
  const total = post.reaction_total ?? 0;
  const top = (post.reactions ?? []).map((r) => r.emoji).join("");
  const pickerOpen = state.channelPicker === post.id;

  const body: VNode[] = [];
  if (post.kind === "photo") body.push(channelPhoto(post));
  else if (post.kind === "video") body.push(channelVideo(post, state));
  else if (post.kind === "voice") body.push(channelVoice(post, state));
  if (caption !== "") {
    body.push({
      type: "text", id: `channel-post-${post.id}-b`, props: { value: caption },
      style: { font_size: 15, color: C.bubbleThemText, padding: "4 6", white_space: "pre-wrap" },
    });
  }
  body.push({
    type: "text", id: `channel-post-${post.id}-t`, props: { value: clockTime(post.posted_at ?? null) },
    style: { font_size: 11, color: C.textSecondary, align_self: "flex-end", padding: "0 6 2 6" },
  });

  return {
    type: "stack", id: `channel-post-${post.id}`,
    style: { direction: "column", align: "flex-start", gap: 4, padding: "4 0" },
    children: [
      {
        type: "card", id: `channel-post-${post.id}-card`,
        style: { direction: "column", max_width: 340, width: "100%", padding: 6, radius: 12, background: C.bubbleThem },
        children: body,
      },
      {
        type: "stack", id: `channel-post-${post.id}-pills`,
        style: { direction: "row", align: "center", gap: 6 },
        children: [
          {
            type: "stack", id: `channel-post-${post.id}-react`,
            style: {
              direction: "row", align: "center", gap: 6, padding: "4 10", radius: 14,
              background: post.my_reaction ? C.accentWash : C.bubbleThem, cursor: "pointer",
            },
            action: act("channel_pick", { post_id: pickerOpen ? null : post.id }),
            children: [{
              type: "text", id: `channel-post-${post.id}-react-t`,
              props: { value: total > 0 ? `${top} ${compactCount(total)}` : "♡" },
              style: { font_size: 13, color: C.textPrimary },
            }],
          },
          // Text only on the web: this shell cannot send media into a chat
          // yet, and a caption sent as "the photo" would be a lie.
          ...(post.kind === "text" && caption !== ""
            ? [{
                type: "stack" as const, id: `channel-post-${post.id}-fwd`,
                style: { direction: "row", align: "center", padding: "4 10", radius: 14, background: C.bubbleThem, cursor: "pointer" },
                action: act("channel_forward_open", { post_id: post.id }),
                children: [{ type: "text" as const, id: `channel-post-${post.id}-fwd-t`, props: { value: "➤" }, style: { font_size: 13, color: C.textPrimary } }],
              }]
            : []),
        ],
      },
      ...(pickerOpen
        ? [{
            type: "stack" as const, id: `channel-post-${post.id}-picker`,
            style: { direction: "row", gap: 4, padding: "2 0" },
            children: CHANNEL_REACTIONS.map((emoji) => ({
              type: "stack" as const, id: `channel-post-${post.id}-pick-${emoji.codePointAt(0)}`,
              style: {
                padding: "4 8", radius: 12, cursor: "pointer",
                background: post.my_reaction === emoji ? C.accentWash : C.surface,
              },
              action: act("channel_react", { post_id: post.id, emoji: post.my_reaction === emoji ? null : emoji }),
              children: [{ type: "text" as const, id: `channel-post-${post.id}-pick-${emoji.codePointAt(0)}-t`, props: { value: emoji }, style: { font_size: 20 } }],
            })),
          }]
        : []),
    ],
  };
}

/** The size pill WhatsApp draws before the bytes are spent. */
function channelSizePill(id: string, bytes: number | null | undefined): VNode[] {
  if (!bytes) return [];
  return [{
    type: "text", id, props: { value: `⬇ ${formatBytes(bytes)}` },
    style: { position: "absolute", font_size: 13, font_weight: 600, color: C.onMedia, background: withAlpha("#000000", 0.55), padding: "6 12", radius: 16 },
  }];
}

function channelPhoto(post: ChannelPost): VNode {
  const blob = (post.renditions ?? []).includes("full") ? statusBlob(post.id, "full") : null;
  return {
    type: "stack", id: `channel-post-${post.id}-photo`,
    style: {
      direction: "row", align: "center", justify: "center", position: "relative", width: "100%",
      min_height: blob ? 0 : 180, radius: 10, overflow: "hidden", background: blob ? "transparent" : C.outline, cursor: "pointer",
    },
    ...(blob ? { action: act("channel_media_open", { post_id: post.id }) } : {}),
    children: blob
      ? [{ type: "image", id: `channel-post-${post.id}-img`, props: { url: blob, alt: post.caption ?? "" }, style: { width: "100%", radius: 10, display: "block" } }]
      : channelSizePill(`channel-post-${post.id}-size`, post.media_bytes),
  };
}

/** The poster with the size pill until the reader taps; then the clip, by
 *  the browser's own player, autoplaying because a tap asked for it. */
function channelVideo(post: ChannelPost, state: ShellState): VNode {
  const poster = statusBlob(post.id, "poster");
  const asked = state.channelPlaying.includes(post.id);
  const clip = asked ? statusBlob(post.id, "video") : null;
  if (clip) {
    return {
      type: "video", id: `channel-post-${post.id}-video`,
      props: { url: clip, poster: poster ?? "", controls: true, autoplay: true, playsinline: true },
      style: { width: "100%", max_height: 320, radius: 10, display: "block", background: C.backdrop },
    };
  }
  return {
    type: "stack", id: `channel-post-${post.id}-poster`,
    style: {
      direction: "row", align: "center", justify: "center", position: "relative", width: "100%",
      min_height: poster ? 0 : 180, radius: 10, overflow: "hidden", background: poster ? "transparent" : C.outline, cursor: "pointer",
    },
    action: act("channel_play", { post_id: post.id }),
    children: [
      ...(poster ? [{ type: "image" as const, id: `channel-post-${post.id}-img`, props: { url: poster, alt: post.caption ?? "" }, style: { width: "100%", radius: 10, display: "block" } }] : []),
      ...(asked
        ? [{ type: "text" as const, id: `channel-post-${post.id}-fetching`, props: { value: t("Loading…") }, style: { position: "absolute", font_size: 13, color: C.onMedia, background: withAlpha("#000000", 0.55), padding: "6 12", radius: 16 } }]
        : channelSizePill(`channel-post-${post.id}-size`, post.media_bytes)),
    ],
  };
}

/** "🎤 0:04 · 25 KB" until tapped; then the browser's own audio player. */
function channelVoice(post: ChannelPost, state: ShellState): VNode {
  const asked = state.channelPlaying.includes(post.id);
  const clip = asked ? statusBlob(post.id, "voice") : null;
  if (clip) {
    return {
      type: "audio", id: `channel-post-${post.id}-audio`,
      props: { url: clip, controls: true, autoplay: true },
      style: { width: "100%", min_width: 260, display: "block" },
    };
  }
  const parts = [t("Voice")];
  if (post.duration_ms) parts.push(formatDuration(post.duration_ms));
  if (post.media_bytes) parts.push(formatBytes(post.media_bytes));
  return {
    type: "stack", id: `channel-post-${post.id}-voice`,
    style: { direction: "row", align: "center", gap: 8, padding: "8 6", cursor: "pointer", min_width: 220 },
    action: act("channel_play", { post_id: post.id }),
    children: [{
      type: "text", id: `channel-post-${post.id}-voice-t`,
      props: { value: `${asked ? "…" : "▶"} 🎤 ${parts.join(" · ")}` },
      style: { font_size: 15, color: C.bubbleThemText },
    }],
  };
}

/** The photo, full-screen, over the shell. A tap anywhere closes it. */
function channelLightbox(state: ShellState): VNode {
  const post = state.channelPosts.find((p) => p.id === state.channelLightbox) ?? null;
  const blob = post ? statusBlob(post.id, "full") : null;
  return {
    type: "stack", id: "channel-lightbox",
    style: { direction: "column", position: "fixed", inset: 0, background: C.backdrop, z_index: 40, align: "center", justify: "center" },
    action: act("channel_media_close"),
    children: [
      ...(blob
        ? [{ type: "image" as const, id: "channel-lightbox-img", props: { url: blob, alt: post?.caption ?? "" }, style: { max_width: "100%", max_height: "100%", object_fit: "contain" } }]
        : []),
      button("channel-lightbox-close", t("Close"), act("channel_media_close")),
    ],
  };
}

/** Pick a chat to forward into. The chats this browser already lists; the
 *  send goes through the normal path, so a sealed chat refuses it itself. */
function channelForwardSheet(state: ShellState): VNode {
  const postId = state.channelForwarding ?? "";
  return {
    type: "stack", id: "channel-forward",
    style: { direction: "column", position: "fixed", inset: 0, background: C.backdrop, z_index: 40, align: "center", justify: "center", padding: 24 },
    children: [{
      type: "card", id: "channel-forward-card",
      style: { direction: "column", width: "100%", max_width: 420, max_height: "80%", background: C.surface, radius: 16, padding: 0, overflow: "hidden" },
      children: [
        { type: "text", id: "channel-forward-title", props: { value: t("Forward to") }, style: { font_size: 18, font_weight: 700, color: C.textPrimary, padding: "16 20 8 20" } },
        {
          type: "scroll-view", id: "channel-forward-list",
          style: { direction: "column", flex: 1, overflow: "auto" },
          children: state.chats.map((chat) => ({
            type: "stack" as const, id: `channel-forward-${chat.id}`,
            style: { direction: "row", align: "center", gap: 12, padding: "10 20", cursor: "pointer" },
            action: act("channel_forward", { post_id: postId, chat_id: chat.id }),
            children: [{ type: "text" as const, id: `channel-forward-${chat.id}-t`, props: { value: chatTitle(chat) }, style: { font_size: 15, color: C.textPrimary } }],
          })),
        },
        {
          type: "stack", id: "channel-forward-actions",
          style: { direction: "row", justify: "flex-end", padding: "8 12 12 12" },
          children: [button("channel-forward-cancel", t("Cancel"), act("channel_forward_close"))],
        },
      ],
    }],
  };
}

/** "15:21" in the reader's own clock. */
function clockTime(at: string | null): string {
  const ms = at ? Date.parse(at) : NaN;
  if (Number.isNaN(ms)) return "";
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function dayKey(at: string | null): string {
  const ms = at ? Date.parse(at) : NaN;
  if (Number.isNaN(ms)) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Today / Yesterday / the date. */
function dayLabel(at: string | null): string {
  const key = dayKey(at);
  const now = new Date();
  if (key === dayKey(now.toISOString())) return t("Today");
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (key === dayKey(yesterday.toISOString())) return t("Yesterday");
  return new Date(Date.parse(at ?? "")).toLocaleDateString(uiLang, { day: "numeric", month: "long", year: "numeric" });
}

/** "50 KB" / "1.2 MB", the way the download pill says it. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** 1.2K / 3.4M, the same shape the phones draw a count in. */
function compactCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0).replace(/\.0$/, "")}K`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
}

/** One line for a channel row. A media post has no caption, so the KIND
 *  carries the meaning — the same reason a chat preview never uses the body
 *  alone. */
function channelPreview(post: ChannelPost | null): string {
  return post ? channelPostText(post) : "";
}

function channelPostText(post: ChannelPost): string {
  if (post.caption && post.caption.trim() !== "") return post.caption;
  if (post.kind === "photo") return t("Photo");
  if (post.kind === "video") return t("Video");
  if (post.kind === "voice") return t("Voice");
  return "";
}

/**
 * A publisher kind rendered for a person.
 *
 * The list comes from the server; this only supplies the English wording. A
 * kind the server serves but this build has no wording for falls back to the
 * server's own value capitalised, never to "Other" — an unknown kind means
 * this client is older than the server, and hiding it helps nobody.
 */
function publisherKindLabel(kind: string | null, kinds: string[]): string {
  if (!kind) return "";
  const known: Record<string, string> = {
    person: "Person",
    organization: "Organization",
    business: "Business",
    news: "News & Information",
    sports: "Sports",
    entertainment: "Entertainment",
    lifestyle: "Lifestyle",
  };
  if (known[kind]) return known[kind];
  // Not in this build's wording table: show the server's word if the server
  // served it, and still show it if it did not — the value came from the
  // server either way.
  void kinds;
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

/** "12m ago" — the same shape the tray rows use, and never a count of anyone. */
function postedAgo(at: string | null): string {
  const ms = at ? Date.parse(at) : NaN;
  if (Number.isNaN(ms)) return "";
  const minutes = Math.max(0, Math.floor((Date.now() - ms) / 60_000));
  if (minutes < 1) return t("just now");
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}h ago`;
  return `${Math.floor(minutes / (60 * 24))}d ago`;
}

/** The post composer. The audience is chosen PER POST (A2) — that is the
 *  difference between "who sees my updates", a preference, and "who is this
 *  for", a decision about the one thing you are about to say.
 *
 *  `public` is not offered and cannot be: Duniani is Phase 3, and the server
 *  refuses the value (A6). */
function postComposer(state: ShellState): VNode {
  const chosen = state.postDraft.audience;
  const options = ["contacts", "only", "nobody"];

  return {
    type: "stack", id: "post-compose",
    style: { direction: "column", gap: 8, padding: 16, background: C.surface },
    children: [
      {
        type: "input", id: "post-draft",
        props: { value: state.postDraft.body, placeholder: t("What's happening?") },
        action: act("post_draft"),
        style: { padding: 12, radius: 10, background: C.bgApp },
      },
      {
        type: "stack", id: "post-audience-row",
        style: { direction: "row", gap: 8, align: "center" },
        children: options.map((option) => ({
          type: "stack" as const, id: `post-audience-${option}`,
          style: {
            direction: "row", align: "center", gap: 6, padding: "6 10", radius: 14,
            background: option === chosen ? C.brandEnd : C.bgApp,
          },
          action: act("post_audience", { audience: option }),
          children: [{
            type: "text" as const, id: `post-audience-${option}-l`,
            props: { value: postAudienceLabel(option) },
            style: { font_size: 12, color: option === chosen ? C.onAccent : C.textPrimary },
          }],
        })),
      },
      {
        type: "stack", id: "post-compose-actions",
        style: { direction: "row", gap: 8, align: "center" },
        children: [button("post-publish", state.postBusy ? t("Posting…") : t("Post"), act("post_publish"))],
      },
      // Who sees it, and what it is not. Both, always — the same sentence the
      // status composer shows, because a promise phrased twice is two promises.
      {
        type: "text", id: "post-encryption-note",
        props: { value: t("A post stays until you delete it. Only the people you choose can see it, and it is not end-to-end encrypted.") },
        style: { font_size: 12, color: C.textSecondary },
      },
      ...(state.feedError
        ? [{ type: "text" as const, id: "post-error", props: { value: state.feedError }, style: { font_size: 12, color: C.danger } }]
        : []),
    ],
  };
}

/**
 * The feed. Reverse-chronological, no ranking (A5).
 *
 * Nothing here draws a NUMBER. No like count, no comment count, no "seen by":
 * the comments listed ARE what this viewer may see under the mutual-only rule,
 * and a total would leak the existence of the ones they may not.
 */
function feedSection(state: ShellState): VNode[] {
  if (state.feed.length === 0) {
    return [emptyState("feed-empty", t("Nothing posted yet. Write one, and the people you chat with will see it."))];
  }

  return state.feed.map((post) => {
    const mine = post.author_id === state.me?.id;
    const name = post.author?.display_name ?? post.author?.username ?? t("Someone");

    const comments: VNode[] = (post.comments ?? []).map((c) => ({
      type: "stack" as const, id: `comment-${c.id}`,
      style: { direction: "row", gap: 6, align: "center" },
      children: [
        {
          type: "text" as const, id: `comment-${c.id}-a`,
          props: { value: `${c.author?.display_name ?? c.author?.username ?? t("Someone")}:` },
          style: { font_size: 12, font_weight: 600, color: C.textPrimary },
        },
        { type: "text" as const, id: `comment-${c.id}-b`, props: { value: c.body }, style: { flex: 1, font_size: 12, color: C.textPrimary } },
        ...(c.author_id === state.me?.id || mine
          ? [{
              type: "text" as const, id: `comment-${c.id}-x`,
              props: { value: t("Delete") },
              style: { font_size: 11, color: C.textSecondary },
              action: act("post_comment_delete", { post_id: post.id, comment_id: c.id }),
            }]
          : []),
      ],
    }));

    const commenting = state.feedCommenting === post.id;

    return {
      type: "stack" as const, id: `post-${post.id}`,
      style: { direction: "column", gap: 6, padding: 16, background: C.surface },
      children: [
        {
          type: "stack" as const, id: `post-${post.id}-head`,
          style: { direction: "row", align: "center", gap: 10 },
          children: [
            avatar(`post-${post.id}-av`, name, {
              ownerId: post.author_id,
              av: post.author?.avatar ?? null,
              frame: post.author?.frame ?? null,
              size: 36,
            }),
            { type: "text" as const, id: `post-${post.id}-n`, props: { value: name }, style: { flex: 1, font_size: 14, font_weight: 600, color: C.textPrimary } },
            // The audience chip, on YOUR OWN posts only: "who can see this" is
            // the one thing an author must never guess, and nobody else's
            // business.
            ...(mine
              ? [{ type: "text" as const, id: `post-${post.id}-aud`, props: { value: postAudienceLabel(post.audience) }, style: { font_size: 11, color: C.textSecondary } }]
              : []),
          ],
        },
        ...(post.caption
          ? [{ type: "text" as const, id: `post-${post.id}-c`, props: { value: post.caption }, style: { font_size: 15, color: C.textPrimary } }]
          : []),
        {
          type: "stack" as const, id: `post-${post.id}-actions`,
          style: { direction: "row", gap: 12, align: "center" },
          children: [
            {
              type: "text" as const, id: `post-${post.id}-like`,
              props: { value: post.liked ? t("♥ Liked") : t("♡ Like") },
              style: { font_size: 13, color: post.liked ? C.brandEnd : C.textSecondary },
              action: act("post_like", { post_id: post.id, liked: !post.liked }),
            },
            ...(post.comment_policy !== "off"
              ? [{
                  type: "text" as const, id: `post-${post.id}-comment`,
                  props: { value: t("Comment") },
                  style: { font_size: 13, color: C.textSecondary },
                  action: act("post_comment_open", { post_id: commenting ? "" : post.id }),
                }]
              : []),
            ...(mine
              ? [{
                  type: "text" as const, id: `post-${post.id}-del`,
                  props: { value: t("Delete") },
                  style: { font_size: 13, color: C.textSecondary },
                  action: act("post_delete", { post_id: post.id }),
                }]
              : []),
          ],
        },
        ...(comments.length > 0
          ? [{
              type: "stack" as const, id: `post-${post.id}-comments`,
              style: { direction: "column", gap: 4, padding: 10, radius: 8, background: C.bgApp },
              children: comments,
            }]
          : []),
        ...(commenting
          ? [
              {
                type: "input" as const, id: `post-${post.id}-cdraft`,
                props: { value: state.feedDraft, placeholder: t("Add a comment") },
                action: act("post_comment_draft"),
                style: { padding: 10, radius: 8, background: C.bgApp },
              },
              button(`post-${post.id}-csend`, t("Post comment"), act("post_comment_send", { post_id: post.id })),
            ]
          : []),
      ],
    };
  });
}

/** The words the composer chip and the post chip both use, so a post cannot
 *  describe its audience one way when it is made and another when it is read. */
function postAudienceLabel(audience: string): string {
  switch (audience) {
    case "contacts": return t("People I chat with");
    case "groups": return t("Chosen groups");
    case "only": return t("Only the people I picked");
    case "nobody": return t("Only me");
    case "public": return t("Everyone");
    default: return audience;
  }
}

// "People I chat with", not "My contacts": this product has no address book,
// and calling it one would be a promise the data cannot keep.
function policyLabel(policy: string): string {
  switch (policy) {
    case "everyone": return t("Everyone");
    case "contacts": return t("People I chat with");
    case "only": return t("Only share with…");
    default: return t("Nobody");
  }
}

/** A conversation's face: a direct chat wears the other person's; a group
 *  wears its icon, else the first counterpart's face stands in (the full
 *  four-tile mosaic is native-first; one face beats a letter meanwhile). */
function chatAvatar(chat: Chat, title: string, size = 44): VNode {
  const id = `av-${chat.id}`;
  const peer = chat.members?.[0] ?? null;
  if (chat.chat_type === "direct" && peer) {
    return avatar(id, title, { ownerId: peer.user_id, av: peer.avatar ?? null, frame: peer.frame ?? null, size });
  }
  if (chat.avatar_version) {
    return avatar(id, title, { chatIconOf: chat.id, av: { kind: "photo", version: chat.avatar_version }, size });
  }
  if (peer?.avatar) {
    return avatar(id, title, { ownerId: peer.user_id, av: peer.avatar, frame: peer.frame ?? null, size });
  }
  return avatar(id, title, { size });
}

/** ADR-0023 D10 — the mood glyph beside a name, the one piece of Status the
 *  chat list keeps now that the tray lives on Updates. */
function moodBadgeFor(state: ShellState, chat: Chat): string | null {
  const peer = chat.chat_type === "direct" ? (chat.members ?? [])[0]?.user_id ?? null : null;
  if (!peer) return null;
  const entry = state.statusTray.find((e) => e.author_id === peer);
  const newest = entry?.items[entry.items.length - 1] ?? null;
  return newest && newest.kind === "mood" ? newest.mood_glyph ?? null : null;
}

function chatRow(chat: Chat, viewerId: string | null, moodGlyph: string | null = null): VNode {
  const title = chatTitle(chat);
  const preview = chatPreview(chat, viewerId);
  const unread = chat.unread_count > 0;
  return {
    type: "card",
    id: `chat-${chat.id}`,
    style: {
      direction: "row",
      align: "center",
      gap: 12,
      padding: "12 16",
      background: C.surface,
      margin: "0 0 1 0",
    },
    action: act("open_chat", { chat_id: chat.id }),
    children: [
      chatAvatar(chat, title),
      {
        type: "stack",
        id: `meta-${chat.id}`,
        style: { direction: "column", flex: 1, gap: 2 },
        children: [
          {
            type: "stack", id: `name-row-${chat.id}`,
            style: { direction: "row", align: "center", gap: 5 },
            children: [
              { type: "text", id: `name-${chat.id}`, props: { value: title }, style: { font_size: 16, font_weight: 600, color: C.textPrimary } },
              // NO PADLOCK HERE — ADR-0033 D1a, the same subtraction the
              // phones made in the same change.
              //
              // s161 put one beside every sealed row and it was right for a
              // world where sealing was a per-chat choice: the shape of a
              // person's encrypted conversations was otherwise invisible. It
              // is the wrong answer for the one we are walking into, where
              // every chat is sealed because it is a chat — a glyph true of
              // every row distinguishes none of them.
              //
              // `chat.e2e` is still carried and still written by `setChatE2E`:
              // it is what decides whether the composer's device note appears,
              // and ADR-0033 gives this slot to a "needs update" chip, which is
              // the one thing about a row that is actionable.
              ...(moodGlyph
                ? [{ type: "text" as const, id: `mood-${chat.id}`, props: { value: moodGlyph }, style: { font_size: 13 } }]
                : []),
            ],
          },
          { type: "text", id: `prev-${chat.id}`, props: { value: preview }, style: { font_size: 14, color: C.textSecondary } },
        ],
      },
      ...(unread
        ? [{
            type: "text" as const,
            id: `unread-${chat.id}`,
            props: { value: String(chat.unread_count) },
            style: {
              background: C.gradEnd,
              color: C.onTint,
              font_size: 12,
              font_weight: 700,
              radius: 999,
              padding: "2 8",
              min_width: 20,
              text_align: "center",
            },
          }]
        : []),
    ],
  };
}

// --- conversation ----------------------------------------------------------

function conversationView(state: ShellState): VNode {
  const chat = chatById(state, state.activeChatId);
  const title = chat ? chatTitle(chat) : EMPTY_CHAT_TITLE;
  const typingLabel = state.typing.length > 0 ? t("typing…") : null;

  return {
    type: "stack",
    id: "conv-root",
    // The chat canvas, not the app's surface — see `C.chatCanvas`. Using
    // `bgApp` here was invisible in light (grey behind a white bubble) and
    // erased every incoming bubble in dark, where both roles land on #221F2B.
    //
    // G16: a `var(…)` and not the role, because the wallpaper is a CHOICE and
    // the role is one fixed pair. The custom property is published by the host
    // for both appearances (`wallpaperCss`) and falls back to the role, so a
    // build whose host never called `onWallpaper` draws exactly what it drew
    // before this existed.
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: `var(--wt-wallpaper-canvas, ${color(C.chatCanvas)})` },
    children: [
      header("conv-header", title, state, {
        back: true, backWhenNarrowOnly: true,
        // ADR-0018 D3 — a zone says what KIND of conversation it is where
        // another chat says what its encryption is. The two are exclusive, not
        // stacked: a zone can never be end-to-end encrypted, so drawing the
        // encryption subtitle over one would be answering a question that does
        // not apply.
        subtitle: typingLabel ?? (chat?.chat_type === "zone"
          ? zoneSubtitle(chat.zone_kind)
          : encryptionSubtitle(state)),
      }),
      // One line down a zone draws NOTHING. `encryptionBar` offers "turn it
      // on", which in a zone is an affordance for something the server
      // refuses, a CHECK refuses and a composite foreign key refuses; and the
      // zone's own card is gone too (ADR-0037 B) — the zone says it is not
      // end-to-end encrypted ONCE, in its dated `zone_created` line.
      ...(chat?.chat_type === "zone" ? [] : encryptionBar(state)),
      ...historySyncBar(state),
      // A failed send, in words, over the thread.
      //
      // `state.error` used to be drawn on the LOGIN screen and nowhere else, so
      // every `setError` a send made was invisible: the old "Message failed to
      // send" had never once reached a screen, and ADR-0027 D2d's refusal would
      // have joined it. Found by driving the browser, not by a test — the shell
      // suite asserts `store.state.error`, which was set correctly the whole
      // time.
      //
      // A bar rather than a modal: it must not interrupt, and the person's text
      // is back in the composer waiting for them.
      ...(state.error ? [sendErrorBar(state.error)] : []),
      // ADR-0037 B — this zone's minimum web build is above this bundle. No
      // thread and no composer (the server would refuse what it collected);
      // the header stays so it is clear which conversation this is.
      ...(state.activeZoneUpdateRequired
        ? [zoneUpdateRequiredView()]
        : [
            {
              type: "scroll-view",
              id: "msg-list",
              style: { direction: "column", flex: 1, gap: 6, padding: 12, overflow: "auto" },
              children:
                state.messages.length === 0
                  ? emptyThread(state)
                  : threadRows(state, chat ?? null),
            } as VNode,
            composer(state),
          ]),
      // B9 — the card a tapped contact bubble opened. Drawn last so it sits
      // over the thread; the SAME card a scan produces on the phones, with the
      // CURRENT person rather than the snapshot the bubble draws.
      ...contactCardOverlay(state),
      // ADR-0007 — the safety number, over the thread it is about.
      ...safetyNumberOverlay(state),
    ],
  };
}

/**
 * "Syncing your chats… 46%" — ADR-0027 D4.7.
 *
 * ## Why a browser needs telling at all
 *
 * A device that has just been linked can read everything said from the moment
 * it joined and NOTHING said before it, because the server holds ciphertext
 * addressed to devices this one is not and cannot re-encrypt what it cannot
 * read. Only a phone can catch it up, and doing so takes as long as it takes.
 * A person watching an empty chat list in the meantime has been told something
 * false; this is the correction, and it carries a real number rather than a
 * spinner, because the difference between "3%" and "80%" is the difference
 * between making tea and waiting.
 *
 * ## Never 100%
 *
 * The last frame ENDS the state rather than filling the bar (see
 * `setHistorySync`), so this never draws a finished sync — a bar at 100% is a
 * thing to look at, not a thing to say. It also never draws a STALLED one: the
 * shell takes the sentence down after a silence, because a bar frozen at 46%
 * for an hour is a worse lie than the empty list it replaced.
 */
function historySyncBar(state: ShellState): VNode[] {
  const sync = state.historySync;
  if (!sync || sync.total <= 0) return [];
  // Floored and capped below 100: a bundle is finished when the phone says so,
  // not when arithmetic rounds up.
  const percent = Math.min(99, Math.floor((sync.received / sync.total) * 100));
  return [
    {
      type: "stack",
      id: "history-sync",
      style: {
        direction: "row", align: "center", gap: 8,
        background: C.infoBg, padding: "8 14",
      },
      children: [
        {
          type: "text",
          id: "history-sync-text",
          props: { value: t("Syncing your chats… %s%", percent) },
          style: { font_size: 13, color: C.info, flex: 1 },
        },
      ],
    },
  ];
}

/** The one-line reason a send did not go, above the thread. Tapping it
 *  dismisses it — the message is already back in the composer. */
function sendErrorBar(text: string): VNode {
  return {
    type: "stack",
    id: "send-error",
    style: {
      direction: "row", align: "center", gap: 8,
      background: C.dangerBg, padding: "10 14",
    },
    action: act("send_error_clear"),
    children: [
      {
        type: "text",
        id: "send-error-text",
        props: { value: text },
        style: { font_size: 14, color: C.danger, flex: 1 },
      },
      {
        type: "text",
        id: "send-error-dismiss",
        props: { value: t("OK") },
        style: { font_size: 14, font_weight: 600, color: C.danger },
      },
    ],
  };
}

/**
 * B9 — the card, over the conversation.
 *
 * "Not on WaveTalk" covers three server answers that are deliberately the same
 * answer: nobody has that id, the account is not active, and (once ADR-0031 D3
 * lands) the account has chosen not to be found. Telling them apart is a
 * registration oracle, so the card does not.
 */
function contactCardOverlay(state: ShellState): VNode[] {
  const card = state.contactCard;
  if (!card) return [];

  const body: VNode[] = [];
  if (card.loading) {
    body.push({
      type: "text", id: "contact-card-loading",
      props: { value: t("Loading…") },
      style: { font_size: 14, color: C.textSecondary },
    });
  } else if (card.notFound || !card.user) {
    body.push({
      type: "text", id: "contact-card-notfound",
      props: { value: t("Not on WaveTalk") },
      style: { font_size: 15, color: C.textSecondary },
    });
  } else {
    const label = card.user.display_name ?? card.user.username ?? "—";
    body.push({
      type: "text", id: "contact-card-name",
      props: { value: label },
      style: { font_size: 18, font_weight: 700, color: C.textPrimary },
    });
    if (card.user.username) {
      body.push({
        type: "text", id: "contact-card-handle",
        props: { value: `@${card.user.username}` },
        style: { font_size: 13, color: C.textSecondary },
      });
    }
    // ADR-0030 D6 — the marker travels with the card. `ok` draws NOTHING, so
    // the card can never be used to learn that somebody IS active.
    // ADR-0030 D4: `ok` and absent both draw NOTHING. The asymmetry is the
    // design — the disclosure is one-directional, so no lookup can be used to
    // learn that somebody IS active.
    const marker = card.user.reachability === "quiet"
      ? t("Not used recently")
      : card.user.reachability === "unused"
      ? t("Never sent a message")
      : null;
    if (marker !== null) {
      body.push({
        type: "text", id: "contact-card-reach",
        props: { value: marker },
        style: { font_size: 12, color: C.danger },
      });
    }
    body.push(button("contact-card-message", t("Message"), act("contact_message", { user_id: card.user.id })));
  }

  body.push(button("contact-card-close", t("Cancel"), act("contact_close")));

  return [{
    type: "stack",
    id: "contact-card",
    style: {
      direction: "column", gap: 10, align: "center",
      padding: 20, margin: 16, radius: 14,
      background: C.surface, border: "1px solid outline.variant",
    },
    children: body,
  }];
}

/**
 * Render a 60-digit safety number as Signal's grid: twelve groups of five,
 * four groups to a line.
 *
 * Non-digits are stripped first, exactly as `formatSafetyNumber` does on
 * Android, so an engine- or shim-side format change garbles nothing. A number
 * that is not sixty digits still renders — in whatever groups it has — because
 * a screen that refused to draw an unexpected length would show nothing at all
 * and say nothing about why.
 */
export function formatSafetyNumber(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  const groups: string[] = [];
  for (let i = 0; i < digits.length; i += 5) groups.push(digits.slice(i, i + 5));
  const lines: string[] = [];
  for (let i = 0; i < groups.length; i += 4) lines.push(groups.slice(i, i + 4).join(" "));
  return lines.join("\n");
}

/**
 * The safety-number screen (ADR-0007) — the web's half of the phones'
 * `SafetyNumberScreen`.
 *
 * ## What it can say, and one thing it must
 *
 * A safety number is a hash of TWO DEVICE identity keys: this device's and one
 * of the peer's. Under ADR-0027 D4 a linked browser holds its own identity key
 * — the one a primary phone signed — so the number this screen shows is NOT
 * the number the same person's phone shows for the same contact. Somebody who
 * compared the two and found them different would conclude they were being
 * attacked, so the screen says which device it is speaking for, out loud,
 * rather than leaving it to be discovered.
 *
 * That is also why the peer's device NUMBER is named. The browser fingerprints
 * whichever of the peer's devices it has a session with, and "Amina's phone"
 * and "Amina's laptop" are two different numbers.
 *
 * ## What it deliberately does NOT have
 *
 * No "Mark as verified". That is a local trust record in the Signal store, and
 * the web engine exposes no op to write one — a button that recorded nothing
 * would be worse than its absence (A16). No "Reset secure session" either: the
 * browser does not send, so there is no held conversation for a reset to
 * release.
 *
 * ## Three states and none of them is an alarm
 *
 * Loading, a number, and no number. The last is ORDINARY — this browser has
 * not yet received an encrypted message from this person, so there is no
 * session and nothing to fingerprint — and it is the same sentence the phones
 * use. A failure to reach the key directory renders as that too, on purpose:
 * neither of them means somebody is in the middle, and a screen that shouted
 * about a dropped request would teach people to ignore the one that matters.
 */
function safetyNumberOverlay(state: ShellState): VNode[] {
  const v = state.safetyNumber;
  if (!v) return [];

  const body: VNode[] = [
    {
      type: "text", id: "verify-title",
      props: { value: t("Verify security") },
      style: { font_size: 18, font_weight: 700, color: C.textPrimary, text_align: "center" },
    },
    {
      type: "text", id: "verify-intro",
      props: { value: t("Compare this number with your contact's, in person or over a call you trust. If they match, your conversation is end-to-end encrypted and no one in the middle can read it.") },
      style: { font_size: 13, color: C.textSecondary, text_align: "center", line_height: 1.5 },
    },
  ];

  if (v.loading) {
    body.push({
      type: "text", id: "verify-loading",
      props: { value: t("Loading…") },
      style: { font_size: 14, color: C.textSecondary },
    });
  } else if (v.digits === null) {
    body.push({
      type: "text", id: "verify-none",
      props: { value: t("No safety number yet — it appears once you've exchanged an encrypted message with this contact.") },
      style: { font_size: 13.5, color: C.textSecondary, text_align: "center", line_height: 1.5 },
    });
  } else {
    body.push({
      type: "text", id: "verify-digits",
      props: { value: formatSafetyNumber(v.digits) },
      style: {
        font_family: "monospace", font_size: 20, line_height: 1.7, letter_spacing: "2px",
        color: C.textPrimary, text_align: "center", white_space: "pre",
        background: C.bgApp, radius: 12, padding: "16 18",
      },
    });
    // The sentence this screen exists to be honest about. Drawn only beside a
    // real number, because it is an explanation OF the number and reads as
    // noise without one.
    body.push({
      type: "text", id: "verify-device-note",
      props: { value: t("This number is for this browser and %s's device %s. Each device has its own key, so your phone shows a different number for the same person.", v.peerLabel, String(v.peerDevice ?? "")) },
      style: { font_size: 12, color: C.textSecondary, text_align: "center", line_height: 1.5 },
    });
  }

  body.push(button("verify-close", t("Close"), act("verify_close")));

  return [{
    type: "stack",
    id: "verify-sheet",
    style: {
      direction: "column", gap: 12, align: "center",
      padding: 22, margin: 16, radius: 14,
      background: C.surface, border: "1px solid outline.variant",
    },
    children: body,
  }];
}

/**
 * A group or E2E event in the timeline — centred, quiet and inert, the same
 * shape the phones draw.
 *
 * The words come from `metadata` and not from the server's English `body`; see
 * `system-notice.ts`. `state.me?.id` is what turns "Ann added Dee" into "You
 * added Dee" for Ann.
 */
function systemNoticeRow(msg: Message, state: ShellState): VNode {
  const key = msg.client_msg_id || msg.id || "tmp";
  return {
    type: "stack",
    id: `row-${key}`,
    style: { direction: "row", justify: "center", width: "100%" },
    children: [
      {
        type: "text",
        id: `sys-${key}`,
        props: { value: systemRowText(msg, state.me?.id ?? null, t) },
        style: {
          max_width: "82%",
          padding: "6 12",
          radius: 12,
          background: C.bubbleThem,
          color: C.textSecondary,
          font_size: 12,
          text_align: "center",
        },
      },
    ],
  };
}

/**
 * The thread, oldest first: a day line above the first message of each day,
 * and in a group the sender's name on the first bubble of a run — WhatsApp's
 * rule: never on your own, never in a direct chat (one other voice), and again
 * after a day line or a system row breaks the run. s174 parity with Android
 * (s173) and the two Apple surfaces.
 */
function threadRows(state: ShellState, chat: Chat | null): VNode[] {
  const viewerId = state.me?.id ?? null;
  // Anything that is not a direct chat — a group, a zone — has more than one
  // other voice. Unknown (no chat row yet) draws no name: "not loaded" is not
  // "a group".
  const manyVoices = chat !== null && chat.chat_type !== "direct";
  const out: VNode[] = [];
  state.messages.forEach((m, i) => {
    const key = m.client_msg_id || m.id || `i${i}`;
    const previous = i > 0 ? state.messages[i - 1] : null;
    const startsDay = previous === null || dayKey(previous.created_at) !== dayKey(m.created_at);
    if (startsDay) out.push(dayLine(`day-${key}`, dayLabel(m.created_at)));
    // ADR-0028 / D1: a system row is a statement about the ROOM, not
    // something somebody said. It drew as an ordinary bubble here until
    // s164 — left-aligned, with an author and a hover menu — which is also
    // why nobody noticed it was English-only.
    const system = (m.message_type ?? "").toLowerCase() === "system";
    if (system) { out.push(systemNoticeRow(m, state)); return; }
    const mine = viewerId !== null && m.user_id === viewerId;
    const previousSystem = previous !== null && (previous.message_type ?? "").toLowerCase() === "system";
    const startsRun = startsDay || previous === null || previous.user_id !== m.user_id || previousSystem;
    let author: string | null = null;
    if (manyVoices && !mine && startsRun) {
      const sender = (chat?.members ?? []).find((x) => x.user_id === m.user_id);
      // Past the rollup's bound of 8 the roster names them (s175, author-names.ts);
      // a member who left degrades to a neutral word rather than to a raw id —
      // the same rule as the phones.
      author = (sender ? memberName(sender.display_name, sender.username) : null)
        ?? (m.user_id ? state.rosterNames[m.user_id] : undefined)
        ?? t("Unknown");
    }
    out.push(messageBubble(m, state, author));
  });
  return out;
}

function messageBubble(msg: Message, state: ShellState, author: string | null = null): VNode {
  const mine = state.me?.id != null && msg.user_id === state.me.id;
  // Prefer client_msg_id so the optimistic echo and its persisted replacement
  // share ONE node identity — the bubble updates in place (opacity, server id)
  // instead of being torn down + rebuilt when the ack arrives.
  const key = msg.client_msg_id || msg.id || "tmp";
  // The same tint the person's avatar wears, so the name and the face agree.
  // The name and the body go into ONE column stack: a card lays its children
  // out side by side, and both a bare text node and a wrapped one drew
  // "Bob MwakalingaHabari" on a single line (s174 drive, twice).
  const content = bubbleContent(msg, key, mine);
  const body: VNode[] = author === null ? content : [{
    type: "stack",
    id: `author-${key}-row`,
    style: { direction: "column", gap: 2 },
    children: [{
      type: "text",
      id: `author-${key}`,
      props: { value: author },
      style: { font_size: 13, font_weight: 600, color: avatarTint(msg.user_id ?? "") },
    }, ...content],
  }];
  return {
    type: "stack",
    id: `row-${key}`,
    style: { direction: "row", justify: mine ? "flex-end" : "flex-start", width: "100%" },
    children: [
      {
        type: "card",
        id: `msg-${key}`,
        style: {
          max_width: "72%",
          padding: "8 12",
          radius: 14,
          background: mine ? C.bubbleMine : C.bubbleThem,
          color: mine ? C.bubbleMineText : C.textPrimary,
          opacity: msg.pending ? 0.65 : 1,
        },
        children: body,
      },
    ],
  };
}

/**
 * What a bubble DRAWS, dispatched on the message type.
 *
 * There was no type switch here at all until s148: every bubble drew
 * `msg.body`, so a sticker and a contact card — both of which carry an empty
 * body by design — rendered as an empty box. B9 needs one, and a sticker gets
 * its LABEL in the same pass; the drawing itself is out of scope and named as
 * such in the commit, because a sticker sheet on the web is a features's worth
 * of asset pipeline rather than a case in a switch.
 */
function bubbleContent(msg: Message, key: string, mine: boolean): VNode[] {
  const ink = mine ? C.bubbleMineText : C.textPrimary;
  const type = (msg.message_type ?? "").toLowerCase();

  if (type === "contact") {
    const card = (msg.metadata?.contact ?? null) as WireContactCard | null;
    const name = card?.display_name ?? null;
    const handle = card?.username ?? null;
    const userId = card?.user_id ?? null;
    // A card with no id names nobody and the server refuses to persist one, so
    // this branch means a row THIS build wrote wrong rather than a message to
    // render badly. It says so instead of drawing an empty bubble.
    if (userId === null || name === null) {
      return [{
        type: "text",
        id: `contact-empty-${key}`,
        props: { value: t("Contact") },
        style: { font_size: 15, color: ink, font_style: "italic" },
      }];
    }
    return [{
      type: "stack",
      id: `contact-${key}`,
      style: { direction: "column", gap: 2, cursor: "pointer" },
      // The tap opens the CARD for the person named, never a chat: the person
      // receiving this did not choose to be handed it, and one deliberate act
      // stands between a card arriving and a conversation starting.
      events: { click: act("open_contact", { user_id: userId }) },
      children: [
        {
          type: "text",
          id: `contact-name-${key}`,
          props: { value: `👤 ${name}` },
          style: { font_size: 15, color: ink, font_weight: 600 },
        },
        {
          type: "text",
          id: `contact-handle-${key}`,
          props: { value: handle ? `@${handle}` : "" },
          style: { font_size: 13, color: ink, opacity: 0.75 },
        },
      ],
    }];
  }

  if (type === "undecryptable") {
    // ADR-0027 D4.6. This browser holds the ciphertext's envelope and cannot
    // open it — a peer reinstalled, or a session was lost. The message is NOT
    // dropped: a bubble that says so can be reported and asked about, and
    // silence cannot. It is the one place the web client currently has to say
    // anything at all about encryption, which is a gap, not a design.
    return [{
      type: "text",
      id: `undecryptable-${key}`,
      props: { value: t("This message can't be read on this device") },
      style: { font_size: 15, color: ink, font_style: "italic" },
    }];
  }

  if (type === "sticker") {
    // The LABEL only. Drawing the art means shipping the pack to the browser,
    // which is a separate piece of work — but a bubble that says nothing at
    // all is worse than one that says what arrived.
    return [{
      type: "text",
      id: `sticker-${key}`,
      props: { value: t("Sticker") },
      style: { font_size: 15, color: ink, font_style: "italic" },
    }];
  }

  return [{
    type: "text",
    id: `body-${key}`,
    props: { value: msg.body ?? "" },
    style: { font_size: 15, color: ink },
  }];
}

/**
 * What an empty thread says.
 *
 * "Say hello 👋" claims there is nothing here. That is a lie in two different
 * situations, and each gets its own answer instead:
 *
 *   * a phone is still re-sending this conversation — the sync bar says so,
 *     and the greeting would be contradicting it;
 *   * the chat is end-to-end encrypted and this browser is not a device, so
 *     there may be a decade of messages in it and none of them can be opened
 *     here. The note above the composer explains that, so this says NOTHING
 *     rather than inviting somebody to start a conversation that already
 *     exists.
 */
function emptyThread(state: ShellState): VNode[] {
  if (state.historySync) return [emptyState("no-msgs", t("Syncing your chats…"))];
  if (state.activeChatE2E === true && state.signalDeviceLinked !== true) return [];
  return [emptyState("no-msgs", t("Say hello 👋"))];
}

/**
 * What the header says about encryption, under the chat's name (ADR-0007).
 *
 * TWO of the three answers are now silence, and the removed one is the
 * reassurance. ADR-0033 D1a: a sealed chat says so ONCE, at the dated
 * `e2e_enabled` line in the thread, and every surface stopped repeating it —
 * the phones dropped a permanent card and a list padlock, and this dropped
 * "🔒 End-to-end encrypted" from under every sealed chat's name. A sentence
 * that will shortly be true of every conversation distinguishes none of them,
 * and it was crowding out the subtitle's other job.
 *
 * `null` still means the server has not said yet, and it still draws nothing:
 * a screen that guessed "not encrypted" while a request was in flight would be
 * telling people their messages are readable — briefly, confidently, and for
 * some of them falsely. Typing still wins the subtitle while it lasts.
 *
 * What survives is the honest negative, in the phones' own words
 * (`e2e_messages_in_this_chat_arent_end`), because a person who reads one
 * sentence on their phone and a different one here has to work out whether
 * they mean the same thing.
 */
function encryptionSubtitle(state: ShellState): string | null {
  if (state.activeChatE2E !== false) return null;
  return t("Messages in this chat aren't end-to-end encrypted.");
}

/**
 * The line above the composer in an encrypted chat: what THIS browser can do
 * with it.
 *
 * Only for an encrypted chat, and only about this device. A linked browser
 * reads the conversation and cannot send into it — the send path is the
 * phone's, and that is a real limitation a person hits mid-sentence, so it is
 * said before they type rather than after the message bounces. A browser that
 * is not a device cannot even read it, and the empty thread it is looking at
 * needs the explanation more than anything else on the screen.
 *
 * It says nothing about a plaintext chat: the header already does, and a
 * second sentence saying the same thing louder is how a surface teaches people
 * to stop reading it.
 *
 * ADR-0033 D1a removed the phones' "End-to-end encrypted" card and this bar
 * DELIBERATELY STAYS, because it is not the same sentence. That card told
 * somebody a fact about the conversation they already knew; this tells them a
 * limitation of the thing they are typing into, which they are about to hit.
 * It is also the only home the web has for Verify — there is no contact-info
 * panel here to move it to, which is where the phones put theirs.
 */
function encryptionBar(state: ShellState): VNode[] {
  if (state.activeChatE2E !== true) return [];
  const linked = state.signalDeviceLinked === true;
  const children: VNode[] = [{
    type: "text",
    id: "conv-e2e-note",
    props: {
      value: linked
        ? t("You can read this chat here, but sending is only on your phone for now.")
        : t("This browser is not a linked device, so it cannot read this chat. Open WaveTalk on your phone → Settings → Scan to log in."),
    },
    style: { font_size: 12.5, color: C.textSecondary, text_align: "center", flex: 1 },
  }];
  // ADR-0007 — the lock's second door, and the banner is where the phones put
  // it too (`EncryptionBanner(onVerify = …)`).
  //
  // Three conditions and all three are A16: the chat must BE encrypted (an
  // offer to verify a plaintext conversation claims something false about
  // it), this browser must be a device (a browser with no identity key of its
  // own has nothing to fingerprint WITH, so the screen could only ever say
  // "nothing to compare"), and the chat must be a 1:1 — a fingerprint is
  // between two identities and a group has no second one.
  const chat = chatById(state, state.activeChatId);
  const oneToOne = (chat?.members ?? []).length === 1;
  if (linked && oneToOne) {
    children.push({
      type: "button",
      id: "conv-e2e-verify",
      props: { label: t("Verify security") },
      style: {
        background: C.accentWash, color: C.brandEnd, border: "none",
        font_size: 12.5, font_weight: 600, padding: "5 11", radius: 999,
        flex_shrink: 0,
      },
      action: act("verify_open"),
    });
  }
  return [{
    type: "stack",
    id: "conv-e2e-bar",
    style: {
      direction: "row", align: "center", gap: 10,
      padding: "8 16", background: C.infoBg, flex_shrink: 0,
    },
    children,
  }];
}

function composer(state: ShellState): VNode {
  const canSend = state.draft.trim().length > 0;
  return {
    type: "stack",
    id: "composer",
    style: {
      direction: "row",
      align: "center",
      gap: 8,
      padding: 10,
      background: C.composerSurface,
      border: "1px solid outline.variant",
    },
    children: [
      {
        type: "input",
        id: "composer-input",
        props: { placeholder: t("Message"), value: state.draft },
        style: { flex: 1, padding: 11, radius: 20, border: "1px solid outline.variant", font_size: 16, background: C.composerField },
        events: { input: act("set_draft") },
      },
      {
        type: "button",
        id: "composer-send",
        props: { label: t("Send"), disabled: !canSend },
        style: {
          padding: "11 16",
          radius: 20,
          background: canSend ? C.brandEnd : C.bgApp,
          color: canSend ? C.onAccent : C.textSecondary,
          font_weight: 600,
          border: "none",
        },
        action: act("send"),
      },
    ],
  };
}

// --- mini-app picker -------------------------------------------------------

function appsView(state: ShellState): VNode {
  const searching = state.appsQuery.trim() !== "";
  let body: VNode[];
  if (state.appsLoading) {
    body = [emptyState("apps-loading", t("Loading mini-apps…"))];
  } else if (state.appsError) {
    body = [emptyState("apps-error", state.appsError)];
  } else if (state.apps.length === 0) {
    body = [emptyState("apps-empty", emptyMessage(state, searching))];
  } else {
    body = state.apps.map(appRow);
  }
  return {
    type: "stack",
    id: "apps-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("apps-header", t("Mini-apps"), state, {}),
      {
        type: "input",
        id: "apps-search",
        props: { placeholder: t("Search mini-apps"), value: state.appsQuery, input_type: "search" },
        style: { margin: "10 12 8 12", padding: "10 14", radius: 10, border: "1px solid outline.variant", font_size: 15, background: C.surface },
        events: { input: act("apps_search") },
      },
      // Tabs hide while searching (results span the whole registry, not a tab).
      ...(searching ? [] : [appsTabs(state)]),
      {
        type: "scroll-view",
        id: "apps-list",
        style: { direction: "column", flex: 1, padding: "8 0", overflow: "auto" },
        children: body,
      },
    ],
  };
}

function appsTabs(state: ShellState): VNode {
  const tab = (key: "featured" | "installed", label: string): VNode => {
    const active = state.appsTab === key;
    return {
      type: "button",
      id: `apps-tab-${key}`,
      props: { label },
      style: {
        flex: 1,
        padding: "9 0",
        font_size: 14,
        font_weight: active ? 700 : 600,
        color: active ? C.brandEnd : C.textSecondary,
        background: "transparent",
        border: "none",
        border_bottom: active ? "2px solid accent" : "2px solid transparent",
      },
      action: act("apps_tab", { tab: key }),
    };
  };
  return {
    type: "stack",
    id: "apps-tabs",
    style: { direction: "row", background: C.surface, margin: "0 0 1 0" },
    children: [tab("featured", t("Featured")), tab("installed", t("Installed"))],
  };
}

function emptyMessage(state: ShellState, searching: boolean): string {
  if (searching) return `No apps match “${state.appsQuery.trim()}”`;
  return state.appsTab === "installed" ? t("No installed apps yet") : t("No mini-apps available yet");
}

function appRow(item: MiniAppListItem): VNode {
  return {
    type: "card",
    id: `app-row-${item.id}`,
    style: { direction: "row", align: "center", gap: 12, padding: "12 16", background: C.surface, margin: "0 0 1 0" },
    // A built-in app resolves locally ("sample"); a registry app is fetched by id.
    action: act("open_miniapp", { app_id: item.builtin ? "sample" : item.id, title: item.name }),
    children: [
      appIcon(`app-icon-${item.id}`, item),
      {
        type: "stack",
        id: `app-meta-${item.id}`,
        style: { direction: "column", flex: 1, gap: 2 },
        children: [
          { type: "text", id: `app-name-${item.id}`, props: { value: item.name }, style: { font_size: 16, font_weight: 600, color: C.textPrimary } },
          { type: "text", id: `app-desc-${item.id}`, props: { value: item.description || "Mini-app" }, style: { font_size: 14, color: C.textSecondary } },
        ],
      },
      ...(item.builtin
        ? [{ type: "text" as const, id: `app-badge-${item.id}`, props: { value: t("Built-in") }, style: { font_size: 11, font_weight: 700, color: C.brandEnd, background: C.dangerBg, radius: 6, padding: "2 8" } }]
        : []),
    ],
  };
}

function appIcon(id: string, item: MiniAppListItem): VNode {
  if (item.icon_url) {
    return {
      type: "image",
      id,
      props: { url: item.icon_url, alt: item.name },
      style: { width: 44, height: 44, radius: 10 },
    };
  }
  return avatar(id, item.name);
}

// --- shared chrome ---------------------------------------------------------

/**
 * A column header.
 *
 * ## It used to carry five controls, and that was the whole problem
 *
 * Chats' header held navigation to Updates, navigation to Mini-apps, the
 * profile avatar, the connection state and Sign out — because there was
 * nowhere else on the surface to put any of them. On an 800px window the last
 * one was already clipped. The rail is the somewhere else, and the canvas's
 * second admission rule is that this header is FULL: a title, one status pill
 * and one primary action, and nothing else (docs/design/canvas/Rail.dc.html).
 *
 * So `logout`, `apps`, `updates` and `profile` are gone from here — not moved
 * to another corner of the header, gone. Sign out lives on Profile, which is
 * where a person looks for it, and the other three are rail slots.
 *
 * ## It is no longer a brand bar
 *
 * The orange gradient was reasonable when a screen WAS the app: it was the
 * app's one chrome. Beside a list column and a detail pane it becomes two
 * orange bars side by side, which is a lot of brand for a surface whose job is
 * to disappear behind a conversation. Surface, a hairline, and the title in
 * the text colour — as the artboards draw it.
 */
function header(
  id: string,
  title: string,
  state: ShellState,
  opts: {
    back?: boolean;
    /** The conversation's back: meaningful only when the list is hidden. In a
     *  two-pane window the list is right there and "back" points at nothing. */
    backWhenNarrowOnly?: boolean;
    subtitle?: string | null;
    search?: boolean;
    /** The connection pill. The LIST column's, per the canvas — a detail pane
     *  showing it too would say the same thing twice on one screen. */
    pill?: boolean;
  } = {},
): VNode {
  const children: VNode[] = [];
  const narrow = layoutFor(state.viewportWidth) === "narrow";
  if (opts.back && (!opts.backWhenNarrowOnly || narrow)) {
    children.push({
      type: "button",
      id: `${id}-back`,
      props: { label: t("‹ Back") },
      style: { background: "transparent", color: C.brandEnd, border: "none", font_size: 15, font_weight: 600, padding: "0 4" },
      action: act("back"),
    });
  }
  children.push({
    type: "stack",
    id: `${id}-titles`,
    style: { direction: "column", flex: 1, gap: 1, min_width: 0 },
    children: [
      {
        type: "text", id: `${id}-title`, props: { value: title },
        style: {
          font_size: 20, font_weight: 700, color: C.textPrimary,
          letter_spacing: "-0.3px", white_space: "nowrap", overflow: "hidden", text_overflow: "ellipsis",
        },
      },
      ...(opts.subtitle
        ? [{ type: "text" as const, id: `${id}-sub`, props: { value: opts.subtitle }, style: { font_size: 13, color: C.textSecondary } }]
        : []),
    ],
  });
  if (opts.pill) children.push(connectionPill(`${id}-conn`, state.connection));
  if (opts.search) {
    // A16's first control, and it exists only now that it has somewhere to go:
    // there was no channel-search endpoint when this screen was built, and a
    // magnifying glass that opens nothing teaches people the surface is
    // broken.
    children.push({
      type: "button",
      id: `${id}-search`,
      props: { label: t("Search") },
      style: { background: C.accentWash, color: C.brandEnd, border: "none", font_size: 13, font_weight: 600, padding: "6 12", radius: 999 },
      action: act("explore_open", { focus: true }),
    });
  }
  return {
    type: "stack",
    id,
    style: {
      direction: "row",
      align: "center",
      gap: 10,
      padding: "14 16",
      background: C.surface,
      border_bottom: "1px solid outline.variant",
      flex_shrink: 0,
    },
    children,
  };
}

/**
 * "● Online" — the connection, in a word.
 *
 * It was a bare coloured dot, which says nothing to anybody who has not been
 * told what the colours mean, and the one state that matters (you are offline
 * and your messages are not going anywhere) looked exactly like the one that
 * does not.
 */
function connectionPill(id: string, connection: ShellState["connection"]): VNode {
  const [colour, label] =
    connection === "online" ? [C.success, t("Online")]
    : connection === "connecting" ? [C.warning, t("Connecting…")]
    : [C.danger, t("No connection")];
  return {
    type: "stack",
    id,
    style: {
      direction: "row", align: "center", gap: 6,
      padding: "4 10 4 8", radius: 999, background: C.bgApp, flex_shrink: 0,
    },
    children: [
      { type: "text", id: `${id}-dot`, props: { value: "●" }, style: { font_size: 9, color: colour, line_height: 1 } },
      { type: "text", id: `${id}-label`, props: { value: label }, style: { font_size: 12, font_weight: 600, color: C.textSecondary, white_space: "nowrap" } },
    ],
  };
}

/** Fallback name for a conversation with nothing to go on. */
const EMPTY_CHAT_TITLE = "New conversation";

/** How many counterparts an unnamed group names before the "+N" remainder. */
const GROUP_NAME_PREVIEW = 2;

/**
 * What to show as a conversation's name.
 *
 * A direct chat has no `name` — nobody names a one-to-one thread — so its name
 * is the other person, resolved from the counterparts the chat-list payload
 * carries. Mirrors the shared KMP `Chat.displayTitle` so the web surface and
 * the native clients agree on what a conversation is called; the native side
 * used to fall back to the chat id, which put a raw UUID on a tester's screen.
 */
export function chatTitle(chat: Chat): string {
  if (chat.name && chat.name.trim() !== "") return chat.name;

  const names = (chat.members ?? [])
    .map((m) => memberName(m.display_name, m.username))
    .filter((n): n is string => n !== null);

  if (names.length === 0) return EMPTY_CHAT_TITLE;
  if (names.length === 1) return names[0];

  const shown = names.slice(0, GROUP_NAME_PREVIEW);
  // Counted against the TRUE member count, not the bounded rollup, so a big
  // group reports the real remainder.
  const others = Math.max(0, (chat.member_count ?? names.length + 1) - 1 - shown.length);
  return others === 0 ? shown.join(", ") : `${shown.join(", ")} +${others}`;
}

/**
 * What a chat-list row says about its newest message.
 *
 * The TypeScript twin of the shared module's `Chat.preview` (Models.kt) —
 * Joana does not consume KMP, so the rule is stated twice and the two must be
 * changed together. It exists because `last_message.body` is empty for every
 * media message, so this row used to read "No messages yet" over a
 * conversation whose newest message was a photo or a voice note.
 *
 * The tick is a text glyph rather than an icon: the shell's component set has
 * no icon primitive, and "✓✓" is the same grammar the native clients draw.
 */
export function chatPreview(chat: Chat, viewerId: string | null): string {
  const m = chat.last_message;
  if (!m) return t("No messages yet");
  if (m.deleted_at) return t("This message was deleted");

  // ADR-0028: a system row's subtitle is a SENTENCE, and until s164 the list
  // drew the server's English one from `body` while the thread said the same
  // thing in the reader's language. Found by opening the app in Kiswahili, not
  // by a gate. Falls through for an event this build does not know, exactly as
  // the thread does.
  const notice = parseNoticeMeta(m.system);
  if (notice !== null) {
    const words = systemNoticeText(notice, viewerId, t);
    if (words !== null) return words;
  }

  const body = m.body && m.body.trim() !== "" ? m.body : null;
  const type = (m.message_type ?? "").toLowerCase();
  const system = type === "system";

  let text: string;
  switch (type) {
    case "image":
      text = `📷 ${body ?? t("Photo")}`;
      break;
    case "video":
      text = `🎥 ${body ?? t("Video")}`;
      break;
    case "audio":
      text = m.voice
        ? `🎤 ${body ?? t("Voice note")}${m.duration_ms ? ` · ${formatDuration(m.duration_ms)}` : ""}`
        : `🎵 ${body ?? t("Audio")}`;
      break;
    case "file":
      text = `📄 ${body ?? t("File")}`;
      break;
    // B9: the NAME, because a row reading only "Contact" tells you nothing you
    // did not already know from the emoji.
    case "contact":
      text = `👤 ${m.contact?.display_name ?? body ?? t("Contact")}`;
      break;
    case "sticker":
      text = t("Sticker");
      break;
    // ADR-0027 D4.6 — an envelope this browser could not open has no body, and
    // the default below would turn it into "No messages yet". There IS a
    // message; the row has to say so, or the chat looks untouched while an
    // unread one sits in it.
    case "undecryptable":
      text = t("This message can't be read on this device");
      break;
    default:
      // ADR-0034 D1: a sealed message this browser has not decrypted. Below
      // the shape branches on purpose — a sealed PHOTO still previews as a
      // photo, because the stub names the shape and the shape is not a secret.
      // Above the "No messages yet" fallback, because that sentence over a
      // conversation somebody just wrote in is the exact lie the stub exists
      // to stop telling.
      if (m.e2e === true && body === null) {
        text = t("Message");
        break;
      }
      if (body === null) return t("No messages yet");
      text = body;
  }

  const mine = viewerId !== null && m.user_id === viewerId;
  // A group event is already a whole sentence about who did what; a tick and a
  // name prefix would both say it a second time.
  if (mine && !system) {
    const tick = deliveryTick(m.status);
    return tick === "" ? text : `${tick} ${text}`;
  }
  if (chat.chat_type === "group" && !system) {
    const sender = (chat.members ?? []).find((x) => x.user_id === m.user_id);
    const name = sender ? memberName(sender.display_name, sender.username) : null;
    // Not when the row's TITLE is already that person's name: an unnamed
    // two-person group would otherwise say the same name twice, on two lines.
    if (name !== null && name !== chatTitle(chat)) return `${name}: ${text}`;
  }
  return text;
}

function deliveryTick(status: string | null | undefined): string {
  switch ((status ?? "").toLowerCase()) {
    case "queued":
    case "pending":
      return "🕘";
    case "sent":
      return "✓";
    case "delivered":
    case "read":
      return "✓✓";
    default:
      return "";
  }
}

/** MM:SS for a voice note's length. */
function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}


/** Installed by the shell: the blob: URL for (kind, owner, version, size),
 *  or null while it loads / when the server said no. Pure views cannot own a
 *  fetcher; `state.avatarTick` is bumped when a blob lands so the tree
 *  rebuilds and this answers differently. */
export let avatarBlob: (
  kind: "user" | "chat", ownerId: string, version: string, rendition: "thumb" | "full",
) => string | null = () => null;

export function installAvatarSource(fn: typeof avatarBlob): void {
  avatarBlob = fn;
}

/** The same arrangement for status media (D8): a pure view cannot own a
 *  fetcher, so it asks, and `state.statusTick` is bumped when a blob lands so
 *  the tree rebuilds and this answers differently. */
export let statusBlob: (
  statusId: string, rendition: "thumb" | "full" | "poster" | "video" | "voice",
) => string | null = () => null;

export function installStatusSource(fn: typeof statusBlob): void {
  statusBlob = fn;
}

/** The identity mark (ADR-0022): photo → sticker → emoji → initials, one
 *  chain everywhere, with the frame (E15) composed over whichever shows.
 *  The tint arithmetic is byte-identical to Avatar.kt / UIHelpers.swift. */
function avatar(id: string, title: string, opts: {
  ownerId?: string;
  av?: WireAvatar | null;
  frame?: string | null;
  chatIconOf?: string | null;
  size?: number;
  /** An accent ring around the face — the rail's zone-3 active state. The
   *  canvas is explicit that the avatar cannot wear the raised tile the other
   *  slots use, because a tile behind a round face reads as a second avatar. */
  ring?: boolean;
} = {}): VNode {
  const size = opts.size ?? 44;
  const children: VNode[] = [];
  const av = opts.av ?? null;
  let face: VNode | null = null;

  if (av?.kind === "photo" && av.version) {
    const owner = opts.chatIconOf ?? opts.ownerId;
    if (owner) {
      const blob = avatarBlob(opts.chatIconOf ? "chat" : "user", owner, av.version, size > 96 ? "full" : "thumb");
      if (blob) {
        face = {
          type: "image", id: `${id}-photo`,
          props: { url: blob, alt: "" },
          style: { width: size, height: size, radius: 999, object_fit: "cover" },
        };
      }
    }
  } else if (av?.kind === "sticker" && av.ref) {
    face = {
      type: "image", id: `${id}-sticker`,
      props: { url: `/stickers-art/${av.ref}.webp`, alt: "" },
      style: { width: size, height: size, radius: 999, background: withAlpha(avatarTint(opts.ownerId ?? id), 0.22), object_fit: "contain", padding: Math.round(size * 0.09) },
    };
  } else if (av?.kind === "emoji" && av.value) {
    face = {
      type: "text", id: `${id}-emoji`,
      props: { value: av.value },
      style: { width: size, height: size, radius: 999, background: withAlpha(avatarTint(opts.ownerId ?? id), 0.22), font_size: Math.round(size * 0.5), text_align: "center", line_height: `${size}px` },
    };
  }

  if (!face) {
    const initial = (title.trim().replace(/^@+/, "")[0] || "?").toUpperCase();
    face = {
      type: "text", id: `${id}-init`,
      props: { value: initial },
      style: {
        width: size, height: size, radius: 999,
        background: avatarTint(opts.chatIconOf ?? opts.ownerId ?? title),
        color: C.onTint, font_size: Math.round(size * 0.4), font_weight: 600,
        text_align: "center", line_height: `${size}px`, flex_shrink: 0,
      },
    };
  }
  children.push(face);

  if (opts.frame) {
    children.push({
      type: "image", id: `${id}-frame`,
      props: { url: `/avatar-frames/${opts.frame}.png`, alt: "" },
      style: { position: "absolute", left: Math.round(-size * 0.06), top: Math.round(-size * 0.06), width: Math.round(size * 1.12), height: Math.round(size * 1.12) },
    });
  }

  const face_: VNode = {
    type: "stack", id,
    style: { position: "relative", width: size, height: size, flex_shrink: 0 },
    children,
  };
  if (!opts.ring) return face_;
  return {
    type: "stack", id: `${id}-ring`,
    style: { padding: 2, radius: 999, border: "2px solid accent", flex_shrink: 0 },
    children: [face_],
  };
}

/** The Kotlin/Swift tint sum, ported byte for byte: acc = acc*31 + utf16, masked. */
const AVATAR_TINTS = ["#E2603C", "#D8456A", "#B4562F", "#8E5A9E", "#3F7A6B", "#4A6FA5", "#C1802B", "#7A5C8E"];
function avatarTint(seed: string): string {
  if (!seed) return AVATAR_TINTS[0];
  let acc = 0;
  for (let i = 0; i < seed.length; i++) acc = ((Math.imul(acc, 31) + seed.charCodeAt(i)) & 0x7fffffff);
  return AVATAR_TINTS[acc % AVATAR_TINTS.length];
}

function withAlpha(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

function emptyState(id: string, label: string): VNode {
  return {
    type: "text",
    id,
    props: { value: label },
    style: { font_size: 15, color: C.textSecondary, text_align: "center", padding: 32 },
  };
}


/**
 * The language rows (ADR-0028).
 *
 * "Follow my browser" is first and is a REAL choice — picking it clears the
 * setting rather than storing a name for today's browser locale, so a browser
 * later switched to Kiswahili takes WaveTalk with it. Its subtitle names the
 * language that currently means, because "follow my browser" tells a reader
 * nothing about what they are about to see.
 *
 * The list is `SHIPPING_LANGUAGES` and never `LANGUAGES` — offering a
 * half-translated locale is the exact failure ADR-0028 D4 exists to prevent,
 * and the server refuses to store one whatever this draws. Planned languages
 * get one closing line, because "is my language on the list" deserves an
 * answer even when the answer is not yet.
 */
function languageRows(state: ShellState): VNode[] {
  const chosen = state.uiLanguage;
  const resolved = resolveLanguage(chosen, browserTags());
  const resolvedName = LANGUAGES.find((l) => l.tag === resolved)?.endonym ?? resolved;
  const planned = LANGUAGES.filter((l) => !l.shipping);

  // This screen is the first thing on the web to draw itself through the
  // catalogue rather than from a literal — which is the point of having built
  // the layer, and means the language picker is translated before anything it
  // switches to exists.
  const t = translator(resolved);

  const rows: VNode[] = [
    languageRow(
      "prof-lang-system",
      t("Follow my browser"),
      t("Right now that is %s", resolvedName),
      chosen === null,
      {},
    ),
    ...SHIPPING_LANGUAGES.map((l) =>
      // The language, in the language, and NOT through the catalogue: an
      // endonym is already in the language it names, so translating it would
      // ask us for the Kiswahili word for "Kiswahili".
      languageRow(`prof-lang-${l.tag}`, l.endonym, null, chosen === l.tag, { tag: l.tag }),
    ),
  ];
  if (planned.length > 0) {
    rows.push({
      type: "text", id: "prof-lang-planned",
      props: {
        value: `${t("Coming: %s", planned.map((l) => l.endonym).join(", "))} ${t("A language arrives complete or not at all.")}`,
      },
      style: { font_size: 12, color: C.textSecondary, padding: "8 16 12" },
    });
  }
  return rows;
}

/**
 * Light, dark, or follow the machine.
 *
 * "Follow my device" is first and is a REAL choice, exactly as "Follow my
 * browser" is in the language list: picking it CLEARS the stored value rather
 * than writing today's answer down, so a machine later switched to dark takes
 * WaveTalk with it. Its subtitle names what that currently means, because
 * "follow my device" tells a reader nothing about what they are about to see.
 *
 * The choice is per BROWSER, which the closing line says out loud — a person
 * who set dark on their phone and finds the desktop light deserves to know
 * that is the design and not a bug.
 */
function appearanceRows(state: ShellState): VNode[] {
  const now = state.appearance;
  const rows: VNode[] = [
    appearanceRow("appearance-system", t("Follow my device"), t("Right now that is %s", systemIsDark() ? t("Dark") : t("Light")), now === "system", "system"),
    appearanceRow("appearance-light", t("Light"), null, now === "light", "light"),
    appearanceRow("appearance-dark", t("Dark"), null, now === "dark", "dark"),
  ];
  rows.push({
    type: "text", id: "appearance-note",
    props: { value: t("This is remembered in this browser, not on your account — a bright desk and a dark phone want different answers.") },
    style: { font_size: 12, color: C.textSecondary, padding: "8 16 14 16" },
  });
  return rows;
}

/**
 * G16 — what the conversation is drawn on.
 *
 * Beside Appearance because the canvas draws them together and because they
 * are one question asked twice: how should this look. Same row shape as the
 * appearance list, same tick, same closing line about where the choice lives —
 * a second control that behaved differently would be a second thing to learn.
 *
 * The swatch is the row's whole argument. Seven names mean nothing on their
 * own, and the light and dark values of one wallpaper are different colours,
 * so the swatch draws the SAME custom-property trick the canvas does: a
 * per-wallpaper `var(…)` published for both appearances, so the picker shows
 * what you will actually get in the appearance you are in.
 *
 * The web draws no doodle (see `wallpaper.ts`), which is why the closing line
 * does not promise a pattern and why the dark options read as tints.
 */
/**
 * A wallpaper's name, in the drawn language.
 *
 * A switch of LITERAL `t("…")` calls rather than `t(w.label)`, and the reason
 * is `scripts/i18n-check`: rule 8 reads source for `t("…")` and a `t(variable)`
 * is invisible to it, so a wallpaper added with an untranslated name would
 * have shipped English behind a green ratchet — the same trap the rail's
 * labels are written out longhand to avoid.
 *
 * `check-wallpaper-mirror.mjs` asserts every catalogue label has a literal
 * `t("…")` here, so an entry added to the shared module and forgotten in this
 * switch fails a gate rather than falling back to English.
 */
function wallpaperLabel(w: { id: string; label: string }): string {
  switch (w.id) {
    case "default": return t("WaveTalk");
    case "plain": return t("Plain");
    case "sand": return t("Sand");
    case "teal": return t("Teal");
    case "indigo": return t("Indigo");
    case "rose": return t("Rose");
    case "slate": return t("Slate");
    // A wallpaper this switch has not been told about. The catalogue's own
    // English is a better answer than an empty row, and the mirror gate is
    // what makes sure nobody has to rely on it.
    default: return w.label;
  }
}

function wallpaperRows(state: ShellState): VNode[] {
  const rows: VNode[] = WALLPAPERS.map((w) => {
    const id = `wallpaper-${w.id}`;
    const selected = state.wallpaper.id === w.id;
    return {
      type: "stack" as const, id,
      style: { direction: "row", align: "center", gap: 12, padding: "12 16", border_bottom: "1px solid outline.variant" },
      action: act("wallpaper_set", { id: w.id }),
      children: [
        {
          type: "stack" as const, id: `${id}-swatch`, style: {
            width: 34, height: 34, radius: 9,
            background: `var(--wt-wallpaper-${w.id})`,
            border: "1px solid outline.variant", flex_shrink: 0,
          },
          children: [],
        },
        { type: "text" as const, id: `${id}-label`, props: { value: wallpaperLabel(w) }, style: { font_size: 15, color: C.textPrimary, flex: 1 } },
        ...(selected
          ? [{ type: "text" as const, id: `${id}-tick`, props: { value: "✓" }, style: { font_size: 15, color: C.brandEnd } }]
          : []),
      ],
    };
  });
  rows.push({
    type: "text", id: "wallpaper-note",
    props: { value: t("This is remembered in this browser, not on your account — a bright desk and a dark phone want different answers.") },
    style: { font_size: 12, color: C.textSecondary, padding: "8 16 14 16" },
  });
  return rows;
}

function appearanceRow(id: string, label: string, subtitle: string | null, selected: boolean, choice: string): VNode {
  return {
    type: "stack", id,
    style: { direction: "row", align: "center", gap: 10, padding: "12 16", border_bottom: "1px solid outline.variant" },
    action: act("appearance_set", { choice }),
    children: [
      {
        type: "stack", id: `${id}-t`, style: { direction: "column", flex: 1, gap: 2 },
        children: [
          { type: "text", id: `${id}-label`, props: { value: label }, style: { font_size: 15, color: C.textPrimary } },
          ...(subtitle
            ? [{ type: "text" as const, id: `${id}-sub`, props: { value: subtitle }, style: { font_size: 12, color: C.textSecondary } }]
            : []),
        ],
      },
      ...(selected
        ? [{ type: "text" as const, id: `${id}-tick`, props: { value: "✓" }, style: { font_size: 15, color: C.brandEnd } }]
        : []),
    ],
  };
}

/**
 * What the machine currently prefers.
 *
 * Guarded rather than assumed: `matchMedia` is missing in the Node test runner
 * and can throw in a locked-down browser, and a settings row that crashes the
 * render is a worse outcome than a subtitle that says Light.
 */
function systemIsDark(): boolean {
  try {
    const g = globalThis as { matchMedia?: (q: string) => { matches: boolean } };
    if (!g.matchMedia) return false;
    return g.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

function languageRow(id: string, label: string, subtitle: string | null, selected: boolean, extra: Record<string, unknown>): VNode {
  return {
    type: "stack", id,
    style: { direction: "row", align: "center", gap: 10, padding: "12 16", border_bottom: "1px solid outline.variant" },
    action: act("language_set", extra),
    children: [
      {
        type: "stack", id: `${id}-t`, style: { direction: "column", flex: 1, gap: 2 },
        children: [
          { type: "text", id: `${id}-label`, props: { value: label }, style: { font_size: 15, color: C.textPrimary } },
          ...(subtitle
            ? [{ type: "text" as const, id: `${id}-sub`, props: { value: subtitle }, style: { font_size: 12, color: C.textSecondary } }]
            : []),
        ],
      },
      ...(selected
        ? [{ type: "text" as const, id: `${id}-tick`, props: { value: "✓" }, style: { font_size: 15, color: C.brandEnd } }]
        : []),
    ],
  };
}

// --- profile (ADR-0022) ------------------------------------------------------

function profileView(state: ShellState): VNode {
  const me = state.profile ?? state.me;
  const name = me?.display_name ?? me?.username ?? "—";
  const privacy = state.privacy;
  const rows: VNode[] = [
    {
      type: "stack", id: "prof-id",
      style: { direction: "row", align: "center", gap: 14, padding: 16, background: C.surface },
      children: [
        avatar("prof-face", name, { ownerId: me?.id, av: me?.avatar ?? null, frame: me?.frame ?? null, size: 64 }),
        {
          type: "stack", id: "prof-names", style: { direction: "column", flex: 1, gap: 2 },
          children: [
            { type: "text", id: "prof-name", props: { value: name }, style: { font_size: 18, font_weight: 700, color: C.textPrimary } },
            ...(me?.username ? [{ type: "text" as const, id: "prof-handle", props: { value: `@${me.username}` }, style: { font_size: 13, color: C.textSecondary } }] : []),
          ],
        },
        button("prof-rename", t("Rename"), act("profile_rename")),
      ],
    },
    // E5 — my own contact code. Above the photo rows because it is how a
    // person is FOUND, which is the more consequential thing on this screen.
    sectionLabel("prof-qr-label", t("My QR code")),
    {
      type: "stack", id: "prof-qr", style: { direction: "column", background: C.surface, padding: 16, gap: 8, align: "center" },
      children: [
        ...(state.contactQr?.svg
          ? [{
              type: "image" as const,
              id: "contact-qr-image",
              props: {
                url: `data:image/svg+xml;utf8,${encodeURIComponent(state.contactQr.svg)}`,
                alt: t("My QR code"),
              },
              style: { width: 200, height: 200, margin: "0 auto" },
            }]
          : []),
        {
          type: "text" as const,
          id: "contact-qr-hint",
          props: { value: t("Let somebody scan this to message you.") },
          style: { font_size: 13, color: C.textSecondary, text_align: "center" },
        },
      ],
    },
    sectionLabel("prof-photo-label", t("Profile photo")),
    {
      type: "stack", id: "prof-actions", style: { direction: "column", background: C.surface },
      children: [
        actionRow("prof-change", t("Choose photo…"), act("avatar_change_photo")),
        actionRow("prof-emoji", t("Use an emoji…"), act("avatar_pick_emoji")),
        actionRow("prof-sticker", t("Use a sticker…"), act("avatar_pick_sticker")),
        actionRow("prof-frame", t("Frame…"), act("avatar_pick_frame")),
        actionRow("prof-history", t("Photo history…"), act("avatar_history")),
        ...(me?.avatar ? [actionRow("prof-remove", t("Remove photo"), act("avatar_remove"), C.danger)] : []),
      ],
    },
    // D6/D7 — Office, the owner's hub (ADR-0024, Phase 3). The web has no More
    // sheet; Profile IS the "about you" screen, which is where an owner's
    // surface belongs. The phones list it above Profile in their More sheets,
    // and this is the same position relative to the same neighbours.
    sectionLabel("prof-office-label", t("Publishing")),
    {
      type: "stack", id: "prof-office", style: { direction: "column", background: C.surface },
      children: [actionRow("prof-office-row", t("Office — your channels"), act("office_open"))],
    },
    // ADR-0028 — the language WaveTalk draws ITSELF in. On Profile because
    // the web has no More sheet and this IS the "about you" screen, which is
    // where the phones keep it too (beside Appearance, in the same position
    // relative to the same neighbours).
    // How WaveTalk LOOKS, above how it reads. Both are "the app itself"
    // rather than "you", and both sit here for the same reason: the web has
    // no More sheet and this is the settings screen.
    sectionLabel("prof-appearance-label", t("Appearance")),
    {
      type: "stack", id: "prof-appearance", style: { direction: "column", background: C.surface },
      children: appearanceRows(state),
    },
    // G16 — the chat wallpaper, directly under Appearance. The design canvas
    // draws the two together, and the phones keep them in the same screen.
    sectionLabel("prof-wallpaper-label", t("Chat wallpaper")),
    {
      type: "stack", id: "prof-wallpaper", style: { direction: "column", background: C.surface },
      children: wallpaperRows(state),
    },
    sectionLabel("prof-lang-label", translator(resolveLanguage(state.uiLanguage, browserTags()))(t("Language"))),
    {
      type: "stack", id: "prof-lang", style: { direction: "column", background: C.surface },
      children: languageRows(state),
    },
    sectionLabel("prof-priv-label", t("Who can see your photo")),
    {
      type: "stack", id: "prof-priv", style: { direction: "column", background: C.surface },
      children: [
        policyRow(privacy, "everyone", t("Everyone"), t("Anyone who finds you, including in search")),
        policyRow(privacy, "contacts", t("People I chat with"), t("Only people you have replied to in a direct chat")),
        policyRow(privacy, "nobody", t("Nobody"), t("Only you")),
        ...(privacy && privacy.policy !== "nobody"
          ? [
              ...privacy.except.map((e) => actionRow(
                `prof-ex-${e.user_id}`,
                `✕ ${e.display_name ?? (e.username ? "@" + e.username : t("Someone"))}`,
                act("avatar_include", { user_id: e.user_id }),
              )),
              actionRow("prof-ex-add", t("+ Hide from someone…"), act("avatar_exclude")),
            ]
          : []),
      ],
    },
    // ADR-0031 D3 — "Who can find me by my phone number".
    //
    // Rendered from `findability.policies`, which the SERVER sends. This
    // surface offers Everyone and Nobody and refuses `contacts` and `only` —
    // but that is the server's sentence, and a client that reprints it from
    // memory is a client that will one day disagree with it. A policy this
    // screen has no words for draws nothing rather than its wire token.
    ...(state.findability
      ? [
          sectionLabel("prof-find-label", t("Who can find me by my phone number")),
          {
            type: "stack" as const, id: "prof-find",
            style: { direction: "column" as const, background: C.surface },
            children: state.findability.policies.flatMap((policy) => {
              const words = findabilityWords(policy);
              return words ? [findabilityRow(state.findability!, policy, words[0], words[1])] : [];
            }),
          },
          {
            type: "text" as const, id: "prof-find-note",
            props: { value: t("When this is set to Nobody, someone typing your number finds nothing — the same as if you had never registered. Your name and @handle can still be searched.") },
            style: { font_size: 12, color: C.textSecondary, padding: "8 16" },
          },
        ]
      : []),
    ...(state.history.length > 0
      ? [
          sectionLabel("prof-hist-label", t("Photo history — only you see this")),
          {
            type: "stack" as const, id: "prof-hist", style: { direction: "column" as const, background: C.surface },
            children: state.history.map((h) => ({
              type: "stack" as const,
              id: `hist-${h.version}`,
              style: { direction: "row" as const, align: "center", gap: 12, padding: "8 16" },
              children: [
                avatar(`hist-face-${h.version}`, "•", { ownerId: me?.id, av: { kind: "photo" as const, version: h.version }, size: 40 }),
                { type: "text" as const, id: `hist-t-${h.version}`, props: { value: h.current ? t("Current") : t("Earlier photo") }, style: { flex: 1, font_size: 14, color: C.textPrimary } },
                ...(h.current ? [] : [
                  button(`hist-use-${h.version}`, t("Use"), act("avatar_restore", { version: h.version })),
                  button(`hist-del-${h.version}`, t("Delete"), act("avatar_forget", { version: h.version })),
                ]),
              ],
            })),
          },
        ]
      : []),
    {
      type: "text", id: "prof-note",
      props: { value: t("Your photo is not end-to-end encrypted — the WaveTalk service stores it so it can be shown to people you chat with.") },
      style: { font_size: 12, color: C.textSecondary, padding: 16 },
    },
    ...(state.profileError
      ? [{ type: "text" as const, id: "prof-err", props: { value: state.profileError }, style: { color: C.danger, font_size: 13, padding: "0 16" } }]
      : []),
    // Sign out lives HERE now, and not in the Chats header where it sat
    // beside four navigation controls and was the first thing clipped on a
    // narrow window. A control does not lose its header without gaining a
    // home the same day (ADR-0024 A16).
    sectionLabel("prof-session-label", t("Session")),
    actionRow("prof-logout", t("Sign out"), act("logout"), C.danger),
  ];

  return {
    type: "stack", id: "profile-root",
    style: { direction: "column", height: "100%", flex: 1, min_height: 0, background: C.surface },
    children: [
      header("profile-header", t("Profile"), state, {}),
      { type: "scroll-view", id: "profile-scroll", style: { direction: "column", flex: 1, overflow: "auto" }, children: rows },
    ],
  };
}

function sectionLabel(id: string, label: string): VNode {
  return {
    type: "text", id, props: { value: label.toUpperCase() },
    style: {
      font_size: 12.5, font_weight: 700, letter_spacing: "0.5px",
      text_transform: "uppercase", color: C.textSecondary, padding: "14 16 8 16",
    },
  };
}

/**
 * Two letters for a channel, on a tinted circle.
 *
 * Two rather than one because these are ORGANISATIONS sitting in a list of
 * organisations — one letter puts "Ajira Portal" and "Amref" on the same
 * circle, and the circle is doing the work a photo would.
 */
function channelInitialsCircle(id: string, name: string, seed: string, size: number): VNode {
  return {
    type: "stack", id,
    style: {
      width: size, height: size, radius: 999,
      direction: "row", align: "center", justify: "center",
      background: avatarTint(seed),
    },
    children: [{
      type: "text", id: `${id}-t`, props: { value: channelInitials(name) },
      style: { font_size: Math.round(size * 0.34), font_weight: 600, color: C.onTint },
    }],
  };
}

/**
 * An ACRONYM keeps its own letters: "TCRA Tanzania" is TC, not TT.
 *
 * Taking one letter per word is right for a name made of words and wrong for a
 * name whose first word is already an abbreviation — and in this market, where
 * the channels people look for are TCRA, TRA, TBS and NHIF, that case is not
 * the exception.
 */
export function channelInitials(name: string): string {
  const words = name.trim().split(/[ \-_]+/).filter((w) => w.length > 0);
  const first = words[0];
  if (!first) return "?";
  const acronym = first.length >= 2 && /^[A-Z0-9]+$/.test(first);
  if (acronym || words.length === 1) return first.slice(0, 2).toUpperCase();
  return (first.slice(0, 1) + words[1].slice(0, 1)).toUpperCase();
}

/**
 * today → clock, yesterday → the word, inside the last week → the weekday,
 * older → a short date.
 *
 * Bucketed against local MIDNIGHT, never by arithmetic on the epoch: a day is
 * not 24h in every zone, and truncating by modulo puts a row in the wrong
 * bucket around a DST transition.
 */
export function channelStamp(post: ChannelPost | null, now: Date = new Date()): string {
  const ms = post?.posted_at ?? null;
  if (!ms) return "";
  const d = new Date(ms);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = 24 * 60 * 60 * 1000;
  const two = (n: number) => String(n).padStart(2, "0");

  if (d.getTime() >= startOfToday) return `${two(d.getHours())}:${two(d.getMinutes())}`;
  if (d.getTime() >= startOfToday - day) return t("Yesterday");
  // Strictly inside the last week, so "Tue" always means the most recent
  // Tuesday and never the one before it.
  if (d.getTime() >= startOfToday - 6 * day) {
    return ["Sun", t("Mon"), t("Tue"), t("Wed"), t("Thu"), t("Fri"), t("Sat")][d.getDay()];
  }
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
}

function actionRow(id: string, label: string, action: VNode["action"], color?: string): VNode {
  return {
    type: "stack", id, style: { direction: "row", padding: "12 16" }, action,
    children: [{ type: "text", id: `${id}-t`, props: { value: label }, style: { font_size: 15, color: color ?? C.textPrimary } }],
  };
}

/**
 * The label and hint for one findability policy, or null when this screen has
 * no words for it.
 *
 * Null rather than a fallback: if the server ever starts offering a third
 * policy here, drawing its wire token ("contacts") at a person is worse than
 * drawing nothing — a row nobody can read is a row nobody can answer.
 *
 * The sentences duplicate the photo section's on purpose. They coincide
 * today; the settings do not, and sharing one string between two privacy
 * sections is how a reworded photo hint quietly changes what this row claims.
 */
function findabilityWords(policy: string): [string, string] | null {
  switch (policy) {
    case "everyone":
      return [t("Everyone"), t("Someone who has your number in their contacts can find you")];
    case "nobody":
      return [t("Nobody"), t("Your number finds nothing, as if you had never registered")];
    default:
      return null;
  }
}

function findabilityRow(
  findability: NonNullable<ShellState["findability"]>,
  value: string,
  label: string,
  hint: string,
): VNode {
  const on = findability.policy === value;
  return {
    type: "stack", id: `findability-${value}`,
    style: { direction: "row", align: "center", gap: 10, padding: "10 16" },
    action: act("findability_set", { policy: value }),
    children: [
      { type: "text", id: `findability-${value}-dot`, props: { value: on ? "●" : "○" }, style: { color: on ? C.brandEnd : C.textSecondary, font_size: 16 } },
      {
        type: "stack", id: `findability-${value}-texts`, style: { direction: "column", flex: 1, gap: 1 },
        children: [
          { type: "text", id: `findability-${value}-l`, props: { value: label }, style: { font_size: 15, color: C.textPrimary } },
          { type: "text", id: `findability-${value}-h`, props: { value: hint }, style: { font_size: 12, color: C.textSecondary } },
        ],
      },
    ],
  };
}

function policyRow(
  privacy: ShellState["privacy"],
  value: string,
  label: string,
  hint: string,
): VNode {
  const on = privacy?.policy === value;
  return {
    type: "stack", id: `policy-${value}`,
    style: { direction: "row", align: "center", gap: 10, padding: "10 16" },
    action: act("avatar_policy", { policy: value }),
    children: [
      { type: "text", id: `policy-${value}-dot`, props: { value: on ? "●" : "○" }, style: { color: on ? C.brandEnd : C.textSecondary, font_size: 16 } },
      {
        type: "stack", id: `policy-${value}-texts`, style: { direction: "column", flex: 1, gap: 1 },
        children: [
          { type: "text", id: `policy-${value}-l`, props: { value: label }, style: { font_size: 15, color: C.textPrimary } },
          { type: "text", id: `policy-${value}-h`, props: { value: hint }, style: { font_size: 12, color: C.textSecondary } },
        ],
      },
    ],
  };
}
